import { DEFAULT_STORAGE_SOURCE_KEY, EntityReference, EntityRelation, GeoPoint, PUBLIC_STORAGE_PREFIX, RebaseApiError, RebaseApiError as RebaseApiError$1, RebaseClientError, RebaseClientError as RebaseClientError$1, SCHEMA_VERSION_HEADER, Vector, hasFieldOperation, isPublicStoragePath, isUnsupported, parseRelationAggregateSort, sortKeyToString, toCanonicalOp, unsupportedMethod } from "@rebasepro/types";
import { COMPOSITE_ID_SEPARATOR, QueryBuilder, RebasePaginationError, and, buildCompositeId, collectAllPages, cond, mergeIncludeSpecs, normalizeOrderBy, not, or, paginateFind, resolveFindWindow, serializeFilter, serializeInclude, serializeLogicalCondition, serializeOrderBy } from "@rebasepro/common";
import { splitSearchTerms, toSnakeCase, unref } from "@rebasepro/utils";
//#region src/reviver.ts
function rebaseReviver(_key, value) {
	if (value && typeof value === "object" && "__type" in value) {
		const record = value;
		switch (record.__type) {
			case "date":
			case "Date": {
				if (typeof record.value !== "string") return value;
				const date = new Date(record.value);
				return isNaN(date.getTime()) ? null : date;
			}
			case "reference":
			case "EntityReference": return new EntityReference({
				id: String(record.id),
				path: record.path,
				driver: record.driver,
				databaseId: record.databaseId
			});
			case "relation":
			case "EntityRelation": return new EntityRelation(record.id, record.path, record.data);
			case "GeoPoint": return new GeoPoint(record.latitude, record.longitude);
			case "Vector": return new Vector(record.value);
			default: return value;
		}
	}
	return value;
}
//#endregion
//#region src/transport.ts
/**
* True when there is no browser to have signed a user in — a Node script, a
* cron job, an edge worker.
*
* Anonymous is an ordinary, correct state in a browser: before sign-in, on a
* marketing page, for public reads. Warning there would be noise that teaches
* people to ignore warnings, so the guard is off entirely. This uses the same
* `typeof window` test as {@link resolveBaseUrl}, and additionally treats a
* defined `document` as a browser so an SSR shim or test harness that installs
* only one of the two is still excluded.
*/
function isServerLikeEnvironment() {
	return typeof window === "undefined" && typeof document === "undefined";
}
/**
* Emitted once per client. Kept as a constant so the wording is testable and
* greppable — this is the string a user will paste into a search.
*/
var ANONYMOUS_SERVER_CLIENT_WARNING = "[rebase] This client was created outside a browser with no credential — no `token`, no auth token getter, and no cookie auth flow — so every request runs as an anonymous caller. Row-level security will return only publicly readable rows, which is usually nothing and occasionally the wrong thing. Inside a cron or function handler, use the `rebase` you were handed instead of building a new one: its data plane is already admin-scoped. In a standalone script or job, pass the service key as `token`. If you really do want anonymous access, pass `anonymous: true` to silence this.";
/**
* Refuse a filter whose *value* is missing.
*
* `where: { status: ["==", undefined] }` used to serialize to the literal
* string, so `status=eq.undefined` went out on the wire and the server dutifully
* looked for rows whose status is the four-letter word "undefined". The caller
* saw an empty page, not an error — the classic shape of a variable that was
* never set.
*
* Dropping the condition instead would be worse than sending it: the query
* would come back *unfiltered*, which for an ownership or tenant filter means
* returning rows the caller never asked to see. So this is a hard error, and
* both correct spellings are named in the message: omit the key to skip the
* filter, or use `["is-null", null]` to match SQL NULL (which still
* serializes — `null` is a value, `undefined` is the absence of one).
*/
function assertNoUndefinedFilterValues(where) {
	const reject = (field, op) => {
		throw new RebaseClientError$1(`Filter on "${field}" has an undefined value (["${String(op)}", undefined]). Omit "${field}" from \`where\` to skip the filter, or use ["is-null", null] to match SQL NULL.`, {
			code: "INVALID_FILTER",
			details: {
				field,
				operator: String(op)
			}
		});
	};
	for (const [field, condition] of Object.entries(where)) {
		if (condition === void 0) continue;
		if (!Array.isArray(condition)) continue;
		const tuples = Array.isArray(condition[0]) ? condition : [condition];
		for (const tuple of tuples) {
			if (!Array.isArray(tuple) || tuple.length !== 2) continue;
			const [op, value] = tuple;
			if (value === void 0) reject(field, op);
			if (Array.isArray(value) && value.some((v) => v === void 0)) reject(field, op);
		}
	}
}
function buildQueryString(params) {
	if (!params) return "";
	const parts = [];
	if (params.limit != null) parts.push(`limit=${params.limit}`);
	if (params.offset != null) parts.push(`offset=${params.offset}`);
	if (params.page != null) parts.push(`page=${params.page}`);
	if (params.after) parts.push(`after=${encodeURIComponent(params.after)}`);
	if (params.orderBy) {
		const wire = serializeOrderBy(params.orderBy);
		if (wire) parts.push(`orderBy=${encodeURIComponent(wire)}`);
	}
	if (params.searchString) {
		parts.push(`searchString=${encodeURIComponent(params.searchString)}`);
		if (params.searchExplain) parts.push("searchExplain=true");
	}
	if (params.vectorSearch) {
		const vs = params.vectorSearch;
		parts.push(`vector_search=${encodeURIComponent(vs.property)}`);
		parts.push(`vector=${encodeURIComponent(JSON.stringify(vs.vector))}`);
		if (vs.distance) parts.push(`vector_distance=${encodeURIComponent(vs.distance)}`);
		if (vs.threshold !== void 0) parts.push(`vector_threshold=${encodeURIComponent(String(vs.threshold))}`);
	}
	const include = serializeInclude(params.include);
	if (include) parts.push(`include=${encodeURIComponent(include)}`);
	if (params.fields && params.fields.length > 0) parts.push(`fields=${encodeURIComponent(params.fields.join(","))}`);
	if (params.distinct) parts.push("distinct=true");
	if (params.logical) {
		const root = params.logical;
		const serialized = (root.conditions ?? []).map(serializeLogicalCondition).join(",");
		parts.push(`${root.type}=${encodeURIComponent(`(${serialized})`)}`);
	}
	if (params.where) {
		assertNoUndefinedFilterValues(params.where);
		const serialized = serializeFilter(params.where);
		for (const [field, value] of Object.entries(serialized)) if (Array.isArray(value)) for (const v of value) parts.push(`${encodeURIComponent(field)}=${encodeURIComponent(v)}`);
		else parts.push(`${encodeURIComponent(field)}=${encodeURIComponent(value)}`);
	}
	return parts.length > 0 ? "?" + parts.join("&") : "";
}
/**
* The query string for `GET /<collection>/aggregate`.
*
* `?select=sum(total),count()` is SQL's spelling, because whoever writes an
* aggregate is thinking in SQL and any other spelling has to be learned first —
* and because it is what the route already parses. The filters are serialised
* by exactly the same code a `find()` uses, so "revenue by status, this month"
* narrows the same rows whichever call is asking.
*
* `orderBy`, `include` and the page are deliberately not here: an aggregate has
* no rows to sort, no relations to load and no page to continue. `limit` is,
* and bounds the number of *groups*.
*/
function buildAggregateQueryString(params) {
	const parts = [];
	const select = params.select.map((entry) => `${entry.fn}(${entry.field ?? ""})`).join(",");
	parts.push(`select=${encodeURIComponent(select)}`);
	if (params.groupBy && params.groupBy.length > 0) parts.push(`groupBy=${encodeURIComponent(params.groupBy.join(","))}`);
	if (params.limit != null) parts.push(`limit=${params.limit}`);
	if (params.searchString) parts.push(`searchString=${encodeURIComponent(params.searchString)}`);
	if (params.logical) {
		const root = params.logical;
		const serialized = (root.conditions ?? []).map(serializeLogicalCondition).join(",");
		parts.push(`${root.type}=${encodeURIComponent(`(${serialized})`)}`);
	}
	if (params.where) {
		assertNoUndefinedFilterValues(params.where);
		const serialized = serializeFilter(params.where);
		for (const [field, value] of Object.entries(serialized)) if (Array.isArray(value)) for (const v of value) parts.push(`${encodeURIComponent(field)}=${encodeURIComponent(v)}`);
		else parts.push(`${encodeURIComponent(field)}=${encodeURIComponent(value)}`);
	}
	return "?" + parts.join("&");
}
/**
* The base every request and every caller-built URL resolves against.
*
* `baseUrl` is optional because the common production shape is a Rebase
* backend serving its own SPA, where the API is simply the page's origin.
* Leaving it unset is therefore the *correct* configuration there — and the
* one that keeps working when a second hostname (a custom domain) points at
* the same app.
*
* When unset in a browser this resolves to the page origin rather than "".
* Requests behave identically either way, but the empty string is a trap for
* anything that builds a URL from `client.baseUrl`: `new URL("" + path)`
* throws, so apps "fixed" it by baking an absolute host into their bundle —
* which is exactly what breaks the day a custom domain is added, and which no
* amount of CORS configuration repairs, because a SameSite=Lax auth cookie is
* not sent cross-site either.
*/
function resolveBaseUrl(configured) {
	if (configured) return configured.replace(/\/$/, "");
	if (typeof window !== "undefined" && window.location?.origin) return window.location.origin;
	return "";
}
function createTransport(config, environment) {
	const rawFetch = config.fetch || globalThis.fetch;
	/**
	* `fetch`, but its rejection is a `RebaseApiError` like everything else
	* this client throws.
	*
	* A transport failure — DNS, a refused connection, CORS, an aborted
	* request — rejects with whatever the runtime's `fetch` felt like: a
	* `TypeError` reading "Failed to fetch" in a browser, a `TypeError` with a
	* `cause` in undici, a `DOMException` on abort. So the one class the SDK
	* documents as "a `catch` block only ever needs to check for this" did not
	* cover the most common failure of all, and `e.status` / `e.code` were
	* undefined on the error every app hits first: the server being down.
	*
	* `status: 0` rather than a made-up 5xx. There was no response, and 0 is
	* how `XMLHttpRequest` has always spelled that; a fabricated 503 would be
	* indistinguishable from one the server actually sent. The original error
	* is on `cause`, so nothing is hidden.
	*
	* Wrapped once, at construction, because the request path reads `fetchFn`
	* twice — the first attempt and the post-refresh retry — and a wrapper
	* applied at one call site is a wrapper missing from the other.
	*/
	const fetchFn = async (input, init) => {
		try {
			return await rawFetch(input, init);
		} catch (e) {
			if (e instanceof RebaseApiError$1) throw e;
			throw new RebaseApiError$1(`Could not reach the server at ${typeof input === "string" ? input : String(input.url ?? input)}: ${e instanceof Error ? e.message : String(e)}`, {
				status: 0,
				code: "NETWORK_ERROR",
				cause: e
			});
		}
	};
	const apiPath = config.apiPath || "/api";
	for (const field of ["baseUrl", "storageUrlOrigin"]) {
		const value = config[field];
		if (!value || !apiPath) continue;
		const trimmed = value.replace(/\/+$/, "");
		if (!trimmed.endsWith(apiPath)) continue;
		console.warn(`[Rebase] ${field} ${JSON.stringify(value)} already ends with the API path ${JSON.stringify(apiPath)}, which is appended to it — requests will go to ${trimmed}${apiPath}/… and 404. Pass the origin only (${JSON.stringify(trimmed.slice(0, trimmed.length - apiPath.length) || "/")}), or set \`apiPath\` if the server really does mount the API one level deeper.`);
	}
	let token = config.token;
	let tokenGetter;
	let onUnauthorizedHandler = config.onUnauthorized;
	/** Once per client, never per request — log spam is its own bug. */
	let anonymousWarningIssued = false;
	/**
	* Warn a server-side caller that it built a client that can only ever be
	* anonymous. Deliberately checked at the *first request* rather than at
	* construction: `setToken()` / `setAuthTokenGetter()` and a server-side
	* `auth.signIn…()` (which calls `transport.setToken`) all land after the
	* constructor, and warning at construction would fire on every one of them.
	*/
	function warnIfAnonymousServerClient(activeToken) {
		if (anonymousWarningIssued) return;
		if (activeToken) return;
		if (tokenGetter) return;
		if (config.anonymous) return;
		if (environment?.credentialOutOfBand) return;
		if (!isServerLikeEnvironment()) return;
		anonymousWarningIssued = true;
		console.warn(ANONYMOUS_SERVER_CLIENT_WARNING);
	}
	const defaultHeaders = {
		...config.headers ?? {},
		...config.schemaVersion ? { [SCHEMA_VERSION_HEADER]: config.schemaVersion } : {}
	};
	function getHeaders(activeToken, init) {
		return {
			"Content-Type": "application/json",
			...defaultHeaders,
			...activeToken ? { Authorization: `Bearer ${activeToken}` } : {},
			...init?.headers || {}
		};
	}
	/**
	* The refusal for a success status carrying a body this client cannot read.
	*
	* The first 120 characters go in the message because they identify the
	* sender at a glance: `<!doctype html>` says "you are talking to a web
	* server, not to this API" faster than any wording here could.
	*
	* One function for both the first attempt and the post-refresh retry — the
	* retry is a second copy of this whole response-reading path, and copies
	* are how one of them ends up fixed and the other not.
	*/
	function unreadableResponse(status, text) {
		return new RebaseApiError$1(`The server answered ${status} with a body that is not JSON, so there is nothing to return. This usually means the request reached something other than the Rebase API — a single-page-app fallback serving index.html, or a proxy error page — so check the API URL configuration (e.g. VITE_API_URL). The body began: ${JSON.stringify(text.slice(0, 120))}`, {
			status,
			code: "INVALID_JSON_RESPONSE"
		});
	}
	/**
	* The server always emits the canonical
	* `{ error: { message, code, details?, requestId? } }` envelope (formatted
	* by the central `errorHandler`), so a field is read strictly from
	* `body.error.*` and never from the top level.
	*/
	function getErrorField(obj, field) {
		const err = obj?.error;
		if (err && typeof err === "object" && err !== null) return err[field];
	}
	/**
	* `Retry-After` as whole seconds, or `undefined`.
	*
	* The header is either a number of seconds or an HTTP date; both spellings
	* are legal and servers use both, so both are read. A date in the past —
	* which happens on a clock skew — is clamped to 0 rather than negative.
	*/
	function parseRetryAfter(header) {
		if (!header) return void 0;
		const seconds = Number(header.trim());
		if (Number.isFinite(seconds)) return Math.max(0, Math.floor(seconds));
		const at = Date.parse(header);
		if (Number.isNaN(at)) return void 0;
		return Math.max(0, Math.round((at - Date.now()) / 1e3));
	}
	/**
	* Everything a failed response says about itself, in one place.
	*
	* The request path reads a response twice — the first attempt and the
	* post-refresh retry — and the two used to build their error separately.
	* Which is how they came to disagree: both dropped `requestId` and
	* `Retry-After`, and a fix applied to one would have missed the other.
	*/
	function errorFrom(res, body, fallbackMessage) {
		const requestId = getErrorField(body, "requestId") ?? res.headers?.get("X-Request-ID") ?? void 0;
		return new RebaseApiError$1(String(getErrorField(body, "message") || fallbackMessage || `Request failed with status ${res.status}`), {
			status: res.status,
			code: getErrorField(body, "code"),
			details: getErrorField(body, "details"),
			...typeof requestId === "string" && requestId ? { requestId } : {},
			...(() => {
				const retryAfter = parseRetryAfter(res.headers?.get("Retry-After") ?? null);
				return retryAfter === void 0 ? {} : { retryAfterSeconds: retryAfter };
			})()
		});
	}
	async function request(path, init, meta) {
		const url = resolveBaseUrl(config.baseUrl) + apiPath + path;
		let activeToken = token;
		if (tokenGetter) try {
			const fetched = await tokenGetter();
			if (fetched !== null && fetched !== void 0) activeToken = fetched;
		} catch (e) {}
		warnIfAnonymousServerClient(activeToken);
		const headers = getHeaders(activeToken, init);
		if (init?.body instanceof FormData) delete headers["Content-Type"];
		const res = await fetchFn(url, {
			...init,
			headers
		});
		if (meta) {
			meta.status = res.status;
			meta.etag = res.headers?.get?.("ETag") ?? void 0;
		}
		if (res.status === 204) return void 0;
		const text = await res.text().catch(() => "");
		let body = {};
		/**
		* Whether the body was there and could not be read as JSON.
		*
		* On an error status this does not matter — the status is the answer
		* and the message falls back to `statusText`. On a *success* status it
		* is the whole answer, and `{}` was being returned as though the server
		* had sent it: `find()` answered `{}` instead of an array, `getOne()`
		* an empty object, with nothing thrown.
		*
		* The case that produces it is not exotic. Point `VITE_API_URL` at the
		* frontend's own host and `/api/data/posts` lands on the SPA fallback,
		* which answers `200` with `index.html` — so the misconfiguration the
		* 404 branch below spends four lines explaining reaches the caller, in
		* its most common form, as an empty success.
		*/
		let unreadableBody = false;
		if (text) try {
			body = JSON.parse(text, rebaseReviver);
		} catch (e) {
			unreadableBody = true;
		}
		if (res.status === 401 && onUnauthorizedHandler) {
			if (await onUnauthorizedHandler()) {
				let retryToken = token;
				if (tokenGetter) try {
					const fetched = await tokenGetter();
					if (fetched !== null && fetched !== void 0) retryToken = fetched;
				} catch (e) {}
				const retryHeaders = getHeaders(retryToken, init);
				const retryRes = await fetchFn(url, {
					...init,
					headers: retryHeaders
				});
				if (meta) {
					meta.status = retryRes.status;
					meta.etag = retryRes.headers?.get?.("ETag") ?? void 0;
				}
				if (retryRes.status === 204) return void 0;
				const retryText = await retryRes.text().catch(() => "");
				let retryBody = {};
				let retryUnreadable = false;
				if (retryText) try {
					retryBody = JSON.parse(retryText, rebaseReviver);
				} catch (e) {
					retryUnreadable = true;
				}
				if (!retryRes.ok) {
					let fallbackMessage = retryRes.statusText;
					if (retryRes.status === 404 && !fallbackMessage) fallbackMessage = `Endpoint not found (${init?.method || "GET"} ${path}). This usually means the collection is not registered on the backend, or the frontend API URL configuration (e.g. VITE_API_URL) is missing or pointing to the wrong host.`;
					throw errorFrom(retryRes, retryBody, fallbackMessage);
				}
				if (retryUnreadable) throw unreadableResponse(retryRes.status, retryText);
				return retryBody;
			}
		}
		if (!res.ok) {
			let fallbackMessage = res.statusText;
			if (res.status === 404 && !fallbackMessage) fallbackMessage = `Endpoint not found (${init?.method || "GET"} ${path}). This usually means the collection is not registered on the backend, or the frontend API URL configuration (e.g. VITE_API_URL) is missing or pointing to the wrong host.`;
			throw errorFrom(res, body, fallbackMessage);
		}
		if (unreadableBody) throw unreadableResponse(res.status, text);
		return body;
	}
	return {
		request,
		setToken(newToken) {
			token = newToken || void 0;
		},
		setAuthTokenGetter(getter) {
			tokenGetter = getter;
		},
		setOnUnauthorized(handler) {
			onUnauthorizedHandler = handler;
		},
		get baseUrl() {
			return resolveBaseUrl(config.baseUrl);
		},
		get apiPath() {
			return apiPath;
		},
		get storageUrlOrigin() {
			return config.storageUrlOrigin?.replace(/\/$/, "") || void 0;
		},
		get fetchFn() {
			return fetchFn;
		},
		getHeaders: (init) => getHeaders(token, init),
		resolveToken: async () => {
			if (tokenGetter) try {
				const fetched = await tokenGetter();
				if (fetched !== null && fetched !== void 0) return fetched;
			} catch (e) {}
			return token || null;
		}
	};
}
//#endregion
//#region src/auth.ts
/** Map a raw user object from an auth response (`/login`, `/refresh`, `/me`) to a `User`. */
function mapRawUser(raw) {
	return {
		uid: raw.uid,
		email: raw.email ?? null,
		displayName: raw.displayName ?? null,
		photoURL: raw.photoURL ?? null,
		providerId: raw.providerId ?? "password",
		isAnonymous: raw.isAnonymous ?? false,
		emailVerified: raw.emailVerified,
		roles: raw.roles,
		metadata: raw.metadata
	};
}
/** Placeholder user, used only as a last resort when none can be resolved. */
var EMPTY_USER = {
	uid: "",
	email: null,
	displayName: null,
	photoURL: null,
	providerId: "password",
	isAnonymous: false
};
function createMemoryStorage() {
	const store = {};
	return {
		getItem(key) {
			return store[key] ?? null;
		},
		setItem(key, value) {
			store[key] = value;
		},
		removeItem(key) {
			delete store[key];
		}
	};
}
function detectStorage() {
	try {
		if (typeof localStorage !== "undefined") {
			localStorage.setItem("__rebase_test__", "1");
			localStorage.removeItem("__rebase_test__");
			return localStorage;
		}
	} catch (e) {}
	return createMemoryStorage();
}
function createAuth(transport, options) {
	const opts = options || {};
	const storage = opts.storage || detectStorage();
	const authPath = opts.authPath || "/auth";
	const autoRefresh = opts.autoRefresh !== false;
	const persistSession = opts.persistSession !== false;
	const authFlowMode = opts.authFlowMode || "json";
	const STORAGE_KEY = "rebase_auth";
	const REFRESH_BUFFER_MS = 12e4;
	/**
	* The largest delay `setTimeout` can hold — 2^31 - 1 ms, about 24.8 days.
	* Anything larger is silently clamped to 1ms by Node and every browser.
	*/
	const MAX_TIMER_DELAY_MS = 2147483647;
	const MAX_REFRESH_RETRIES = 5;
	const REFRESH_RETRY_BASE_MS = 1e3;
	const REFRESH_RETRY_MAX_MS = 3e4;
	let currentSession = null;
	const listeners = /* @__PURE__ */ new Set();
	let refreshTimeout = null;
	let inFlightRefresh = null;
	let resolveInitialized;
	const isInitialized = new Promise((resolve) => {
		resolveInitialized = resolve;
	});
	function authUrl(endpoint) {
		return transport.baseUrl + transport.apiPath + authPath + endpoint;
	}
	function getFetch() {
		return transport.fetchFn || globalThis.fetch;
	}
	function throwApiError(status, body, statusText) {
		throw new RebaseApiError(body?.error?.message || body?.message || statusText, {
			status,
			code: body?.error?.code || body?.code,
			details: body?.error?.details || body?.details
		});
	}
	function emit(event, session) {
		for (const fn of listeners) try {
			fn(event, session);
		} catch (e) {
			console.error("Error in auth state change listener:", e);
		}
	}
	function saveSession(session) {
		if (!persistSession || authFlowMode === "cookie") return;
		try {
			storage.setItem(STORAGE_KEY, JSON.stringify(session));
		} catch (e) {}
	}
	function clearStoredSession() {
		try {
			storage.removeItem(STORAGE_KEY);
		} catch (e) {}
	}
	function loadStoredSession() {
		try {
			const raw = storage.getItem(STORAGE_KEY);
			if (raw) return JSON.parse(raw);
		} catch (e) {}
		return null;
	}
	/**
	* A refresh failure is only fatal if the refresh token itself is rejected
	* (expired / invalid / forbidden). Network blips, timeouts, and 5xx (e.g. a
	* backend restart mid-session) are transient and must NOT log the user out.
	*/
	function isFatalRefreshError(err) {
		if (!(err instanceof RebaseApiError)) return false;
		if (err.code === "TOKEN_ALREADY_USED") return false;
		if (err.code === "INVALID_TOKEN" || err.code === "TOKEN_EXPIRED") return true;
		return err.status === 401 || err.status === 403;
	}
	/**
	* Drop this client's session without telling the server.
	*
	* `signOut()` is a user action: it POSTs /logout, which revokes the whole
	* sign-in. That is the wrong hammer for a refresh that failed. Our token
	* may be stale precisely because a sibling tab holds a live one, and
	* logging out on its behalf would turn one tab's bad luck into everybody
	* being signed out — the exact failure this work exists to remove.
	*/
	function abandonSessionLocally() {
		currentSession = null;
		clearStoredSession();
		if (refreshTimeout) {
			clearTimeout(refreshTimeout);
			refreshTimeout = null;
		}
		transport.setToken(null);
		emit("SIGNED_OUT", null);
	}
	/**
	* Recover from a 401 on an ordinary API request.
	*
	* Returns `true` when the caller should retry — we minted a fresh access
	* token. When the refresh is rejected *fatally* (the refresh token itself
	* is invalid, expired or revoked) this client can no longer act as the
	* user at all, so we drop the session and emit `SIGNED_OUT`. UIs gate on
	* that event, so they show their login screen instead of leaving the user
	* staring at "Invalid or expired token" on every view.
	*
	* Transient failures (offline, 5xx, backend restarting) keep the session:
	* the scheduled refresh backs off and retries, and the token is very
	* likely still good once the backend answers again.
	*/
	async function handleUnauthorized() {
		if (!currentSession) return false;
		if (authFlowMode !== "cookie" && !currentSession.refreshToken) {
			abandonSessionLocally();
			return false;
		}
		try {
			await refreshSession();
			return true;
		} catch (err) {
			if (isFatalRefreshError(err)) abandonSessionLocally();
			return false;
		}
	}
	async function attemptScheduledRefresh(attempt) {
		try {
			await refreshSession();
		} catch (err) {
			if (isFatalRefreshError(err)) {
				abandonSessionLocally();
				return;
			}
			if (attempt >= MAX_REFRESH_RETRIES) {
				abandonSessionLocally();
				return;
			}
			const backoff = Math.min(REFRESH_RETRY_BASE_MS * 2 ** attempt, REFRESH_RETRY_MAX_MS);
			refreshTimeout = setTimeout(() => {
				attemptScheduledRefresh(attempt + 1);
			}, backoff);
		}
	}
	function scheduleRefresh(expiresAt) {
		if (refreshTimeout) clearTimeout(refreshTimeout);
		if (!autoRefresh) return;
		const delay = expiresAt - REFRESH_BUFFER_MS - Date.now();
		if (delay <= 0) {
			attemptScheduledRefresh(0);
			return;
		}
		if (delay > MAX_TIMER_DELAY_MS) {
			refreshTimeout = setTimeout(() => scheduleRefresh(expiresAt), MAX_TIMER_DELAY_MS);
			return;
		}
		refreshTimeout = setTimeout(() => {
			attemptScheduledRefresh(0);
		}, delay);
	}
	/**
	* Stop the scheduled token refresh, leaving the session itself alone.
	*
	* This is teardown, not sign-out. `scheduleRefresh` arms an ordinary
	* `setTimeout` up to a token lifetime away, and it is not `unref`'d — so on
	* Node it holds the event loop open by itself. `client.close()` promised
	* that "a script that does not call this will not exit on its own", which
	* was true, while the converse it plainly implies was not: a signed-in
	* client that closed its socket still hung, because this timer outlived it.
	* Any script, cron handler or job that signs in hit that.
	*
	* Deliberately does NOT clear the session, touch storage, or emit
	* SIGNED_OUT. Closing a client is not the user signing out — `signOut()`
	* POSTs /logout and revokes the whole sign-in, which is the wrong hammer
	* (see `abandonSessionLocally`) — and a persisted session must still be
	* there for the next client to restore.
	*/
	function stopAutoRefresh() {
		if (refreshTimeout) {
			clearTimeout(refreshTimeout);
			refreshTimeout = null;
		}
	}
	function handleAuthResponse(data, event) {
		const user = mapRawUser(data.user);
		const session = {
			accessToken: data.tokens.accessToken,
			refreshToken: data.tokens.refreshToken || currentSession?.refreshToken || "",
			expiresAt: data.tokens.accessTokenExpiresAt,
			user
		};
		currentSession = session;
		saveSession(session);
		transport.setToken(session.accessToken);
		scheduleRefresh(session.expiresAt);
		emit(event || "SIGNED_IN", session);
		return session;
	}
	async function signInWithEmail(email, password) {
		const res = await getFetch()(authUrl("/login"), {
			method: "POST",
			headers: { "Content-Type": "application/json" },
			body: JSON.stringify({
				email,
				password
			}),
			credentials: authFlowMode === "cookie" ? "include" : void 0
		});
		const body = await res.json().catch(() => ({}));
		if (!res.ok) throwApiError(res.status, body, res.statusText);
		const session = handleAuthResponse(body, "SIGNED_IN");
		return {
			user: session.user,
			accessToken: session.accessToken,
			refreshToken: session.refreshToken
		};
	}
	async function signUp(email, password, displayName) {
		const fetchFn = getFetch();
		const payload = {
			email,
			password
		};
		if (displayName !== void 0) payload.displayName = displayName;
		const res = await fetchFn(authUrl("/register"), {
			method: "POST",
			headers: { "Content-Type": "application/json" },
			body: JSON.stringify(payload),
			credentials: authFlowMode === "cookie" ? "include" : void 0
		});
		const body = await res.json().catch(() => ({}));
		if (!res.ok) throwApiError(res.status, body, res.statusText);
		const session = handleAuthResponse(body, "SIGNED_IN");
		return {
			user: session.user,
			accessToken: session.accessToken,
			refreshToken: session.refreshToken
		};
	}
	/**
	* Sign in with Google.
	*
	* Supports three invocation styles:
	* - `signInWithGoogle({ idToken })` — ID-token flow (One Tap / Sign In button)
	* - `signInWithGoogle({ accessToken })` — Access-token flow (popup)
	* - `signInWithGoogle({ code, redirectUri })` — Authorization code flow (most secure)
	*/
	async function signInWithGoogle(payload) {
		const res = await getFetch()(authUrl("/google"), {
			method: "POST",
			headers: { "Content-Type": "application/json" },
			body: JSON.stringify(payload),
			credentials: authFlowMode === "cookie" ? "include" : void 0
		});
		const responseBody = await res.json().catch(() => ({}));
		if (!res.ok) throwApiError(res.status, responseBody, res.statusText);
		const session = handleAuthResponse(responseBody, "SIGNED_IN");
		return {
			user: session.user,
			accessToken: session.accessToken,
			refreshToken: session.refreshToken
		};
	}
	async function signInWithLinkedin(code, redirectUri) {
		const res = await getFetch()(authUrl("/linkedin"), {
			method: "POST",
			headers: { "Content-Type": "application/json" },
			body: JSON.stringify({
				code,
				redirectUri
			}),
			credentials: authFlowMode === "cookie" ? "include" : void 0
		});
		const body = await res.json().catch(() => ({}));
		if (!res.ok) throwApiError(res.status, body, res.statusText);
		const session = handleAuthResponse(body, "SIGNED_IN");
		return {
			user: session.user,
			accessToken: session.accessToken,
			refreshToken: session.refreshToken
		};
	}
	/**
	* Generic OAuth sign-in. Posts the given payload to `/auth/{providerId}`.
	* Use this for any provider registered on the backend.
	*/
	async function signInWithOAuth(providerId, payload) {
		const res = await getFetch()(authUrl(`/${providerId}`), {
			method: "POST",
			headers: { "Content-Type": "application/json" },
			body: JSON.stringify(payload),
			credentials: authFlowMode === "cookie" ? "include" : void 0
		});
		const body = await res.json().catch(() => ({}));
		if (!res.ok) throwApiError(res.status, body, res.statusText);
		const session = handleAuthResponse(body, "SIGNED_IN");
		return {
			user: session.user,
			accessToken: session.accessToken,
			refreshToken: session.refreshToken
		};
	}
	async function signInWithGitHub(code, redirectUri) {
		return signInWithOAuth("github", {
			code,
			redirectUri
		});
	}
	async function signInWithMicrosoft(code, redirectUri) {
		return signInWithOAuth("microsoft", {
			code,
			redirectUri
		});
	}
	async function signInWithApple(code, redirectUri, user) {
		return signInWithOAuth("apple", {
			code,
			redirectUri,
			user
		});
	}
	async function signInWithFacebook(code, redirectUri) {
		return signInWithOAuth("facebook", {
			code,
			redirectUri
		});
	}
	async function signInWithTwitter(code, redirectUri, codeVerifier) {
		return signInWithOAuth("twitter", {
			code,
			redirectUri,
			codeVerifier
		});
	}
	async function signInWithDiscord(code, redirectUri) {
		return signInWithOAuth("discord", {
			code,
			redirectUri
		});
	}
	async function signInWithGitLab(code, redirectUri) {
		return signInWithOAuth("gitlab", {
			code,
			redirectUri
		});
	}
	async function signInWithBitbucket(code, redirectUri) {
		return signInWithOAuth("bitbucket", {
			code,
			redirectUri
		});
	}
	async function signInWithSlack(code, redirectUri) {
		return signInWithOAuth("slack", {
			code,
			redirectUri
		});
	}
	async function signInWithSpotify(code, redirectUri) {
		return signInWithOAuth("spotify", {
			code,
			redirectUri
		});
	}
	async function signOut() {
		const fetchFn = getFetch();
		try {
			if (authFlowMode === "cookie" || currentSession?.refreshToken) await fetchFn(authUrl("/logout"), {
				method: "POST",
				headers: { "Content-Type": "application/json" },
				body: JSON.stringify({ refreshToken: currentSession?.refreshToken }),
				credentials: authFlowMode === "cookie" ? "include" : void 0
			});
		} catch (e) {}
		currentSession = null;
		clearStoredSession();
		if (refreshTimeout) {
			clearTimeout(refreshTimeout);
			refreshTimeout = null;
		}
		transport.setToken(null);
		emit("SIGNED_OUT", null);
	}
	/**
	* Serialise refreshes across TABS, not just within one.
	*
	* The in-flight promise below covers callers inside a single JavaScript
	* context. It does nothing about the far more common case: two tabs of the
	* same app booting together, each firing its own /refresh with the same
	* cookie. The server tolerates that now (superseded tokens stay usable for
	* a grace window), but tolerating a stampede is not the same as avoiding
	* one, and every extra rotation is another chance to end up holding a
	* token whose response never arrived.
	*
	* Web Locks are best-effort on purpose. supabase-js shipped this and then
	* spent a year fielding deadlock reports — a lock held by a crashed or
	* frozen tab must never be able to wedge sign-in — so a lock we cannot
	* take within the timeout is simply not taken, and the refresh proceeds
	* unserialised, exactly as it did before.
	*/
	const REFRESH_LOCK_NAME = "rebase-auth-refresh";
	const REFRESH_LOCK_TIMEOUT_MS = 5e3;
	async function withRefreshLock(fn) {
		const locks = globalThis.navigator?.locks;
		if (!locks?.request) return fn();
		const controller = new AbortController();
		const giveUp = setTimeout(() => controller.abort(), REFRESH_LOCK_TIMEOUT_MS);
		try {
			return await locks.request(REFRESH_LOCK_NAME, { signal: controller.signal }, async () => fn());
		} catch (e) {
			if (e?.name !== "AbortError") throw e;
			return fn();
		} finally {
			clearTimeout(giveUp);
		}
	}
	function refreshSession() {
		if (inFlightRefresh) return inFlightRefresh;
		inFlightRefresh = withRefreshLock(() => doRefreshSession()).finally(() => {
			inFlightRefresh = null;
		});
		return inFlightRefresh;
	}
	async function doRefreshSession() {
		if (authFlowMode !== "cookie" && !currentSession?.refreshToken) throw new RebaseClientError$1("No active session to refresh. Sign in first, or set `authFlowMode: \"cookie\"` if the refresh token is held in an HttpOnly cookie.", { code: "NOT_SIGNED_IN" });
		const res = await getFetch()(authUrl("/refresh"), {
			method: "POST",
			headers: { "Content-Type": "application/json" },
			body: JSON.stringify({ refreshToken: currentSession?.refreshToken }),
			credentials: authFlowMode === "cookie" ? "include" : void 0
		});
		const body = await res.json().catch(() => ({}));
		if (!res.ok) throwApiError(res.status, body, res.statusText);
		const accessToken = body.tokens.accessToken;
		transport.setToken(accessToken);
		let user = currentSession?.user;
		if (body.user && typeof body.user.uid === "string") user = mapRawUser(body.user);
		else if (!user || !user.uid) try {
			user = await getUser();
		} catch {}
		const session = {
			accessToken,
			refreshToken: body.tokens.refreshToken || currentSession?.refreshToken || "",
			expiresAt: body.tokens.accessTokenExpiresAt,
			user: user ?? EMPTY_USER
		};
		currentSession = session;
		saveSession(session);
		transport.setToken(session.accessToken);
		scheduleRefresh(session.expiresAt);
		emit("TOKEN_REFRESHED", session);
		return session;
	}
	async function getUser() {
		return (await transport.request(authPath + "/me", { method: "GET" })).user;
	}
	/**
	* Resolve an email to a minimal public profile (`uid`, `displayName`,
	* `photoURL`) for invite-by-email flows. Returns `null` when no account
	* matches. Requires the backend to opt in via `auth.allowUserLookup`;
	* otherwise the endpoint is absent and this rejects.
	*/
	async function findUserByEmail(email) {
		return (await transport.request(authPath + "/find-user", {
			method: "POST",
			body: JSON.stringify({ email })
		})).user;
	}
	async function updateUser(updates) {
		const data = await transport.request(authPath + "/me", {
			method: "PATCH",
			body: JSON.stringify(updates)
		});
		if (currentSession) {
			currentSession = {
				...currentSession,
				user: data.user
			};
			saveSession(currentSession);
			emit("USER_UPDATED", currentSession);
		}
		return data.user;
	}
	async function resetPasswordForEmail(email) {
		const res = await getFetch()(authUrl("/forgot-password"), {
			method: "POST",
			headers: { "Content-Type": "application/json" },
			body: JSON.stringify({ email })
		});
		const body = await res.json().catch(() => ({}));
		if (!res.ok) throwApiError(res.status, body, res.statusText);
		return body;
	}
	async function resetPassword(token, password) {
		const res = await getFetch()(authUrl("/reset-password"), {
			method: "POST",
			headers: { "Content-Type": "application/json" },
			body: JSON.stringify({
				token,
				password
			})
		});
		const body = await res.json().catch(() => ({}));
		if (!res.ok) throwApiError(res.status, body, res.statusText);
		return body;
	}
	async function changePassword(oldPassword, newPassword) {
		return transport.request(authPath + "/change-password", {
			method: "POST",
			body: JSON.stringify({
				oldPassword,
				newPassword
			})
		});
	}
	/**
	* Link an OAuth provider to the **currently signed-in** account.
	*
	* Use this when `signIn*` failed with `EMAIL_NOT_VERIFIED` — an account
	* with that email already exists under a different sign-in method — or to
	* attach a provider whose email differs from the account's.
	*
	* The payload is the same one the provider's sign-in method takes, e.g.
	* `linkProvider("google", { idToken })`.
	*
	* Unlike sign-in, this does not require the provider to have verified the
	* email, and the emails need not match: the active session already proves
	* account ownership.
	*
	* Throws `IDENTITY_ALREADY_LINKED` (409) if that provider identity is
	* attached to a different user. Succeeds idempotently (`alreadyLinked:
	* true`) if it is already attached to the current one.
	*/
	async function linkProvider(providerId, payload) {
		return transport.request(authPath + "/link/" + providerId, {
			method: "POST",
			body: JSON.stringify(payload)
		});
	}
	async function sendVerificationEmail() {
		return transport.request(authPath + "/send-verification", { method: "POST" });
	}
	async function verifyEmail(token) {
		const res = await getFetch()(authUrl("/verify-email?token=" + encodeURIComponent(token)), {
			method: "GET",
			headers: { "Content-Type": "application/json" }
		});
		const body = await res.json().catch(() => ({}));
		if (!res.ok) throwApiError(res.status, body, res.statusText);
		return body;
	}
	async function sendMagicLink(email) {
		const res = await getFetch()(authUrl("/magic-link"), {
			method: "POST",
			headers: { "Content-Type": "application/json" },
			body: JSON.stringify({ email })
		});
		const body = await res.json().catch(() => ({}));
		if (!res.ok) throwApiError(res.status, body, res.statusText);
		return body;
	}
	async function verifyMagicLink(token) {
		const res = await getFetch()(authUrl("/magic-link/verify"), {
			method: "POST",
			headers: { "Content-Type": "application/json" },
			body: JSON.stringify({ token }),
			credentials: authFlowMode === "cookie" ? "include" : void 0
		});
		const body = await res.json().catch(() => ({}));
		if (!res.ok) throwApiError(res.status, body, res.statusText);
		const session = handleAuthResponse(body, "SIGNED_IN");
		return {
			user: session.user,
			accessToken: session.accessToken,
			refreshToken: session.refreshToken
		};
	}
	/**
	* Ask for a six-digit sign-in code by email.
	*
	* Answers the same thing whether or not the address has an account — do not
	* use the result to tell a person whether they are registered, because it
	* does not know.
	*/
	async function sendEmailOtp(email) {
		const res = await getFetch()(authUrl("/otp"), {
			method: "POST",
			headers: { "Content-Type": "application/json" },
			body: JSON.stringify({ email })
		});
		const body = await res.json().catch(() => ({}));
		if (!res.ok) throwApiError(res.status, body, res.statusText);
		return body;
	}
	/**
	* Trade a code for a session.
	*
	* The address is sent with the code because the code is only valid for it:
	* that is what keeps a six-digit guess a guess against one account rather
	* than against every account at once.
	*/
	async function verifyEmailOtp(email, code) {
		const res = await getFetch()(authUrl("/otp/verify"), {
			method: "POST",
			headers: { "Content-Type": "application/json" },
			body: JSON.stringify({
				email,
				code
			}),
			credentials: authFlowMode === "cookie" ? "include" : void 0
		});
		const body = await res.json().catch(() => ({}));
		if (!res.ok) throwApiError(res.status, body, res.statusText);
		const session = handleAuthResponse(body, "SIGNED_IN");
		return {
			user: session.user,
			accessToken: session.accessToken,
			refreshToken: session.refreshToken
		};
	}
	/**
	* Sign in without credentials, as a throwaway account.
	*
	* The account is real — it has an id, roles and a session, so row-level
	* security scopes its data exactly as it would a signed-up user's. What it
	* does not have is a way back: nobody can sign in *as* it a second time, so
	* everything it owns is lost when the session is. `linkAnonymous` is how it
	* stops being throwaway.
	*
	* Refused with `ANONYMOUS_AUTH_DISABLED` unless the backend has it on.
	*/
	async function signInAnonymously() {
		const res = await getFetch()(authUrl("/anonymous"), {
			method: "POST",
			headers: { "Content-Type": "application/json" },
			credentials: authFlowMode === "cookie" ? "include" : void 0
		});
		const body = await res.json().catch(() => ({}));
		if (!res.ok) throwApiError(res.status, body, res.statusText);
		const session = handleAuthResponse(body, "SIGNED_IN");
		return {
			user: session.user,
			accessToken: session.accessToken,
			refreshToken: session.refreshToken
		};
	}
	/**
	* Put an email and password on the anonymous account this client holds.
	*
	* The user keeps their id, so everything they created while anonymous stays
	* theirs — which is the point: the alternative is asking a visitor to sign
	* up before they have any reason to.
	*
	* Requires a live anonymous session. `NOT_ANONYMOUS` (400) if the session
	* belongs to an ordinary account, `EMAIL_EXISTS` (409) if the address is
	* taken.
	*/
	async function linkAnonymous(email, password) {
		const session = handleAuthResponse(await transport.request(authPath + "/anonymous/link", {
			method: "POST",
			body: JSON.stringify({
				email,
				password
			})
		}), "USER_UPDATED");
		return {
			user: session.user,
			accessToken: session.accessToken,
			refreshToken: session.refreshToken
		};
	}
	/**
	* Multi-factor authentication: TOTP factors, and the challenge that raises
	* a session to `aal2`.
	*
	* The backend has served all six of these since MFA landed; the SDK exposed
	* none of them, so every app that wanted MFA hand-wrote `fetch` calls
	* against `/auth/mfa/*` — including the auth header and the cookie flag,
	* which is exactly what a client is for.
	*/
	const mfa = {
		/**
		* Start enrolling a TOTP factor.
		*
		* Returns the secret and an `otpauth://` URI to render as a QR code,
		* plus ten single-use recovery codes. **Show the recovery codes once
		* and never again** — only their hashes are stored.
		*
		* The factor is not usable until `verify` confirms the user's
		* authenticator produced a correct code from it.
		*/
		async enroll(options) {
			return transport.request(authPath + "/mfa/enroll", {
				method: "POST",
				body: JSON.stringify(options ?? {})
			});
		},
		/** Confirm an enrolment with a code from the authenticator app. */
		async verify(factorId, code) {
			return transport.request(authPath + "/mfa/verify", {
				method: "POST",
				body: JSON.stringify({
					factorId,
					code
				})
			});
		},
		/** The factors on this account, verified or not. */
		async listFactors() {
			return (await transport.request(authPath + "/mfa/factors", { method: "GET" })).factors;
		},
		/**
		* Remove a factor. Requires an `aal2` session — one that has already
		* answered a challenge — so a stolen `aal1` token cannot turn MFA off.
		*/
		async unenroll(factorId) {
			return transport.request(authPath + "/mfa/unenroll", {
				method: "DELETE",
				body: JSON.stringify({ factorId })
			});
		},
		/** Open a challenge against a verified factor. Expires in five minutes. */
		async challenge(factorId) {
			return transport.request(authPath + "/mfa/challenge", {
				method: "POST",
				body: JSON.stringify({ factorId })
			});
		},
		/**
		* Answer a challenge with a TOTP code or a recovery code.
		*
		* This is where an MFA-enrolled account's real session is minted, at
		* `aal2` — so the returned tokens replace the ones the sign-in handed
		* back, and this client adopts them.
		*/
		async verifyChallenge(challengeId, code) {
			const session = handleAuthResponse(await transport.request(authPath + "/mfa/challenge/verify", {
				method: "POST",
				body: JSON.stringify({
					challengeId,
					code
				})
			}), "SIGNED_IN");
			return {
				user: session.user,
				accessToken: session.accessToken,
				refreshToken: session.refreshToken
			};
		}
	};
	async function getSessions() {
		return (await transport.request(authPath + "/sessions", { method: "GET" })).sessions;
	}
	async function revokeSession(sessionId) {
		return transport.request(authPath + "/sessions/" + encodeURIComponent(sessionId), { method: "DELETE" });
	}
	async function revokeAllSessions() {
		const result = await transport.request(authPath + "/sessions", { method: "DELETE" });
		currentSession = null;
		clearStoredSession();
		if (refreshTimeout) {
			clearTimeout(refreshTimeout);
			refreshTimeout = null;
		}
		transport.setToken(null);
		emit("SIGNED_OUT", null);
		return result;
	}
	async function getAuthConfig() {
		const res = await getFetch()(authUrl("/config"), {
			method: "GET",
			headers: { "Content-Type": "application/json" }
		});
		const body = await res.json().catch(() => ({}));
		if (!res.ok) throwApiError(res.status, body, res.statusText);
		return body;
	}
	function getSession() {
		return currentSession;
	}
	function onAuthStateChange(callback) {
		listeners.add(callback);
		return () => listeners.delete(callback);
	}
	if (persistSession) {
		const stored = loadStoredSession();
		if (stored && stored.accessToken) if (stored.expiresAt > Date.now()) {
			currentSession = stored;
			transport.setToken(stored.accessToken);
			scheduleRefresh(stored.expiresAt);
			resolveInitialized();
		} else if (authFlowMode === "cookie" || stored.refreshToken) {
			currentSession = stored;
			refreshSession().then(() => {
				resolveInitialized();
			}).catch(() => {
				currentSession = null;
				clearStoredSession();
				transport.setToken(null);
				resolveInitialized();
			});
		} else resolveInitialized();
		else if (authFlowMode === "cookie") refreshSession().then(() => {
			resolveInitialized();
		}).catch(() => {
			resolveInitialized();
		});
		else resolveInitialized();
	} else resolveInitialized();
	return {
		signInWithEmail,
		signUp,
		signInWithGoogle,
		signInWithLinkedin,
		signInWithOAuth,
		signInWithGitHub,
		signInWithMicrosoft,
		signInWithApple,
		signInWithFacebook,
		signInWithTwitter,
		signInWithDiscord,
		signInWithGitLab,
		signInWithBitbucket,
		signInWithSlack,
		signInWithSpotify,
		signInAnonymously,
		linkAnonymous,
		mfa,
		signOut,
		stopAutoRefresh,
		refreshSession,
		handleUnauthorized,
		getUser,
		findUserByEmail,
		updateUser,
		resetPasswordForEmail,
		resetPassword,
		changePassword,
		linkProvider,
		sendVerificationEmail,
		verifyEmail,
		sendMagicLink,
		verifyMagicLink,
		sendEmailOtp,
		verifyEmailOtp,
		getSessions,
		revokeSession,
		revokeAllSessions,
		getAuthConfig,
		getSession,
		onAuthStateChange,
		canRestoreSession: () => persistSession || authFlowMode === "cookie",
		isInitialized: () => isInitialized
	};
}
function createCookieStorage(options = {}) {
	const defaultOptions = {
		path: "/",
		sameSite: "Lax",
		...options
	};
	return {
		getItem(key) {
			if (typeof document === "undefined") return null;
			const nameEQ = encodeURIComponent(key) + "=";
			const ca = document.cookie.split(";");
			for (let i = 0; i < ca.length; i++) {
				let c = ca[i];
				while (c.charAt(0) === " ") c = c.substring(1, c.length);
				if (c.indexOf(nameEQ) === 0) return decodeURIComponent(c.substring(nameEQ.length, c.length));
			}
			return null;
		},
		setItem(key, value) {
			if (typeof document === "undefined") return;
			let cookieStr = `${encodeURIComponent(key)}=${encodeURIComponent(value)}`;
			if (defaultOptions.path) cookieStr += `; path=${defaultOptions.path}`;
			if (defaultOptions.domain) cookieStr += `; domain=${defaultOptions.domain}`;
			if (defaultOptions.maxAge !== void 0) cookieStr += `; max-age=${defaultOptions.maxAge}`;
			else cookieStr += `; max-age=${365 * 24 * 60 * 60}`;
			if (defaultOptions.secure) cookieStr += "; secure";
			if (defaultOptions.sameSite) cookieStr += `; samesite=${defaultOptions.sameSite}`;
			document.cookie = cookieStr;
		},
		removeItem(key) {
			if (typeof document === "undefined") return;
			let cookieStr = `${encodeURIComponent(key)}=; path=${defaultOptions.path || "/"}; max-age=-1`;
			if (defaultOptions.domain) cookieStr += `; domain=${defaultOptions.domain}`;
			document.cookie = cookieStr;
		}
	};
}
//#endregion
//#region src/admin.ts
function createAdmin(transport, options) {
	const adminPath = (options || {}).adminPath || "/admin";
	async function listUsers() {
		return transport.request(adminPath + "/users", { method: "GET" });
	}
	async function listUsersPaginated(options) {
		const params = new URLSearchParams();
		if (options?.limit !== void 0) params.set("limit", String(options.limit));
		if (options?.offset !== void 0) params.set("offset", String(options.offset));
		if (options?.search) params.set("search", options.search);
		if (options?.orderBy) params.set("orderBy", options.orderBy);
		if (options?.orderDir) params.set("orderDir", options.orderDir);
		const qs = params.toString();
		return transport.request(adminPath + "/users" + (qs ? "?" + qs : ""), { method: "GET" });
	}
	async function getUser(userId) {
		return transport.request(adminPath + "/users/" + encodeURIComponent(userId), { method: "GET" });
	}
	async function createUser(data) {
		return transport.request(adminPath + "/users", {
			method: "POST",
			body: JSON.stringify(data)
		});
	}
	async function updateUser(userId, data) {
		return transport.request(adminPath + "/users/" + encodeURIComponent(userId), {
			method: "PUT",
			body: JSON.stringify(data)
		});
	}
	async function deleteUser(userId) {
		return transport.request(adminPath + "/users/" + encodeURIComponent(userId), { method: "DELETE" });
	}
	async function resetPassword(userId, options) {
		return transport.request(adminPath + "/users/" + encodeURIComponent(userId) + "/reset-password", {
			method: "POST",
			...options?.password ? { body: JSON.stringify({ password: options.password }) } : {}
		});
	}
	async function listRoles() {
		return transport.request(adminPath + "/roles", { method: "GET" });
	}
	async function bootstrap() {
		return transport.request(adminPath + "/bootstrap", { method: "POST" });
	}
	return {
		listUsers,
		listUsersPaginated,
		getUser,
		createUser,
		updateUser,
		deleteUser,
		resetPassword,
		listRoles,
		bootstrap
	};
}
//#endregion
//#region src/cron.ts
function createCron(transport, options) {
	const cronPath = options?.cronPath || "/cron";
	async function listJobs() {
		return transport.request(cronPath, { method: "GET" });
	}
	async function getJob(jobId) {
		return transport.request(cronPath + "/" + encodeURIComponent(jobId), { method: "GET" });
	}
	async function triggerJob(jobId) {
		return transport.request(cronPath + "/" + encodeURIComponent(jobId) + "/trigger", { method: "POST" });
	}
	async function getJobLogs(jobId, options) {
		const params = new URLSearchParams();
		if (options?.limit !== void 0) params.set("limit", String(options.limit));
		const qs = params.toString();
		return transport.request(cronPath + "/" + encodeURIComponent(jobId) + "/logs" + (qs ? "?" + qs : ""), { method: "GET" });
	}
	async function toggleJob(jobId, enabled) {
		return transport.request(cronPath + "/" + encodeURIComponent(jobId), {
			method: "PUT",
			body: JSON.stringify({ enabled })
		});
	}
	return {
		listJobs,
		getJob,
		triggerJob,
		getJobLogs,
		toggleJob
	};
}
//#endregion
//#region src/backups.ts
function createBackups(transport, options) {
	const backupsPath = options?.backupsPath || "/admin/backups";
	async function list() {
		return transport.request(backupsPath, { method: "GET" });
	}
	/**
	* Download a backup's bytes. Uses an authenticated fetch (not the JSON
	* transport) so the octet-stream response comes back as a Blob.
	*/
	async function download(key) {
		const token = await transport.resolveToken();
		const url = `${transport.baseUrl}${transport.apiPath}${backupsPath}/download?key=${encodeURIComponent(key)}`;
		const res = await fetch(url, {
			method: "GET",
			headers: token ? { Authorization: `Bearer ${token}` } : {}
		});
		if (!res.ok) throw new Error(`Failed to download backup (${res.status})`);
		return res.blob();
	}
	return {
		list,
		download
	};
}
//#endregion
//#region src/api-keys.ts
/**
* Creates a client for managing API keys via the admin routes.
*
* @param transport - The shared HTTP transport created by `createTransport`.
* @param options   - Optional overrides (e.g. a custom base path).
*/
function createApiKeys(transport, options) {
	const apiKeysPath = options?.apiKeysPath || "/admin/api-keys";
	/** List all API keys (masked). */
	async function listKeys() {
		return transport.request(apiKeysPath, { method: "GET" });
	}
	/** Get a single API key by ID (masked). */
	async function getKey(id) {
		return transport.request(apiKeysPath + "/" + encodeURIComponent(id), { method: "GET" });
	}
	/** Create a new API key. The full secret is included in the response. */
	async function createKey(data) {
		return transport.request(apiKeysPath, {
			method: "POST",
			body: JSON.stringify(data)
		});
	}
	/** Update an existing API key. */
	async function updateKey(id, data) {
		return transport.request(apiKeysPath + "/" + encodeURIComponent(id), {
			method: "PUT",
			body: JSON.stringify(data)
		});
	}
	/** Revoke (soft-delete) an API key. */
	async function revokeKey(id) {
		return transport.request(apiKeysPath + "/" + encodeURIComponent(id), { method: "DELETE" });
	}
	return {
		listKeys,
		getKey,
		createKey,
		updateKey,
		revokeKey
	};
}
//#endregion
//#region src/sdk_query_builder.ts
/**
* SDK Query Builder — returns flat rows (`FindResult<M>`) instead of
* Entity-wrapped results (`FindResponse<M>`).
*
* @example
* const { data } = await rebase.data.posts
*     .where("status", "==", "published")
*     .orderBy("created_at", "desc")
*     .limit(10)
*     .find();
*
* console.log(data[0].title); // flat access
*/
var SDKQueryBuilder = class {
	collection;
	params = { where: {} };
	constructor(collection) {
		this.collection = collection;
	}
	where(columnOrCondition, operator, value) {
		if (typeof columnOrCondition === "object" && columnOrCondition !== null && "type" in columnOrCondition) {
			const next = columnOrCondition;
			this.params.logical = this.params.logical ? {
				type: "and",
				conditions: [this.params.logical, next]
			} : next;
			return this;
		}
		if (!this.params.where) this.params.where = {};
		const column = columnOrCondition;
		const condition = [operator, value];
		const existing = this.params.where[column];
		if (existing === void 0) this.params.where[column] = condition;
		else if (Array.isArray(existing) && existing.length > 0 && Array.isArray(existing[0])) this.params.where[column].push(condition);
		else {
			let firstCondition;
			if (Array.isArray(existing) && existing.length === 2 && typeof existing[0] === "string") firstCondition = existing;
			else firstCondition = ["==", existing];
			this.params.where[column] = [firstCondition, condition];
		}
		return this;
	}
	/**
	* Order the results by a specific column.
	*
	* Call it again to add a tie-breaker rather than replace the sort: keys
	* apply in the order they were added, so
	* `.orderBy("roles").orderBy("created_at", "desc")` sorts by role and
	* shows the newest first within each one.
	*/
	orderBy(column, direction = "asc", nulls) {
		const existing = normalizeOrderBy(this.params.orderBy) ?? [];
		const key = sortKeyToString(column);
		this.params.orderBy = [...existing, nulls ? [
			key,
			direction,
			nulls
		] : [key, direction]];
		return this;
	}
	/**
	* Limit the number of results returned.
	*/
	limit(count) {
		this.params.limit = count;
		return this;
	}
	/**
	* Skip the first N results.
	*/
	offset(count) {
		this.params.offset = count;
		return this;
	}
	/**
	* Set a free-text search string if supported by the backend.
	*
	* By default this is a substring match across the collection's top-level
	* string properties. A Postgres collection that declares a `search` block
	* gets ranked full-text matching over the fields it named instead, and each
	* row comes back with a `_score` you can sort on:
	*
	* ```ts
	* client.data.talents.search("auditor iso 14001").orderBy("_score", "desc").find()
	* ```
	*
	* Pass `{ explain: true }` to have each row report which of the declared
	* fields matched, with a highlighted snippet, on `_matches`:
	*
	* ```ts
	* const { data } = await client.data.talents.search("iso 14001", { explain: true }).find();
	* data[0]._matches
	* // [{ field: "questionnaire.certifications", snippet: "<mark>ISO</mark> <mark>14001</mark> Lead Auditor" }]
	* ```
	*/
	search(searchString, options) {
		this.params.searchString = searchString;
		if (options?.explain !== void 0) this.params.searchExplain = options.explain;
		return this;
	}
	/**
	* Order rows by nearest-neighbour distance to `vector`.
	*
	* The server has supported this from the REST layer since vectors landed;
	* this is the SDK reaching it. Results come back closest-first with a
	* `_distance` on each row, and any `where` / `orderBy` on the same query is
	* a filter applied before the ordering — distance decides the order.
	*
	* You supply the query vector. Rebase stores and searches embeddings; it
	* does not produce them, so this is where whatever model you already use
	* for the stored vectors gets called.
	*
	* @param property - Name of the `vector` property to compare against.
	* @param vector - The query embedding. Its length must match the property's
	*                 declared `dimensions`, or the server answers 400.
	* @example
	* client.data.docs.vectorSearch("embedding", queryVector, { threshold: 0.35 }).limit(10).find()
	*/
	vectorSearch(property, vector, options) {
		this.params.vectorSearch = {
			property,
			vector,
			...options?.distance !== void 0 && { distance: options.distance },
			...options?.threshold !== void 0 && { threshold: options.threshold }
		};
		return this;
	}
	/**
	* Load related rows into the response, in place of their foreign keys.
	*
	* Three spellings, all the same request:
	*
	* ```ts
	* client.data.posts.include("tags", "author")            // names
	* client.data.posts.include("comments.author")           // a dotted path
	* client.data.posts.include({                            // parametrised
	*     comments: {
	*         limit: 5,
	*         where: { published: ["==", true] },
	*         orderBy: ["created_at", "desc"],
	*         include: { author: true }
	*     }
	* })
	* ```
	*
	* Up to three hops deep, and `"*"` still loads every relation one hop
	* deep. A name that is not a relation of the collection is a 400
	* `UNKNOWN_RELATION` — it used to be ignored, which answers 200 with the
	* field missing and reads exactly like a row that has no related row.
	*
	* Repeated calls **merge**: `.include("author").include("tags")` asks for
	* both. Assigning here — which is what it used to do — meant the second
	* call silently discarded the first.
	*/
	include(...relations) {
		this.params.include = mergeIncludeSpecs(this.params.include, relations);
		return this;
	}
	/**
	* Return only these columns.
	*
	* A projection at the database, not a trim of the response: a query that
	* needs two fields of a wide row reads two columns. The primary key always
	* comes back regardless — a row that cannot be addressed cannot be
	* updated, deleted or paged past — and `excludeFromApi` columns stay
	* hidden whether or not they are named.
	*/
	fields(...columns) {
		this.params.fields = [...this.params.fields ?? [], ...columns];
		return this;
	}
	/**
	* Collapse rows that are identical over the columns being returned.
	*
	* Only meaningful alongside {@link fields}: the primary key is always in
	* the projection, so without narrowing it every row is already distinct.
	*
	* ```ts
	* client.data.posts.fields("status").distinct().find()   // the statuses in use
	* ```
	*/
	distinct(enabled = true) {
		this.params.distinct = enabled;
		return this;
	}
	/**
	* Continue after a previous page's `meta.nextCursor` — keyset paging.
	*
	* Unlike `offset`, a row inserted or deleted before the cursor cannot
	* shift the window, so a walk neither repeats nor skips rows. Keep
	* `orderBy` identical across pages: a cursor only continues the listing it
	* came from, and one used against a different sort is refused rather than
	* seeked in an order nobody asked for.
	*/
	after(cursor) {
		this.params.after = cursor;
		return this;
	}
	/**
	* Execute the find query and return the results as flat rows.
	*/
	async find() {
		return this.collection.find(this.params);
	}
	/**
	* Aggregate the rows this query matches instead of returning them.
	*
	* The builder's `where`/`logical`/`search` narrow which rows are
	* aggregated; its `orderBy`, `include` and window do not apply — an
	* aggregate has no rows to sort, no relations to load and no page to
	* continue.
	*
	* ```ts
	* await client.data.orders
	*     .where("created_at", ">=", startOfMonth)
	*     .aggregate({ select: [{ fn: "sum", field: "total" }], groupBy: ["status"] });
	* // [{ status: "paid", sum_total: 41822.5 }, …]
	* ```
	*/
	async aggregate(params) {
		return this.collection.aggregate({
			...params,
			where: this.params.where,
			logical: this.params.logical,
			searchString: this.params.searchString
		});
	}
	/**
	* Page through everything this query matches, one row at a time.
	*
	* `.limit()` on the builder becomes the page size, so the ceiling on a
	* single `find()` is not a ceiling on what the query can read.
	*/
	iterate(options) {
		return this.collection.iterate({
			...this.params,
			...this.params.limit !== void 0 && { pageSize: this.params.limit },
			...options
		});
	}
	/** Collect everything this query matches into one array. */
	findAll(options) {
		return this.collection.findAll({
			...this.params,
			...this.params.limit !== void 0 && { pageSize: this.params.limit },
			...options
		});
	}
	/**
	* Count the records matching this query.
	*/
	async count() {
		return this.collection.count(this.params);
	}
	/**
	* Listen to realtime updates matching this query.
	*
	* The guard that used to stand here — `if (!this.collection.listen) throw`
	* — has moved onto the client itself, which is where the same failure had
	* to be diagnosed anyway: `client.data.posts.listen(…)`, one call short of
	* this builder, answered `undefined is not a function`. The message is now
	* the same either way.
	*/
	listen(onUpdate, onError) {
		return this.collection.listen(this.params, onUpdate, onError);
	}
};
//#endregion
//#region src/collection.ts
/**
* What `listen`/`listenById` say on a client that has no socket.
*
* One sentence, in one place: it used to live in `SDKQueryBuilder.listen` only,
* so the same failure reached callers of `client.data.posts.listen` as
* `undefined is not a function`.
*/
var NO_SOCKET = "Listen is only available when RebaseClient is configured with a websocketUrl, and not when it was created with realtime: false.";
/**
* Counts currently in flight, **per client**, keyed by the exact request they
* issue. Entries live only for the duration of the request — see `count()` for
* why they exist at all.
*
* The map used to be module-level, shared by every client in the process. A
* request key is a path and a query string: it says nothing about *who* is
* asking. So two clients holding different credentials — an admin panel beside
* a signed-in user's client, a per-request server client in a Node process, a
* test asserting one client's calls — counting the same collection would share
* one request, and whichever arrived second was answered with the first one's
* total. Row-level security makes those genuinely different numbers.
*
* Keyed on the transport instead, which is the object a credential lives on.
* A `WeakMap`, so a closed client's entry goes with it.
*/
var inflightCounts = /* @__PURE__ */ new WeakMap();
/** The in-flight map for one transport, created on first use. */
function inflightCountsFor(transport) {
	let map = inflightCounts.get(transport);
	if (!map) {
		map = /* @__PURE__ */ new Map();
		inflightCounts.set(transport, map);
	}
	return map;
}
/**
* Where a fetched row's version is kept.
*
* Non-enumerable, so it does not appear in `Object.keys`, in `JSON.stringify`,
* in a spread into an update body, or in the generated `Row` type — a row's
* version is a fact about the *response*, not a column, and putting it in the
* row proper would mean the server having to strip it back off every write.
*
* A string key rather than a symbol so it survives the structured clone an
* offline store or a worker boundary does… which it does not, and that is the
* honest position: an etag is per-read, and a row that has been through a cache
* has not been read from the server. `etagOf` answers `undefined` there, and a
* conditional write without a tag is an unconditional one.
*/
var ETAG_KEY = "__etag";
/**
* The version of a row, when it came straight from a read that reported one.
*
* ```ts
* const post = await client.data.posts.get(1);
* await client.data.posts.update(1, { title: "New" }, { ifMatch: etagOf(post) });
* ```
*
* `undefined` for a row from `find()` (a list response carries one ETag at
* most, and it would not be this row's), from the offline cache, or from a
* server that does not send them. Passing `undefined` as `ifMatch` sends no
* precondition, so the call above degrades to an ordinary update rather than
* failing — which is the right default, and the reason to read this at the call
* site where the choice is visible.
*/
function etagOf(row) {
	if (!row || typeof row !== "object") return void 0;
	const value = row[ETAG_KEY];
	return typeof value === "string" ? value : void 0;
}
/**
* The headers a write's options ask for.
*
* One function rather than a spread at each call site: the three headers are
* easy to add to one method and forget on the next, which is exactly how single
* `PATCH` and `DELETE` came to send an `Idempotency-Key` the server never read
* and `delete()` came to have no options at all.
*/
function writeHeaders(options) {
	const headers = {};
	if (options?.idempotencyKey) headers["Idempotency-Key"] = options.idempotencyKey;
	if (options?.ifMatch) headers["If-Match"] = options.ifMatch;
	if (options?.returning === false) headers["Prefer"] = "return=minimal";
	return Object.keys(headers).length > 0 ? { headers } : void 0;
}
/** Attach a version to a row without making it part of the row. */
function withETag(row, etag) {
	if (!etag || !row || typeof row !== "object") return row;
	Object.defineProperty(row, ETAG_KEY, {
		value: etag,
		enumerable: false,
		configurable: true,
		writable: true
	});
	return row;
}
function createCollectionClient(transport, slug, ws) {
	const basePath = `/data/${slug}`;
	const client = {
		async find(params) {
			const qs = buildQueryString(params);
			const raw = await transport.request(basePath + qs, { method: "GET" });
			return {
				data: raw.data || [],
				meta: raw.meta
			};
		},
		iterate(params) {
			return paginateFind((p) => client.find(p), params, slug);
		},
		findAll(params) {
			return collectAllPages((p) => client.find(p), params, slug);
		},
		async findById(id) {
			try {
				const meta = {};
				const raw = await transport.request(`${basePath}/${encodeURIComponent(String(id))}`, { method: "GET" }, meta);
				if (!raw) return void 0;
				return withETag(raw, meta.etag);
			} catch (err) {
				if (err instanceof RebaseApiError && err.status === 404) return;
				throw err;
			}
		},
		async get(id) {
			const row = await client.findById(id);
			if (row === void 0) throw new RebaseApiError(`No record with id ${JSON.stringify(String(id))} in "${slug}".`, {
				status: 404,
				code: "NOT_FOUND"
			});
			return row;
		},
		async create(data, id, options) {
			const body = { ...data };
			if (id !== void 0) body.id = id;
			return await transport.request(basePath, {
				method: "POST",
				body: JSON.stringify(body),
				...writeHeaders(options)
			});
		},
		/**
		* Insert, or replace the row already occupying the conflict target.
		*
		* One statement server-side (`INSERT ... ON CONFLICT DO UPDATE`), so
		* unlike a `findById` followed by `create`-or-`update` it cannot lose
		* the race between the two.
		*/
		async upsert(data, options) {
			const query = options?.onConflict?.length ? `?on_conflict=${encodeURIComponent(options.onConflict.join(","))}` : "";
			return await transport.request(`${basePath}${query}`, {
				method: "POST",
				body: JSON.stringify(data),
				...writeHeaders(options)
			});
		},
		async createMany(data, options) {
			if (!Array.isArray(data)) throw new TypeError("createMany expects an array of records.");
			if (data.length === 0) return [];
			const raw = await transport.request(`${basePath}/bulk`, {
				method: "POST",
				body: JSON.stringify({
					rows: data,
					...options?.upsert ? { upsert: true } : {},
					...options?.onConflict?.length ? { onConflict: options.onConflict } : {}
				}),
				...writeHeaders(options)
			});
			if (options?.returning === false) return [];
			return raw.data || [];
		},
		/**
		* `PATCH`, the verb for a merge — `update(id, data: Partial<M>)` sends
		* only the keys the caller named and the server merges the rest.
		*
		* It was `PUT` for a while, on a route that served both so older
		* clients kept working. The alias is gone: the OpenAPI spec, the SDK
		* and the route now name one verb, and a spec-validating gateway in
		* front of the API sees the operation the server actually implements.
		*/
		async update(id, data, options) {
			return await transport.request(`${basePath}/${encodeURIComponent(String(id))}`, {
				method: "PATCH",
				body: JSON.stringify(data),
				...writeHeaders(options)
			});
		},
		async updateMany(updates, options) {
			if (!Array.isArray(updates)) throw new TypeError("updateMany expects an array of { id, data } entries.");
			if (updates.length === 0) return [];
			const raw = await transport.request(`${basePath}/bulk`, {
				method: "PATCH",
				body: JSON.stringify({ updates }),
				...writeHeaders(options)
			});
			if (options?.returning === false) return [];
			return raw.data || [];
		},
		async delete(id, options) {
			await transport.request(`${basePath}/${encodeURIComponent(String(id))}`, {
				method: "DELETE",
				...writeHeaders(options)
			});
		},
		/**
		* `POST .../bulk/delete`, not `DELETE .../bulk`.
		*
		* The honest verb would take the ids in a DELETE body, and that is the
		* one request shape the HTTP ecosystem handles unreliably: bodies on
		* DELETE are permitted but widely dropped by proxies and CDNs, and
		* several OpenAPI generators ignore `requestBody` on a DELETE
		* operation, so a generated client would send the request without its
		* ids. A backend deployed behind arbitrary ingress cannot take that
		* bet. Same reason `:batchDelete` exists in Google's API guidelines.
		*/
		async deleteMany(ids, options) {
			if (!Array.isArray(ids)) throw new TypeError("deleteMany expects an array of ids.");
			if (ids.length === 0) return;
			await transport.request(`${basePath}/bulk/delete`, {
				method: "POST",
				body: JSON.stringify({ ids }),
				...writeHeaders(options)
			});
		},
		async count(params) {
			const qs = buildQueryString({
				...params,
				limit: void 0,
				offset: void 0,
				include: void 0
			});
			const key = basePath + "/count" + qs;
			const inflight = inflightCountsFor(transport);
			const existing = inflight.get(key);
			if (existing) return existing;
			const request = transport.request(key, { method: "GET" }).then((raw) => raw.count ?? 0);
			inflight.set(key, request);
			try {
				return await request;
			} finally {
				inflight.delete(key);
			}
		},
		observe(params, onResult, onError, options) {
			let closed = false;
			let liveDelivered = false;
			let signature;
			const deliver = (result, fromLive) => {
				if (closed) return;
				if (fromLive) liveDelivered = true;
				else if (liveDelivered) return;
				const next = `${result.meta?.total ?? ""}|${JSON.stringify(result.data)}`;
				if (signature !== void 0 && next === signature) return;
				signature = next;
				onResult({
					...result,
					fromCache: false,
					hasPendingWrites: false,
					partial: false
				});
			};
			client.find(params).then((result) => deliver(result, false)).catch((error) => {
				if (!closed) onError?.(error);
			});
			const live = options?.realtime !== false && !isUnsupported(client.listen) ? client.listen(params, (result) => deliver(result, true), onError) : void 0;
			return () => {
				closed = true;
				live?.();
			};
		},
		observeById(id, onResult, onError, options) {
			let closed = false;
			let liveDelivered = false;
			let signature;
			const deliver = (row, fromLive) => {
				if (closed) return;
				if (fromLive) liveDelivered = true;
				else if (liveDelivered) return;
				const next = row === void 0 ? "\0missing" : JSON.stringify(row);
				if (signature !== void 0 && next === signature) return;
				signature = next;
				onResult(row, {
					fromCache: false,
					hasPendingWrites: false
				});
			};
			client.findById(id).then((row) => deliver(row, false)).catch((error) => {
				if (!closed) onError?.(error);
			});
			const live = options?.realtime !== false && !isUnsupported(client.listenById) ? client.listenById(id, (row) => deliver(row, true), onError) : void 0;
			return () => {
				closed = true;
				live?.();
			};
		},
		where(columnOrCondition, operator, value) {
			const builder = new SDKQueryBuilder(client);
			if (typeof columnOrCondition === "object") return builder.where(columnOrCondition);
			return builder.where(columnOrCondition, operator, value);
		},
		orderBy(column, direction, nulls) {
			return new SDKQueryBuilder(client).orderBy(column, direction, nulls);
		},
		limit(count) {
			return new SDKQueryBuilder(client).limit(count);
		},
		offset(count) {
			return new SDKQueryBuilder(client).offset(count);
		},
		search(searchString, options) {
			return new SDKQueryBuilder(client).search(searchString, options);
		},
		vectorSearch(property, vector, options) {
			return new SDKQueryBuilder(client).vectorSearch(property, vector, options);
		},
		include(...relations) {
			return new SDKQueryBuilder(client).include(...relations);
		},
		fields(...columns) {
			return new SDKQueryBuilder(client).fields(...columns);
		},
		distinct(enabled) {
			return new SDKQueryBuilder(client).distinct(enabled);
		},
		after(cursor) {
			return new SDKQueryBuilder(client).after(cursor);
		},
		/**
		* `count`/`sum`/`avg`/`min`/`max` over the matching rows, optionally
		* grouped — `GET /<collection>/aggregate`.
		*
		* The REST route has served this since aggregates landed and the SDK
		* had no method for it, so the only way to reach it from a typed client
		* was to hand-build the URL. `findAll()` and a reduce is the thing this
		* exists to replace: wrong under a `limit`, unaffordable without one.
		*/
		async aggregate(params) {
			const qs = buildAggregateQueryString(params);
			return (await transport.request(`${basePath}/aggregate${qs}`, { method: "GET" })).data || [];
		},
		listen: unsupportedMethod(NO_SOCKET),
		listenById: unsupportedMethod(NO_SOCKET)
	};
	if (ws) {
		client.listen = (params, onUpdate, onError) => {
			let active = true;
			let lastUpdateId = 0;
			let lastKnownTotal;
			const window = resolveFindWindow(params);
			const unsub = ws.listenCollection({
				path: slug,
				filter: params?.where,
				logical: params?.logical,
				limit: params?.limit,
				offset: window.driverOffset,
				page: params?.page,
				include: params?.include,
				fields: params?.fields,
				distinct: params?.distinct,
				orderBy: normalizeOrderBy(params?.orderBy),
				searchString: params?.searchString,
				searchExplain: params?.searchExplain,
				vectorSearch: params?.vectorSearch
			}, (incomingRows, frameMeta) => {
				const currentUpdateId = ++lastUpdateId;
				const requestedLimit = window.limit;
				const offset = window.offset;
				const rows = incomingRows;
				const emit = (total, hasMore, nextCursor) => {
					if (!active || currentUpdateId !== lastUpdateId) return;
					onUpdate({
						data: rows,
						meta: {
							total,
							limit: frameMeta?.limit ?? requestedLimit,
							offset: frameMeta?.offset ?? offset,
							hasMore,
							...nextCursor && { nextCursor }
						}
					});
				};
				if (frameMeta && frameMeta.total !== void 0) {
					lastKnownTotal = frameMeta.total;
					emit(frameMeta.total, frameMeta.hasMore, frameMeta.nextCursor);
					return;
				}
				if (lastKnownTotal !== void 0) {
					emit(lastKnownTotal, offset + rows.length < lastKnownTotal, frameMeta?.nextCursor);
					return;
				}
				client.count(params).then((total) => {
					lastKnownTotal = total;
					emit(total, offset + rows.length < total, frameMeta?.nextCursor);
				}).catch(() => {
					emit(offset + rows.length, rows.length >= requestedLimit, frameMeta?.nextCursor);
				});
			}, onError);
			return () => {
				active = false;
				unsub();
			};
		};
		client.listenById = (id, onUpdate, onError) => {
			return ws.listenOne({
				path: slug,
				id: String(id)
			}, (row) => {
				if (row) onUpdate(row);
				else onUpdate(void 0);
			}, onError);
		};
	}
	return client;
}
//#endregion
//#region src/functions.ts
/**
* Create a `FunctionsClient` backed by the given transport.
*
* The transport already handles:
* - Base URL resolution
* - JWT injection via `Authorization: Bearer`
* - 401 retry / `onUnauthorized` flow
* - Consistent error throwing via `RebaseApiError`
*
* @internal
*/
function createFunctionsClient(transport) {
	return { async invoke(name, payload, options) {
		if (name.includes("/")) throw new RebaseClientError$1(`Invalid function name "${name}": a function name is a single path segment. Pass anything after it as \`options.path\` — invoke("${name.split("/")[0]}", payload, { path: "${name.split("/").slice(1).join("/")}" }).`, {
			code: "INVALID_FUNCTION_NAME",
			details: { name }
		});
		const method = options?.method ?? "POST";
		const rawPath = options?.path;
		const subPath = rawPath ? /^[?#]/.test(rawPath) ? rawPath : `/${rawPath.replace(/^\//, "")}` : "";
		const routePath = `/functions/${encodeURIComponent(name)}${subPath}`;
		const init = { method };
		if (payload !== void 0 && method !== "GET") init.body = JSON.stringify(payload);
		if (options?.headers) init.headers = options.headers;
		return transport.request(routePath, init);
	} };
}
//#endregion
//#region src/storage.ts
/**
* Create a StorageSource that talks to the Rebase backend REST API.
*
* @param transport - HTTP transport instance
* @param storageId - Optional storage-source key for multi-backend routing.
*                    When set, it is forwarded to the server so the correct
*                    `StorageController` is resolved from the registry.
*/
/**
* A storage key, encoded for a URL path.
*
* Every call below interpolated the key raw, and the server decodes what it
* receives — so three ordinary filenames did three different wrong things:
*
*   `Invoice #12.pdf`  the `#` began the fragment, the server saw
*                      `default/Invoice ` and looked up a key that is not the
*                      file — and a scoped `?token=` after it was swallowed too
*   `100% done.png`    `decodeURIComponent` threw URIError → 500
*   `a%2Fb.png`        decoded to `a/b.png`, silently resolving a DIFFERENT
*                      object
*
* Per SEGMENT, because `/` is the key's own separator and must survive; every
* other character is encoded, including a literal `%`. Verified to round-trip
* through the server's decode for all of the above, plus `+`, accented
* characters and a trailing `%`.
*/
function encodeStorageKey(key) {
	return key.split("/").map(encodeURIComponent).join("/");
}
function createStorage(transport, storageId) {
	const urlsCache = /* @__PURE__ */ new Map();
	/**
	* Base for URLs the *browser* will fetch on its own (file downloads,
	* previews). API requests keep going to `baseUrl`; see
	* {@link RebaseClientConfig.storageUrlOrigin} for why these can differ.
	*/
	const fileUrlBase = () => `${transport.storageUrlOrigin ?? transport.baseUrl}${transport.apiPath}`;
	/** Append ?storageId=... to a path when multi-backend routing is active. */
	const withStorageId = (path) => {
		if (!storageId) return path;
		return `${path}${path.includes("?") ? "&" : "?"}storageId=${encodeURIComponent(storageId)}`;
	};
	async function putObject({ file, key, metadata, bucket, public: isPublic, context }) {
		const formData = new FormData();
		formData.append("file", file);
		if (context?.collection && context?.property) {
			formData.append("collection", context.collection);
			formData.append("property", context.property);
		}
		let effectiveKey = key;
		if (isPublic && effectiveKey && !isPublicStoragePath(effectiveKey)) effectiveKey = `${PUBLIC_STORAGE_PREFIX}${effectiveKey.replace(/^\/+/, "")}`;
		if (effectiveKey) formData.append("key", effectiveKey);
		if (bucket) formData.append("bucket", bucket);
		if (storageId) formData.append("storageId", storageId);
		if (metadata) {
			for (const [key, value] of Object.entries(metadata)) if (value !== void 0 && value !== null) formData.append(`metadata_${key}`, typeof value === "string" ? value : JSON.stringify(value));
		}
		return (await transport.request(withStorageId("/storage/upload"), {
			method: "POST",
			body: formData,
			headers: {}
		})).data;
	}
	async function getSignedUrl(keyOrUrl, bucket) {
		const cacheKey = bucket ? `${bucket}/${keyOrUrl}` : keyOrUrl;
		const cachedEntry = urlsCache.get(cacheKey);
		if (cachedEntry) {
			if (!cachedEntry.expiresAt || cachedEntry.expiresAt > Date.now()) return cachedEntry.config;
			urlsCache.delete(cacheKey);
		}
		let filePath = keyOrUrl;
		if (filePath && (filePath.startsWith("local://") || filePath.startsWith("s3://") || filePath.startsWith("gs://"))) filePath = filePath.substring(filePath.indexOf("://") + 3);
		if (bucket && filePath && !filePath.startsWith(bucket)) filePath = `${bucket}/${filePath}`;
		if (!filePath || filePath.trim() === "" || filePath === "/") return {
			url: null,
			fileNotFound: true
		};
		if (isPublicStoragePath(filePath)) {
			const publicConfig = { url: withStorageId(`${fileUrlBase()}/storage/file/${encodeStorageKey(filePath)}`) };
			urlsCache.set(cacheKey, { config: publicConfig });
			return publicConfig;
		}
		try {
			const result = await transport.request(withStorageId(`/storage/metadata/${encodeStorageKey(filePath)}`));
			if (result.data.public) {
				const publicConfig = {
					url: withStorageId(`${fileUrlBase()}/storage/file/${encodeStorageKey(filePath)}`),
					metadata: result.data
				};
				urlsCache.set(cacheKey, { config: publicConfig });
				return publicConfig;
			}
			const scopedToken = result.data.token;
			const tokenQuery = scopedToken ? `?token=${scopedToken}` : "";
			const downloadConfig = {
				url: withStorageId(`${fileUrlBase()}/storage/file/${encodeStorageKey(filePath)}${tokenQuery}`),
				metadata: result.data
			};
			const expiresAt = result.data.tokenExpiresIn ? Date.now() + (result.data.tokenExpiresIn - 10) * 1e3 : void 0;
			urlsCache.set(cacheKey, {
				config: downloadConfig,
				expiresAt
			});
			return downloadConfig;
		} catch (e) {
			if (e instanceof Error && "status" in e && e.status === 404) return {
				url: null,
				fileNotFound: true
			};
			throw e;
		}
	}
	async function getObject(key, bucket) {
		const downloadConfig = await getSignedUrl(key, bucket);
		if (downloadConfig.fileNotFound || !downloadConfig.url) return null;
		const response = await transport.fetchFn(downloadConfig.url, { headers: {} });
		if (response.status === 404) return null;
		if (!response.ok) throw new Error("Failed to get file");
		const blob = await response.blob();
		const fileName = (bucket ? `${bucket}/${key}` : key).split("/").pop() || "file";
		return new File([blob], fileName, { type: blob.type });
	}
	async function deleteObject(key, bucket) {
		let filePath = key;
		if (filePath && (filePath.startsWith("local://") || filePath.startsWith("s3://") || filePath.startsWith("gs://"))) filePath = filePath.substring(filePath.indexOf("://") + 3);
		if (bucket && filePath && !filePath.startsWith(bucket)) filePath = `${bucket}/${filePath}`;
		if (!filePath || filePath.trim() === "" || filePath === "/") return;
		try {
			await transport.request(withStorageId(`/storage/file/${encodeStorageKey(filePath)}`), { method: "DELETE" });
		} catch (e) {
			if (!(e instanceof Error && "status" in e && e.status === 404)) throw e;
		}
		urlsCache.delete(bucket ? `${bucket}/${key}` : key);
	}
	async function listObjects(prefix, options) {
		const params = new URLSearchParams();
		if (prefix) params.set("prefix", prefix);
		if (options?.bucket) params.set("bucket", options.bucket);
		if (options?.maxResults) params.set("maxResults", String(options.maxResults));
		if (options?.pageToken) params.set("pageToken", options.pageToken);
		if (storageId) params.set("storageId", storageId);
		return (await transport.request(`/storage/list?${params.toString()}`)).data;
	}
	return {
		putObject,
		getSignedUrl,
		getObject,
		deleteObject,
		listObjects
	};
}
//#endregion
//#region src/storage-registry.ts
/**
* Default implementation of the client-side `StorageSourceRegistry`.
*/
var ClientStorageSourceRegistry = class ClientStorageSourceRegistry {
	sources = /* @__PURE__ */ new Map();
	/**
	* Register a storage source.
	* @param key - Unique key matching a `StorageSourceDefinition.key`
	* @param source - The `StorageSource` instance
	*/
	register(key, source) {
		this.sources.set(key, source);
	}
	getDefault() {
		const source = this.sources.get(DEFAULT_STORAGE_SOURCE_KEY);
		if (!source) throw new Error(`[StorageSourceRegistry] No default storage source registered. Register one with key "${DEFAULT_STORAGE_SOURCE_KEY}".`);
		return source;
	}
	get(key) {
		if (key === void 0 || key === null) return this.sources.get(DEFAULT_STORAGE_SOURCE_KEY);
		return this.sources.get(key);
	}
	getOrDefault(key) {
		if (key === void 0 || key === null) return this.getDefault();
		const source = this.sources.get(key);
		if (source) return source;
		console.warn(`[StorageSourceRegistry] Storage source "${key}" not found, falling back to "${DEFAULT_STORAGE_SOURCE_KEY}".`);
		return this.getDefault();
	}
	has(key) {
		return this.sources.has(key);
	}
	list() {
		return Array.from(this.sources.keys());
	}
	/**
	* Build a registry from `StorageSourceDefinition[]` and an HTTP transport.
	*
	* - Sources with `transport: "server"` are auto-wired via `createStorage(transport, key)`.
	* - Sources with `transport: "direct"` are **not** auto-wired — they must
	*   be registered manually after this call (e.g. via a Firebase hook).
	*
	* @param definitions - Array of storage source definitions
	* @param transport - HTTP transport for server-backed sources
	*/
	static fromDefinitions(definitions, transport) {
		const registry = new ClientStorageSourceRegistry();
		for (const def of definitions) if (def.transport === "server") {
			const source = createStorage(transport, def.key === DEFAULT_STORAGE_SOURCE_KEY ? void 0 : def.key);
			registry.register(def.key, source);
		}
		return registry;
	}
};
//#endregion
//#region src/websocket.ts
/**
* Extract error message and code from a WebSocket message payload.
* Handles both `{ error: string }` and `{ error: { message, code } }` shapes.
*/
function extractMessageError(message) {
	const payload = message.payload;
	const errPayload = payload?.error;
	const errorMessage = typeof errPayload === "object" ? errPayload.message : payload?.message || (typeof errPayload === "string" ? errPayload : void 0) || message.error || "Unknown error";
	const errorCode = typeof errPayload === "object" ? errPayload.code : payload?.code;
	return {
		errorMessage: typeof errorMessage === "string" ? errorMessage : errorMessage == null ? "Unknown error" : JSON.stringify(errorMessage),
		errorCode
	};
}
/**
* The error an `ERROR` frame describes, with the server's `details` when it
* sent them — `{ stage, path }` for a collection-callback refusal, the object
* the REST envelope carries for the same veto. `status` stays `undefined`:
* a frame is not an HTTP response (see `RebaseErrorInit.status`).
*/
function frameError(message) {
	const { errorMessage, errorCode } = extractMessageError(message);
	const errPayload = message.payload?.error;
	return new RebaseApiError$1(errorMessage, {
		code: errorCode,
		details: typeof errPayload === "object" && errPayload !== null ? errPayload.details : void 0
	});
}
/**
* Broadcast and presence frames.
*
* Fire-and-forget (the server sends no response envelope), and exempt from the
* client-side auth gate — a public channel is usable without an account.
*/
var CHANNEL_MESSAGE_TYPES = /* @__PURE__ */ new Set([
	"join_channel",
	"leave_channel",
	"broadcast",
	"presence_track",
	"presence_untrack",
	"presence_state",
	"channel_history"
]);
/**
* The other direction: the frame types the *server* addresses by channel.
*
* Not the mirror of {@link CHANNEL_MESSAGE_TYPES} — a request and its answer
* have different names (`presence_state` is the one type in both) — so the two
* sets are listed separately rather than derived from each other. These are
* exactly the members of `ChannelMessage`; adding one here without declaring it
* there leaves the handler with a frame it cannot read.
*/
var CHANNEL_FRAME_TYPES = /* @__PURE__ */ new Set([
	"broadcast",
	"presence_state",
	"presence_diff",
	"channel_history",
	"error",
	"ERROR"
]);
/**
* Low-level realtime WebSocket client.
*
* @internal Not a stable app-facing API. `createRebaseClient()` constructs and
* manages this internally (exposed as `client.ws`, typed by the minimal
* `RebaseWebSocket` contract in `@rebasepro/types`). It stays exported from the
* package root for the same reason it always was — a data-source driver may
* instantiate it directly — but nothing in this repo does since
* `@rebasepro/client-postgres` was removed; its surface may change without a
* major bump.
*/
/**
* Narrow a frame to the collection-update shape. See its use below.
*
* `WebSocketMessage.type` is `string`, so the message types that extend it do
* not form a discriminated union and `type === "collection_update"` narrows
* nothing on its own.
*/
function isCollectionUpdate(message) {
	return message.type === "collection_update";
}
/**
* Narrow a frame to one of the channel-addressed shapes.
*
* Both halves of the test are load-bearing. The type says which member of
* {@link ChannelMessage} it is; `channel` is what a channel frame is *addressed
* by*, and an `ERROR` from a server older than the change that names the
* channel has none — it is a generic error, and falls through to the catch-all
* warning rather than being delivered to a channel that was never named.
*/
function isChannelMessage(message) {
	return typeof message.channel === "string" && CHANNEL_FRAME_TYPES.has(message.type);
}
var RebaseWebSocketClient = class {
	websocketUrl;
	ws = null;
	getAuthToken;
	listeners = /* @__PURE__ */ new Map();
	/** Channel-name → handlers, for broadcast and presence frames. */
	channelHandlers = /* @__PURE__ */ new Map();
	/** Set by `close()`. Blocks any later operation from silently redialling. */
	closedByCaller = false;
	/**
	* Set when the backoff budget ran out, cleared by anything that earns a
	* fresh one.
	*
	* Unlike {@link closedByCaller} this is not final — nobody *asked* for the
	* socket to stay down. Five attempts with exponential backoff is about a
	* minute, which a laptop lid, a wifi handover or a backend rollout all
	* exceed routinely; treating that as permanent meant realtime silently
	* stopped for the rest of the page's life, with a reload the only cure.
	*/
	gaveUp = false;
	/**
	* Whether a socket exists at all (open or still opening).
	*
	* Lets callers distinguish "authenticate the live socket" from "there is
	* nothing to authenticate yet", without that question forcing a dial.
	*/
	get hasSocket() {
		return this.ws !== null;
	}
	/** So the "no WebSocket in this environment" warning is said once, not per call. */
	warnedNoWebSocket = false;
	/** Subscribe to broadcast/presence frames for one channel. */
	onChannelMessage(channel, handler) {
		if (!this.channelHandlers.has(channel)) this.channelHandlers.set(channel, /* @__PURE__ */ new Set());
		this.channelHandlers.get(channel).add(handler);
		return () => {
			const handlers = this.channelHandlers.get(channel);
			if (!handlers) return;
			handlers.delete(handler);
			if (handlers.size === 0) this.channelHandlers.delete(channel);
		};
	}
	/** Notified after the socket comes back, so channels can re-join. */
	onReconnect(handler) {
		return this.on("reconnect", handler);
	}
	on(event, cb) {
		if (!this.listeners.has(event)) this.listeners.set(event, /* @__PURE__ */ new Set());
		this.listeners.get(event).add(cb);
		return () => this.listeners.get(event).delete(cb);
	}
	emit(event, ...args) {
		if (this.listeners.has(event)) this.listeners.get(event).forEach((cb) => cb(...args));
	}
	collectionSubscriptions = /* @__PURE__ */ new Map();
	singleSubscriptions = /* @__PURE__ */ new Map();
	backendToCollectionKey = /* @__PURE__ */ new Map();
	backendToEntityKey = /* @__PURE__ */ new Map();
	pendingRequests = /* @__PURE__ */ new Map();
	reconnectAttempts = 0;
	maxReconnectAttempts = 5;
	isConnected = false;
	messageQueue = [];
	requestTimeoutMs = 3e4;
	subscriptionTimeoutMs = 3e4;
	reconnectTimeout = null;
	isAuthenticated = false;
	authPromise = null;
	WebSocketConstructor;
	onUnauthorized;
	refreshInProgress = null;
	constructor(config) {
		this.websocketUrl = config.websocketUrl;
		this.getAuthToken = config.getAuthToken;
		this.onUnauthorized = config.onUnauthorized;
		this.WebSocketConstructor = config.WebSocket || (typeof WebSocket !== "undefined" ? WebSocket : void 0);
	}
	/**
	* Open the socket if it is not open (or opening) already.
	*
	* Idempotent, synchronous, and safe to call on every operation that needs a
	* live socket — `initWebSocket` already no-ops on an open socket and is
	* re-entrant, since the reconnect path has always called it.
	*/
	ensureConnected() {
		if (this.closedByCaller) return;
		if (!this.WebSocketConstructor) {
			if (!this.warnedNoWebSocket) {
				this.warnedNoWebSocket = true;
				console.warn("WebSocket is not defined in this environment. Realtime subscriptions will not work unless you provide a WebSocket implementation in the config.");
			}
			return;
		}
		this.installOnlineListener();
		if (this.ws || this.reconnectTimeout) return;
		if (this.gaveUp) {
			this.gaveUp = false;
			this.reconnectAttempts = 0;
		}
		this.initWebSocket();
	}
	/**
	* The browser says the network is back — the usual reason the budget ran
	* out in the first place. Registered lazily so a Node client, or a page
	* that never subscribes, adds no listener.
	*/
	installOnlineListener() {
		if (this.onlineListener || typeof window === "undefined" || typeof window.addEventListener !== "function") return;
		this.onlineListener = () => {
			if (this.closedByCaller || !this.gaveUp) return;
			console.debug("Network is back — retrying the realtime connection");
			this.ensureConnected();
		};
		window.addEventListener("online", this.onlineListener);
	}
	onlineListener = null;
	/**
	* Authenticate the WebSocket connection
	*/
	async authenticate(token) {
		return new Promise((resolve, reject) => {
			const requestId = `auth_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;
			const timeout = setTimeout(() => {
				this.pendingRequests.delete(requestId);
				reject(/* @__PURE__ */ new Error("Authentication timeout"));
			}, 3e4);
			this.pendingRequests.set(requestId, {
				resolve: () => {
					clearTimeout(timeout);
					this.isAuthenticated = true;
					resolve();
				},
				reject: (error) => {
					clearTimeout(timeout);
					reject(error);
				}
			});
			const message = {
				type: "AUTHENTICATE",
				requestId,
				payload: { token }
			};
			if (!this.isConnected || !this.ws) this.messageQueue.unshift(message);
			else this.ws.send(JSON.stringify(message));
		});
	}
	/**
	* Set the auth token getter function
	*/
	setAuthTokenGetter(getAuthToken) {
		this.getAuthToken = getAuthToken;
		if (this.isConnected && !this.isAuthenticated && !this.authPromise) {
			console.debug("WebSocket auto-authenticating after token getter set");
			this.getAuthToken().then((token) => {
				if (!this.ws) return;
				if (token) this.authenticate(token).catch((e) => {
					if (this.ws) console.debug("WebSocket auto-auth skipped:", e?.message || e);
				});
			}).catch((e) => {
				if (this.ws) console.debug("WebSocket auto-auth skipped:", e?.message || e);
			});
		}
	}
	/**
	* Drop the socket.
	*
	* `permanent` distinguishes the two callers. Signing out drops the socket
	* but the client stays usable — a later subscribe should reconnect
	* anonymously. `client.close()` is the caller saying they are done, and
	* must not be undone by a stray queued frame.
	*/
	disconnect(permanent = false) {
		if (permanent) this.closedByCaller = true;
		if (permanent && this.onlineListener && typeof window !== "undefined") {
			window.removeEventListener("online", this.onlineListener);
			this.onlineListener = null;
		}
		this.isAuthenticated = false;
		this.authPromise = null;
		if (this.reconnectTimeout) {
			clearTimeout(this.reconnectTimeout);
			this.reconnectTimeout = null;
		}
		this.suspendSubscribeWatchdogs();
		if (this.ws) {
			this.ws.onclose = null;
			this.ws.onerror = null;
			this.ws.onopen = null;
			this.ws.onmessage = null;
			this.ws.close();
			this.ws = null;
		}
	}
	initWebSocket() {
		if (!this.WebSocketConstructor) return;
		if (this.ws?.readyState === this.WebSocketConstructor.OPEN) return;
		if (this.ws) {
			this.ws.onclose = null;
			this.ws.close();
			this.ws = null;
		}
		try {
			const socket = new this.WebSocketConstructor(this.websocketUrl);
			this.ws = socket;
			this.ws.onopen = async () => {
				console.debug("Connected to PostgreSQL backend");
				const wasReconnect = this.reconnectAttempts > 0;
				this.isConnected = true;
				this.reconnectAttempts = 0;
				if (this.getAuthToken && !this.isAuthenticated) try {
					const token = await this.getAuthToken();
					if (token) {
						await this.authenticate(token);
						console.debug("WebSocket auto-authenticated");
					}
				} catch (error) {
					console.debug("WebSocket connected without auth:", error?.message || error);
				}
				this.emit(wasReconnect ? "reconnect" : "connect");
				this.processMessageQueue();
				if (wasReconnect) this.resubscribeAll();
				this.armPendingSubscribeWatchdogs();
			};
			this.ws.onmessage = (event) => {
				try {
					const message = JSON.parse(event.data, rebaseReviver);
					this.handleWebSocketMessage(message);
				} catch (error) {
					console.error("Error parsing WebSocket message:", error);
				}
			};
			this.ws.onclose = () => {
				console.debug("Disconnected from PostgreSQL backend");
				if (this.ws === socket) this.ws = null;
				this.isConnected = false;
				this.isAuthenticated = false;
				this.authPromise = null;
				this.suspendSubscribeWatchdogs();
				this.emit("disconnect");
				for (const [reqId, request] of this.pendingRequests.entries()) {
					if (reqId.startsWith("auth_")) request.reject(/* @__PURE__ */ new Error("Connection closed during authentication"));
					else if (request.message) {
						request.message._queuedResolve = request.resolve;
						request.message._queuedReject = request.reject;
						this.messageQueue.push(request.message);
					} else request.reject(new RebaseApiError$1("Connection closed"));
					this.pendingRequests.delete(reqId);
				}
				this.attemptReconnect();
			};
			this.ws.onerror = (error) => {
				console.error("WebSocket error:", error);
				this.isConnected = false;
				this.emit("error", error);
			};
		} catch (error) {
			console.error("Failed to initialize WebSocket:", error);
			this.attemptReconnect();
		}
	}
	processMessageQueue() {
		while (this.messageQueue.length > 0 && this.isConnected) {
			const message = this.messageQueue.shift();
			if (message) this.sendMessage(message);
		}
	}
	attemptReconnect() {
		if (this.reconnectAttempts >= this.maxReconnectAttempts) {
			console.error("Max reconnection attempts reached");
			this.gaveUp = true;
			this.failAllPendingSubscriptions(new RebaseApiError$1("Connection lost", { code: "CONNECTION_LOST" }));
			return;
		}
		this.reconnectAttempts++;
		const delay = Math.min(1e3 * Math.pow(2, this.reconnectAttempts), 3e4);
		console.debug(`Attempting to reconnect in ${delay}ms (attempt ${this.reconnectAttempts})`);
		if (this.reconnectTimeout) clearTimeout(this.reconnectTimeout);
		this.reconnectTimeout = setTimeout(() => {
			this.reconnectTimeout = null;
			this.initWebSocket();
		}, delay);
	}
	isAuthError(message) {
		if (message.type === "AUTH_ERROR") return true;
		const { errorMessage, errorCode } = extractMessageError(message);
		if (errorCode === "UNAUTHORIZED" || errorCode === "JWT_EXPIRED" || errorCode === "AUTH_ERROR") return true;
		const lowerMessage = errorMessage.toLowerCase();
		return lowerMessage.includes("unauthorized") || lowerMessage.includes("token expired") || lowerMessage.includes("token is expired") || lowerMessage.includes("invalid token") || lowerMessage.includes("session expired") || lowerMessage.includes("auth error");
	}
	async handleAuthFailure() {
		if (this.refreshInProgress) return this.refreshInProgress;
		this.refreshInProgress = (async () => {
			this.isAuthenticated = false;
			this.authPromise = null;
			if (this.onUnauthorized) try {
				if (await this.onUnauthorized() && this.getAuthToken) {
					const token = await this.getAuthToken();
					if (token) {
						await this.authenticate(token);
						return true;
					}
				}
			} catch (error) {
				console.error("WebSocket auth refresh failed:", error);
			}
			return false;
		})();
		try {
			return await this.refreshInProgress;
		} finally {
			this.refreshInProgress = null;
		}
	}
	/**
	* Shared logic for re-subscribing a collection or row subscription
	* after an auth error is resolved by refreshing credentials.
	*/
	resubscribeAfterAuthRefresh(message, subscription, subscriptionKey, idPrefix, backendKeyMap, messageType) {
		this.handleAuthFailure().then((refreshed) => {
			if (refreshed) {
				const oldBackendId = subscription.backendSubscriptionId;
				const newBackendId = `${idPrefix}_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;
				subscription.backendSubscriptionId = newBackendId;
				backendKeyMap.delete(oldBackendId);
				backendKeyMap.set(newBackendId, subscriptionKey);
				if (messageType === "subscribe_collection") this.sendCollectionSubscribe(subscriptionKey);
				else this.sendEntitySubscribe(subscriptionKey);
				return;
			}
			const error = frameError(message);
			if (messageType === "subscribe_collection") this.failCollectionSubscription(subscriptionKey, error);
			else this.failEntitySubscription(subscriptionKey, error);
		}).catch((err) => {
			const error = err instanceof Error ? err : new Error(String(err));
			if (messageType === "subscribe_collection") this.failCollectionSubscription(subscriptionKey, error);
			else this.failEntitySubscription(subscriptionKey, error);
		});
	}
	handleWebSocketMessage(message) {
		const { type, requestId, subscriptionId } = message;
		if (requestId && this.pendingRequests.has(requestId)) {
			const pendingReq = this.pendingRequests.get(requestId);
			if (type === "ERROR" || type === "AUTH_ERROR" || message.error) if (this.isAuthError(message)) {
				this.pendingRequests.delete(requestId);
				this.handleAuthFailure().then((refreshed) => {
					if (refreshed && pendingReq.message) this.doSendMessage(pendingReq.message, pendingReq.resolve, pendingReq.reject).catch(pendingReq.reject);
					else pendingReq.reject(frameError(message));
				}).catch((err) => {
					pendingReq.reject(err);
				});
			} else {
				this.pendingRequests.delete(requestId);
				pendingReq.reject(frameError(message));
			}
			else {
				this.pendingRequests.delete(requestId);
				pendingReq.resolve(message.payload || message);
			}
			return;
		}
		if (isChannelMessage(message)) {
			const handlers = this.channelHandlers.get(message.channel);
			if (handlers) for (const handler of [...handlers]) try {
				handler(message);
			} catch (error) {
				console.error("Error in channel handler:", error);
			}
			return;
		}
		if (subscriptionId && isCollectionUpdate(message)) {
			const collectionUpdate = message;
			const subscriptionKey = this.backendToCollectionKey.get(subscriptionId);
			if (subscriptionKey) {
				const collectionSub = this.collectionSubscriptions.get(subscriptionKey);
				if (collectionSub) {
					const incomingRows = message.rows || [];
					const updatePks = collectionUpdate.pks;
					if (updatePks) collectionSub.pks = updatePks;
					const updateMeta = collectionUpdate.meta;
					if (updateMeta) collectionSub.latestMeta = updateMeta;
					const rows = this.mergeRows(collectionSub.latestData, incomingRows, collectionSub.pks);
					collectionSub.latestData = rows;
					collectionSub.lastUpdated = Date.now();
					collectionSub.isInitialDataReceived = true;
					if (collectionSub.subscribeTimeout) clearTimeout(collectionSub.subscribeTimeout);
					collectionSub.subscribeTimeout = void 0;
					collectionSub.subscribeInFlight = false;
					collectionSub.callbacks.forEach((callback) => {
						try {
							callback.onUpdate(rows, collectionSub.latestMeta);
						} catch (error) {
							console.error("Error in collection subscription callback:", error);
							if (callback.onError) callback.onError(error instanceof Error ? error : new Error(String(error)));
						}
					});
					return;
				}
			}
		}
		if (subscriptionId && type === "single_update") {
			const subscriptionKey = this.backendToEntityKey.get(subscriptionId);
			if (subscriptionKey) {
				const entitySub = this.singleSubscriptions.get(subscriptionKey);
				if (entitySub) {
					const wireEntity = message.row ?? null;
					const row = wireEntity ? wireEntity : null;
					entitySub.latestData = row;
					entitySub.lastUpdated = Date.now();
					entitySub.isInitialDataReceived = true;
					if (entitySub.subscribeTimeout) clearTimeout(entitySub.subscribeTimeout);
					entitySub.subscribeTimeout = void 0;
					entitySub.subscribeInFlight = false;
					entitySub.callbacks.forEach((callback) => {
						try {
							callback.onUpdate(row);
						} catch (error) {
							console.error("Error in row subscription callback:", error);
							if (callback.onError) callback.onError(error instanceof Error ? error : new Error(String(error)));
						}
					});
					return;
				}
			}
		}
		if (subscriptionId && (type === "ERROR" || message.error)) {
			const collectionKey = this.backendToCollectionKey.get(subscriptionId);
			if (collectionKey) {
				const collectionSub = this.collectionSubscriptions.get(collectionKey);
				if (collectionSub) {
					if (this.isAuthError(message)) {
						this.resubscribeAfterAuthRefresh(message, collectionSub, collectionKey, "collection", this.backendToCollectionKey, "subscribe_collection");
						return;
					}
					if (collectionSub.subscribeTimeout) clearTimeout(collectionSub.subscribeTimeout);
					collectionSub.subscribeTimeout = void 0;
					collectionSub.subscribeInFlight = false;
					const error = frameError(message);
					collectionSub.callbacks.forEach((callback) => {
						if (callback.onError) callback.onError(error);
					});
					return;
				}
			}
			const entityKey = this.backendToEntityKey.get(subscriptionId);
			if (entityKey) {
				const entitySub = this.singleSubscriptions.get(entityKey);
				if (entitySub) {
					if (this.isAuthError(message)) {
						this.resubscribeAfterAuthRefresh(message, entitySub, entityKey, "row", this.backendToEntityKey, "subscribe_one");
						return;
					}
					if (entitySub.subscribeTimeout) clearTimeout(entitySub.subscribeTimeout);
					entitySub.subscribeTimeout = void 0;
					entitySub.subscribeInFlight = false;
					const error = frameError(message);
					entitySub.callbacks.forEach((callback) => {
						if (callback.onError) callback.onError(error);
					});
					return;
				}
			}
		}
		if (type === "ERROR" || type === "error" || message.error) {
			const { errorMessage, errorCode } = extractMessageError(message);
			console.warn(`[Rebase] Realtime error from the server${errorCode ? ` (${errorCode})` : ""}: ${errorMessage}`);
		}
	}
	async ensureAuthenticated(retryCount = 3) {
		if (this.isAuthenticated || !this.getAuthToken) return;
		if (!this.authPromise) {
			this.authPromise = this.runAuthentication(retryCount);
			this.authPromise.finally(() => {
				this.authPromise = null;
			}).catch(() => void 0);
		}
		await this.authPromise;
	}
	async runAuthentication(retryCount) {
		let lastError = null;
		for (let attempt = 0; attempt < retryCount; attempt++) try {
			const token = await this.getAuthToken();
			if (!token) throw new Error("user not logged in");
			await this.authenticate(token);
			console.debug("WebSocket authenticated on demand");
			return;
		} catch (error) {
			lastError = error;
			const errMsg = error instanceof Error ? error.message : String(error);
			if (errMsg.includes("not logged in") || errMsg.includes("Session expired")) {
				console.warn("WebSocket auth failed: user not logged in");
				throw error;
			}
			if (errMsg.includes("still loading")) {
				if (attempt < retryCount - 1) {
					const delay = Math.min(500 * (attempt + 1), 2e3);
					await new Promise((resolve) => setTimeout(resolve, delay));
					continue;
				}
			}
			if (attempt < retryCount - 1) {
				const delay = Math.min(1e3 * (attempt + 1), 3e3);
				console.debug(`WebSocket auth attempt ${attempt + 1} failed, retrying in ${delay}ms...`);
				await new Promise((resolve) => setTimeout(resolve, delay));
			}
		}
		console.warn("WebSocket on-demand auth failed after retries:", lastError);
		throw lastError;
	}
	async reauthenticate() {
		if (!this.getAuthToken) return;
		this.isAuthenticated = false;
		try {
			const token = await this.getAuthToken();
			if (!token) throw new Error("user not logged in");
			await this.authenticate(token);
			console.debug("WebSocket reauthenticated successfully");
		} catch (error) {
			console.error("WebSocket reauthentication failed:", error);
			throw error;
		}
	}
	/**
	* Public because `RebaseRealtimeChannel` sends channel frames through it.
	* Not part of the stable surface — prefer `client.realtime.channel(name)`.
	*/
	sendMessage(message) {
		const queuedMsg = message;
		if (queuedMsg._queuedResolve && queuedMsg._queuedReject) return this.doSendMessage(message, queuedMsg._queuedResolve, queuedMsg._queuedReject);
		if (!this.isConnected || !this.ws) {
			this.ensureConnected();
			return new Promise((resolve, reject) => {
				const queueable = message;
				queueable._queuedResolve = resolve;
				queueable._queuedReject = reject;
				this.messageQueue.push(message);
			});
		}
		return new Promise((resolve, reject) => {
			this.doSendMessage(message, resolve, reject);
		});
	}
	async doSendMessage(message, resolve, reject) {
		if (message.type !== "AUTHENTICATE" && !CHANNEL_MESSAGE_TYPES.has(message.type) && this.getAuthToken && !this.isAuthenticated) try {
			await this.ensureAuthenticated();
		} catch (error) {
			reject(new RebaseApiError$1(error instanceof Error ? error.message : "Authentication required"));
			return;
		}
		const requestId = message.requestId || `req_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;
		message.requestId = requestId;
		const expectsResponse = !(message.type === "subscribe_collection" || message.type === "subscribe_one" || message.type === "unsubscribe" || CHANNEL_MESSAGE_TYPES.has(message.type));
		if (expectsResponse && !this.pendingRequests.has(requestId)) {
			const timeoutHandle = setTimeout(() => {
				if (this.pendingRequests.has(requestId)) {
					this.pendingRequests.delete(requestId);
					reject(new RebaseApiError$1("Request timed out"));
				}
			}, this.requestTimeoutMs);
			this.pendingRequests.set(requestId, {
				resolve: (value) => {
					clearTimeout(timeoutHandle);
					resolve(value);
				},
				reject: (error) => {
					clearTimeout(timeoutHandle);
					reject(error);
				},
				message
			});
		}
		try {
			this.ws.send(JSON.stringify(message));
			if (!expectsResponse) resolve(void 0);
		} catch (error) {
			if (expectsResponse) this.pendingRequests.delete(requestId);
			reject(new RebaseApiError$1("Failed to send message", { cause: error }));
		}
	}
	async fetchCollection(props) {
		return (await this.sendMessage({
			type: "FETCH_COLLECTION",
			payload: props
		})).rows || [];
	}
	async fetchOne(props) {
		return (await this.sendMessage({
			type: "FETCH_ONE",
			payload: props
		})).row ?? void 0;
	}
	async save(props) {
		return (await this.sendMessage({
			type: "SAVE",
			payload: props
		})).row;
	}
	async delete(props) {
		await this.sendMessage({
			type: "DELETE",
			payload: props
		});
	}
	async executeSql(sql, options) {
		return (await this.sendMessage({
			type: "EXECUTE_SQL",
			payload: {
				sql,
				options
			}
		})).result || [];
	}
	async fetchAvailableDatabases() {
		return (await this.sendMessage({
			type: "FETCH_DATABASES",
			payload: {}
		})).databases || [];
	}
	async fetchAvailableRoles() {
		return (await this.sendMessage({ type: "FETCH_ROLES" })).roles || [];
	}
	async fetchApplicationRoles() {
		return (await this.sendMessage({ type: "FETCH_APPLICATION_ROLES" })).roles || [];
	}
	async fetchCurrentDatabase() {
		return (await this.sendMessage({ type: "FETCH_CURRENT_DATABASE" })).database;
	}
	async checkUniqueField(path, name, value, id, collection) {
		return (await this.sendMessage({
			type: "CHECK_UNIQUE_FIELD",
			payload: {
				path,
				name,
				value,
				id,
				collection
			}
		})).isUnique;
	}
	async count(props) {
		return (await this.sendMessage({
			type: "COUNT",
			payload: props
		})).count;
	}
	async fetchUnmappedTables(mappedPaths) {
		return (await this.sendMessage({
			type: "FETCH_UNMAPPED_TABLES",
			payload: { mappedPaths }
		})).tables || [];
	}
	async fetchTableMetadata(tableName) {
		return (await this.sendMessage({
			type: "FETCH_TABLE_METADATA",
			payload: { tableName }
		})).metadata || {
			columns: [],
			foreignKeys: [],
			junctions: [],
			policies: []
		};
	}
	async createBranch(name, options) {
		return (await this.sendMessage({
			type: "CREATE_BRANCH",
			payload: {
				name,
				options
			}
		})).branch;
	}
	async deleteBranch(name) {
		await this.sendMessage({
			type: "DELETE_BRANCH",
			payload: { name }
		});
	}
	async listBranches() {
		return (await this.sendMessage({
			type: "LIST_BRANCHES",
			payload: {}
		})).branches || [];
	}
	/**
	* Recursively compare two values for structural equality.
	* Handles primitives, null, undefined, Date, RegExp, arrays, and plain objects.
	*/
	deepEqual(a, b) {
		if (a === b) return true;
		if (a === null || b === null || a === void 0 || b === void 0) return false;
		if (typeof a !== typeof b) return false;
		if (typeof a !== "object") return false;
		if (a instanceof Date && b instanceof Date) return a.getTime() === b.getTime();
		if (a instanceof Date || b instanceof Date) return false;
		if (a instanceof RegExp && b instanceof RegExp) return a.source === b.source && a.flags === b.flags;
		if (a instanceof RegExp || b instanceof RegExp) return false;
		const aIsArray = Array.isArray(a);
		const bIsArray = Array.isArray(b);
		if (aIsArray !== bIsArray) return false;
		if (aIsArray && bIsArray) {
			if (a.length !== b.length) return false;
			for (let i = 0; i < a.length; i++) if (!this.deepEqual(a[i], b[i])) return false;
			return true;
		}
		const aObj = a;
		const bObj = b;
		const aKeys = Object.keys(aObj);
		const bKeys = Object.keys(bObj);
		if (aKeys.length !== bKeys.length) return false;
		for (const key of aKeys) {
			if (!Object.prototype.hasOwnProperty.call(bObj, key)) return false;
			if (!this.deepEqual(aObj[key], bObj[key])) return false;
		}
		return true;
	}
	normalizeForComparison(val) {
		if (!val) return val;
		if (Array.isArray(val)) return val.map((item) => this.normalizeForComparison(item));
		if (typeof val === "object") {
			if (val instanceof Date) return val;
			if (val instanceof RegExp) return val;
			const obj = val;
			if (obj.__type === "relation") {
				const { data, ...rest } = obj;
				return rest;
			}
			const result = {};
			for (const [k, v] of Object.entries(obj)) result[k] = this.normalizeForComparison(v);
			return result;
		}
		return val;
	}
	/**
	* The address of a row, for matching it against another copy of itself.
	*
	* A row is exactly its columns and carries no address, so it is derived
	* from the key columns the server named — including the ordinary case where
	* that key is `id`, which the server reports like any other.
	*
	* Undefined when there are no keys, which means the server could not
	* resolve any: such rows genuinely cannot be recognised, and guessing at a
	* column called `id` would be inventing an identity for a table that has
	* none.
	*/
	rowAddress(row, pks) {
		if (!pks || pks.length === 0) return void 0;
		const address = buildCompositeId(row, pks);
		if (!address || address.split(COMPOSITE_ID_SEPARATOR).every((part) => part === "")) return void 0;
		return address;
	}
	/**
	* Merge incoming rows with cached data, preserving cached references
	* for rows whose values haven't changed. This avoids unnecessary
	* React re-renders when the server refetches all rows but most
	* haven't actually changed.
	*/
	mergeRows(cached, incoming, pks) {
		if (!cached || cached.length === 0) return incoming;
		const cachedById = /* @__PURE__ */ new Map();
		for (const row of cached) {
			const address = this.rowAddress(row, pks);
			if (address !== void 0) cachedById.set(address, row);
		}
		return incoming.map((incomingRow) => {
			const address = this.rowAddress(incomingRow, pks);
			const cachedRow = address === void 0 ? void 0 : cachedById.get(address);
			if (!cachedRow) return incomingRow;
			const normCached = this.normalizeForComparison(cachedRow);
			const normIncoming = this.normalizeForComparison(incomingRow);
			if (this.deepEqual(normCached, normIncoming)) return cachedRow;
			else {
				const mismatches = {};
				const allKeys = /* @__PURE__ */ new Set([...Object.keys(normCached), ...Object.keys(normIncoming)]);
				for (const key of allKeys) if (!this.deepEqual(normCached[key], normIncoming[key])) mismatches[key] = {
					cached: normCached[key],
					incoming: normIncoming[key]
				};
				console.debug(`[RebaseWS] Row ${address} refetch mismatch:\n`, JSON.stringify(mismatches, null, 2));
			}
			return incomingRow;
		});
	}
	listenCollection(props, onUpdate, onError) {
		this.ensureConnected();
		const subscriptionKey = this.createCollectionSubscriptionKey(props);
		const callbackId = `callback_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;
		const existingSubscription = this.collectionSubscriptions.get(subscriptionKey);
		if (existingSubscription) {
			const callbackMap = existingSubscription.callbacks;
			callbackMap.set(callbackId, {
				onUpdate,
				onError
			});
			if (existingSubscription.latestData !== void 0 && existingSubscription.isInitialDataReceived) try {
				onUpdate(existingSubscription.latestData, existingSubscription.latestMeta);
			} catch (error) {
				console.error("Error in collection subscription callback:", error);
				if (onError) onError(error instanceof Error ? error : new Error(String(error)));
			}
			else if (!existingSubscription.subscribeInFlight) this.sendCollectionSubscribe(subscriptionKey);
			return () => {
				callbackMap.delete(callbackId);
				if (callbackMap.size === 0) {
					if (this.collectionSubscriptions.get(subscriptionKey) !== existingSubscription) return;
					if (existingSubscription.subscribeTimeout) clearTimeout(existingSubscription.subscribeTimeout);
					this.collectionSubscriptions.delete(subscriptionKey);
					this.backendToCollectionKey.delete(existingSubscription.backendSubscriptionId);
					if (this.isConnected && this.ws) this.sendMessage({
						type: "unsubscribe",
						payload: { subscriptionId: existingSubscription.backendSubscriptionId }
					}).catch(console.error);
				}
			};
		}
		const backendSubscriptionId = `collection_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;
		const callbackMap = /* @__PURE__ */ new Map();
		callbackMap.set(callbackId, {
			onUpdate,
			onError
		});
		this.collectionSubscriptions.set(subscriptionKey, {
			backendSubscriptionId,
			callbacks: callbackMap,
			props
		});
		this.backendToCollectionKey.set(backendSubscriptionId, subscriptionKey);
		this.sendCollectionSubscribe(subscriptionKey);
		return () => {
			const subscription = this.collectionSubscriptions.get(subscriptionKey);
			if (subscription) {
				const callbacks = subscription.callbacks;
				callbacks.delete(callbackId);
				if (callbacks.size === 0) {
					if (subscription.subscribeTimeout) clearTimeout(subscription.subscribeTimeout);
					this.collectionSubscriptions.delete(subscriptionKey);
					this.backendToCollectionKey.delete(subscription.backendSubscriptionId);
					if (this.isConnected && this.ws) this.sendMessage({
						type: "unsubscribe",
						payload: { subscriptionId: subscription.backendSubscriptionId }
					}).catch(console.error);
				}
			}
		};
	}
	listenOne(props, onUpdate, onError) {
		this.ensureConnected();
		const subscriptionKey = this.createSingleSubscriptionKey(props);
		const callbackId = `callback_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;
		const existingSubscription = this.singleSubscriptions.get(subscriptionKey);
		if (existingSubscription) {
			const callbackMap = existingSubscription.callbacks;
			callbackMap.set(callbackId, {
				onUpdate,
				onError
			});
			if (existingSubscription.latestData !== void 0 && existingSubscription.isInitialDataReceived) try {
				onUpdate(existingSubscription.latestData);
			} catch (error) {
				console.error("Error in row subscription callback:", error);
				if (onError) onError(error instanceof Error ? error : new Error(String(error)));
			}
			else if (!existingSubscription.subscribeInFlight) this.sendEntitySubscribe(subscriptionKey);
			return () => {
				callbackMap.delete(callbackId);
				if (callbackMap.size === 0) {
					if (this.singleSubscriptions.get(subscriptionKey) !== existingSubscription) return;
					if (existingSubscription.subscribeTimeout) clearTimeout(existingSubscription.subscribeTimeout);
					this.singleSubscriptions.delete(subscriptionKey);
					this.backendToEntityKey.delete(existingSubscription.backendSubscriptionId);
					if (this.isConnected && this.ws) this.sendMessage({
						type: "unsubscribe",
						payload: { subscriptionId: existingSubscription.backendSubscriptionId }
					}).catch(console.error);
				}
			};
		}
		const backendSubscriptionId = `entity_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;
		const callbackMap = /* @__PURE__ */ new Map();
		callbackMap.set(callbackId, {
			onUpdate,
			onError
		});
		this.singleSubscriptions.set(subscriptionKey, {
			backendSubscriptionId,
			callbacks: callbackMap,
			props
		});
		this.backendToEntityKey.set(backendSubscriptionId, subscriptionKey);
		this.sendEntitySubscribe(subscriptionKey);
		return () => {
			const subscription = this.singleSubscriptions.get(subscriptionKey);
			if (subscription) {
				const callbacks = subscription.callbacks;
				callbacks.delete(callbackId);
				if (callbacks.size === 0) {
					this.singleSubscriptions.delete(subscriptionKey);
					this.backendToEntityKey.delete(subscription.backendSubscriptionId);
					if (this.isConnected && this.ws) this.sendMessage({
						type: "unsubscribe",
						payload: { subscriptionId: subscription.backendSubscriptionId }
					}).catch(console.error);
				}
			}
		};
	}
	/**
	* Send a `subscribe_collection` for an already-registered subscription and
	* arm its watchdog.
	*
	* Every path that registers a collection subscription goes through here, so
	* that a subscribe which never lands — a rejected send, or a server that
	* never answers — always ends up in `failCollectionSubscription` rather than
	* leaving the entry parked with `isInitialDataReceived === false` forever.
	*/
	sendCollectionSubscribe(subscriptionKey) {
		const subscription = this.collectionSubscriptions.get(subscriptionKey);
		if (!subscription) return;
		const backendSubscriptionId = subscription.backendSubscriptionId;
		subscription.subscribeInFlight = true;
		if (subscription.subscribeTimeout) clearTimeout(subscription.subscribeTimeout);
		subscription.subscribeTimeout = void 0;
		if (this.isConnected) this.sendCollectionSubscribeWatchdog(subscriptionKey);
		this.sendMessage({
			type: "subscribe_collection",
			payload: {
				...subscription.props,
				subscriptionId: backendSubscriptionId
			}
		}).catch((error) => {
			const current = this.collectionSubscriptions.get(subscriptionKey);
			if (!current || current.backendSubscriptionId !== backendSubscriptionId) return;
			this.failCollectionSubscription(subscriptionKey, error instanceof Error ? error : new Error(String(error)));
		});
	}
	/** The `listenOne` counterpart of {@link sendCollectionSubscribe}. */
	sendEntitySubscribe(subscriptionKey) {
		const subscription = this.singleSubscriptions.get(subscriptionKey);
		if (!subscription) return;
		const backendSubscriptionId = subscription.backendSubscriptionId;
		subscription.subscribeInFlight = true;
		if (subscription.subscribeTimeout) clearTimeout(subscription.subscribeTimeout);
		subscription.subscribeTimeout = void 0;
		if (this.isConnected) this.sendEntitySubscribeWatchdog(subscriptionKey);
		this.sendMessage({
			type: "subscribe_one",
			payload: {
				...subscription.props,
				subscriptionId: backendSubscriptionId
			}
		}).catch((error) => {
			const current = this.singleSubscriptions.get(subscriptionKey);
			if (!current || current.backendSubscriptionId !== backendSubscriptionId) return;
			this.failEntitySubscription(subscriptionKey, error instanceof Error ? error : new Error(String(error)));
		});
	}
	/**
	* Report a subscribe failure to every listener and drop the registration.
	*
	* Dropping it is the point: the callbacks stay live (their components are
	* still mounted and have been told), but the next `listenCollection` for
	* these params finds no entry and issues a fresh subscribe instead of
	* silently attaching to a dead one.
	*/
	failCollectionSubscription(subscriptionKey, error) {
		const subscription = this.collectionSubscriptions.get(subscriptionKey);
		if (!subscription) return;
		if (subscription.subscribeTimeout) clearTimeout(subscription.subscribeTimeout);
		subscription.subscribeInFlight = false;
		this.collectionSubscriptions.delete(subscriptionKey);
		this.backendToCollectionKey.delete(subscription.backendSubscriptionId);
		subscription.callbacks.forEach((callback) => {
			if (callback.onError) try {
				callback.onError(error);
			} catch (callbackError) {
				console.error("Error in collection subscription error callback:", callbackError);
			}
		});
	}
	/** The `listenOne` counterpart of {@link failCollectionSubscription}. */
	failEntitySubscription(subscriptionKey, error) {
		const subscription = this.singleSubscriptions.get(subscriptionKey);
		if (!subscription) return;
		if (subscription.subscribeTimeout) clearTimeout(subscription.subscribeTimeout);
		subscription.subscribeInFlight = false;
		this.singleSubscriptions.delete(subscriptionKey);
		this.backendToEntityKey.delete(subscription.backendSubscriptionId);
		subscription.callbacks.forEach((callback) => {
			if (callback.onError) try {
				callback.onError(error);
			} catch (callbackError) {
				console.error("Error in row subscription error callback:", callbackError);
			}
		});
	}
	/**
	* Stop the watchdogs without failing anything — used when the socket drops,
	* since the reconnect path re-subscribes everything anyway and a watchdog
	* firing mid-reconnect would tear down healthy subscriptions.
	*/
	suspendSubscribeWatchdogs() {
		for (const sub of this.collectionSubscriptions.values()) {
			if (sub.subscribeTimeout) clearTimeout(sub.subscribeTimeout);
			sub.subscribeTimeout = void 0;
			sub.subscribeInFlight = false;
		}
		for (const sub of this.singleSubscriptions.values()) {
			if (sub.subscribeTimeout) clearTimeout(sub.subscribeTimeout);
			sub.subscribeTimeout = void 0;
			sub.subscribeInFlight = false;
		}
	}
	/**
	* Arm watchdogs for subscribes that were requested while offline and have
	* just been flushed to the socket. Their timers were deliberately not set at
	* request time, so without this they would have no timeout at all.
	*/
	armPendingSubscribeWatchdogs() {
		for (const [key, sub] of this.collectionSubscriptions.entries()) if (sub.subscribeInFlight && !sub.subscribeTimeout) this.sendCollectionSubscribeWatchdog(key);
		for (const [key, sub] of this.singleSubscriptions.entries()) if (sub.subscribeInFlight && !sub.subscribeTimeout) this.sendEntitySubscribeWatchdog(key);
	}
	sendCollectionSubscribeWatchdog(subscriptionKey) {
		const subscription = this.collectionSubscriptions.get(subscriptionKey);
		if (!subscription) return;
		const backendSubscriptionId = subscription.backendSubscriptionId;
		subscription.subscribeTimeout = setTimeout(() => {
			const current = this.collectionSubscriptions.get(subscriptionKey);
			if (!current || current.backendSubscriptionId !== backendSubscriptionId) return;
			if (!current.subscribeInFlight) return;
			this.failCollectionSubscription(subscriptionKey, new RebaseApiError$1("Subscription timed out", { code: "SUBSCRIPTION_TIMEOUT" }));
		}, this.subscriptionTimeoutMs);
	}
	sendEntitySubscribeWatchdog(subscriptionKey) {
		const subscription = this.singleSubscriptions.get(subscriptionKey);
		if (!subscription) return;
		const backendSubscriptionId = subscription.backendSubscriptionId;
		subscription.subscribeTimeout = setTimeout(() => {
			const current = this.singleSubscriptions.get(subscriptionKey);
			if (!current || current.backendSubscriptionId !== backendSubscriptionId) return;
			if (!current.subscribeInFlight) return;
			this.failEntitySubscription(subscriptionKey, new RebaseApiError$1("Subscription timed out", { code: "SUBSCRIPTION_TIMEOUT" }));
		}, this.subscriptionTimeoutMs);
	}
	/**
	* Fail every subscription that never received data. Called when reconnection
	* is given up on, so views surface an error instead of spinning forever.
	*/
	failAllPendingSubscriptions(error) {
		for (const key of [...this.collectionSubscriptions.keys()]) {
			const sub = this.collectionSubscriptions.get(key);
			if (sub && !sub.isInitialDataReceived) this.failCollectionSubscription(key, error);
		}
		for (const key of [...this.singleSubscriptions.keys()]) {
			const sub = this.singleSubscriptions.get(key);
			if (sub && !sub.isInitialDataReceived) this.failEntitySubscription(key, error);
		}
	}
	/**
	* Re-send all active subscriptions to the backend after a reconnect.
	* The server wipes subscription state when a client disconnects, so
	* we need to re-register everything to resume receiving updates.
	*/
	resubscribeAll() {
		console.debug(`[WS] Re-subscribing: ${this.collectionSubscriptions.size} collection(s), ${this.singleSubscriptions.size} row(ies)`);
		for (const [key, sub] of this.collectionSubscriptions.entries()) {
			const oldBackendId = sub.backendSubscriptionId;
			const newBackendId = `collection_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;
			sub.backendSubscriptionId = newBackendId;
			this.backendToCollectionKey.delete(oldBackendId);
			this.backendToCollectionKey.set(newBackendId, key);
			this.sendCollectionSubscribe(key);
		}
		for (const [key, sub] of this.singleSubscriptions.entries()) {
			const oldBackendId = sub.backendSubscriptionId;
			const newBackendId = `entity_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;
			sub.backendSubscriptionId = newBackendId;
			this.backendToEntityKey.delete(oldBackendId);
			this.backendToEntityKey.set(newBackendId, key);
			this.sendEntitySubscribe(key);
		}
	}
	createCollectionSubscriptionKey(props) {
		const { collection, ...query } = props;
		const key = {
			...query,
			collection: collection?.name
		};
		return JSON.stringify(key, (_, value) => {
			if (value && typeof value === "object" && !Array.isArray(value)) return Object.keys(value).sort().reduce((sorted, k) => {
				sorted[k] = value[k];
				return sorted;
			}, {});
			return value;
		});
	}
	createSingleSubscriptionKey(props) {
		return `${props.path}|${props.id}`;
	}
};
//#endregion
//#region src/realtime-channel.ts
/**
* Re-send presence comfortably inside the server's 30s expiry.
*
* Two-thirds of the window: one lost heartbeat still leaves time for the next
* before the entry is reaped, so a single dropped frame is not a disappearance.
*/
var PRESENCE_HEARTBEAT_MS = 2e4;
/**
* How long live messages are held back waiting for a catch-up response.
*
* Short, because the cost of waiting is visible — on a collaborative document
* this is a stall in everyone else's edits appearing. Long enough that a slow
* replay of a busy channel is not abandoned needlessly.
*/
var CATCH_UP_TIMEOUT_MS = 1e4;
var RebaseRealtimeChannel = class {
	name;
	transport;
	presenceHandlers = /* @__PURE__ */ new Set();
	broadcastHandlers = /* @__PURE__ */ new Set();
	errorHandlers = /* @__PURE__ */ new Set();
	unsubscribers = [];
	/** Last known roster, kept so handlers always get a full picture. */
	presences = {};
	/** What this client last tracked, replayed on reconnect and heartbeat. */
	trackedState = null;
	heartbeat = null;
	joined = false;
	/** Whether this handle asks the server to replay missed messages. */
	wantsHistory;
	/**
	* Highest sequence number delivered to handlers so far.
	*
	* This is the resume point sent as `sinceSeq`, and the watermark that makes
	* replay idempotent: catch-up ranges overlap with what arrived live, and
	* anything at or below this has already been seen.
	*/
	lastSeq = 0;
	/**
	* Live messages that arrived while a catch-up was in flight.
	*
	* Without this they would be delivered ahead of the older messages being
	* fetched, and — worse — would advance {@link lastSeq} past them, so the
	* catch-up response would then be discarded as already-seen and those
	* messages would be lost for good. Held here and flushed, in order, once
	* the replay lands.
	*/
	pendingLive = [];
	catchUpInFlight = false;
	/**
	* Deadline for a catch-up response.
	*
	* Buffering live messages is only safe because the wait is bounded. A
	* catch-up frame that never arrives — a server that dropped it, a socket
	* that died between request and reply — would otherwise leave the channel
	* silently holding every subsequent edit forever, which is a worse failure
	* than the one replay was added to fix.
	*/
	catchUpTimeout = null;
	/**
	* Callers of {@link history} awaiting the next `channel_history` frame.
	*
	* These frames are addressed by channel rather than by request id, so they
	* are matched in arrival order. Requests on one channel are serialized by
	* the socket, so FIFO is the right correlation here.
	*/
	historyWaiters = [];
	constructor(name, transport, options = {}) {
		this.name = name;
		this.transport = transport;
		this.wantsHistory = options.history ?? false;
	}
	/**
	* Turn on catch-up for a handle that was created without it.
	*
	* The client hands back the same channel object for a given name, so a
	* later `channel(name, { history: true })` has no new object to configure —
	* it upgrades this one instead. Idempotent, and never downgrades: one
	* caller asking for history must not be switched off by another that did
	* not ask.
	*/
	enableHistory() {
		if (this.wantsHistory) return;
		this.wantsHistory = true;
		if (this.joined) this.requestHistory();
	}
	/**
	* Join the channel and ask for the current roster.
	*
	* Called automatically by `track`, `broadcast`, `onPresence` and
	* `onBroadcast`; calling it directly is only needed to start receiving
	* before there is anything to send.
	*/
	/**
	* Send a channel message.
	*
	* Every channel message is read by the server out of a `payload` envelope
	* (`payload?.channel`, `payload?.state`, `payload?.event`). Sending those
	* fields flat does not error: `payload?.channel` simply reads as
	* `undefined`, so the client is registered into channel `undefined` with
	* empty state, and the echo comes back with no `channel` for
	* `onChannelMessage` to match — presence and broadcast both go quiet with
	* nothing logged. Funnelled through one place so a new message type cannot
	* reintroduce that.
	*/
	send(type, fields = {}) {
		return this.transport.sendMessage({
			type,
			payload: {
				channel: this.name,
				...fields
			}
		});
	}
	async join() {
		if (this.joined) return;
		this.joined = true;
		this.unsubscribers.push(this.transport.onChannelMessage(this.name, (message) => this.handle(message)));
		this.unsubscribers.push(this.transport.onReconnect(() => {
			this.rejoin();
		}));
		await this.send("join_channel");
		await this.send("presence_state");
		if (this.wantsHistory) await this.requestHistory();
	}
	async rejoin() {
		try {
			await this.send("join_channel");
			await this.send("presence_state");
			if (this.trackedState) await this.send("presence_track", { state: this.trackedState });
			if (this.wantsHistory) await this.requestHistory();
		} catch {}
	}
	/**
	* Ask the server for everything after {@link lastSeq}.
	*
	* Live messages are buffered from here until the answer arrives — see
	* {@link pendingLive}.
	*/
	async requestHistory(limit) {
		this.catchUpInFlight = true;
		if (this.catchUpTimeout) clearTimeout(this.catchUpTimeout);
		this.catchUpTimeout = setTimeout(() => this.abandonCatchUp(), CATCH_UP_TIMEOUT_MS);
		unref(this.catchUpTimeout);
		try {
			await this.send("channel_history", {
				sinceSeq: this.lastSeq,
				...limit !== void 0 ? { limit } : {}
			});
		} catch {
			this.abandonCatchUp();
		}
	}
	/**
	* Give up waiting for a catch-up and release what was held back.
	*
	* The buffered messages are still the freshest thing this client has, so
	* they are delivered rather than dropped. Callers of {@link history} are
	* answered with `retained: false` — accurate in the sense that matters:
	* this client has no history to work from and has to resync.
	*/
	abandonCatchUp() {
		if (this.catchUpTimeout) {
			clearTimeout(this.catchUpTimeout);
			this.catchUpTimeout = null;
		}
		if (!this.catchUpInFlight) return;
		this.catchUpInFlight = false;
		for (const resolve of this.historyWaiters.splice(0)) resolve({
			messages: [],
			retained: false
		});
		this.flushPendingLive();
	}
	/**
	* Publish this client's presence state, and keep publishing it.
	*
	* Calling `track` again replaces the state (and restarts the heartbeat),
	* which is how you update e.g. a cursor position.
	*/
	async track(state) {
		await this.join();
		this.trackedState = state;
		await this.send("presence_track", { state });
		if (!this.heartbeat) {
			this.heartbeat = setInterval(() => {
				if (!this.trackedState) return;
				this.send("presence_track", { state: this.trackedState }).catch(() => {});
			}, PRESENCE_HEARTBEAT_MS);
			unref(this.heartbeat);
		}
	}
	/** Stop publishing presence, without leaving the channel. */
	async untrack() {
		this.stopHeartbeat();
		this.trackedState = null;
		if (this.joined) await this.send("presence_untrack");
	}
	/**
	* Observe the roster. The handler fires immediately with what is already
	* known, then on every change.
	*/
	onPresence(handler) {
		this.presenceHandlers.add(handler);
		this.join();
		if (Object.keys(this.presences).length > 0) handler({ ...this.presences });
		return () => this.presenceHandlers.delete(handler);
	}
	/**
	* Send a broadcast. The sender does not receive its own message.
	*
	* Resolves when the frame is written to the socket, **not** when the server
	* has accepted it. Channel frames are fire-and-forget by design — a
	* collaborative app broadcasts a cursor position sixty times a second, and
	* waiting for an acknowledgement on each would make that a round trip.
	*
	* A refusal therefore arrives on {@link onError}, not as a rejection here.
	*/
	async broadcast(event, payload) {
		await this.join();
		await this.send("broadcast", {
			event,
			payload
		});
	}
	/**
	* Observe refusals about this channel.
	*
	* `CHANNEL_FORBIDDEN` when an authorizer refuses a join, a broadcast or a
	* history read; `RATE_LIMITED` past the channel frame budget;
	* `CHANNEL_HISTORY_WRITE_FAILED` / `CHANNEL_HISTORY_READ_FAILED` when
	* retention cannot be served; `CHANNEL_BUS_PAYLOAD_TOO_LARGE` when a
	* broadcast on an ephemeral channel is too big to cross the bus.
	*
	* These used to be dropped on the floor — there is no promise to reject on
	* a fire-and-forget frame, so a forbidden broadcast looked exactly like a
	* delivered one. With no handler attached they are logged as a warning,
	* because an unobserved refusal is the failure this exists for.
	*
	* @returns An unsubscribe function.
	*/
	onError(handler) {
		this.errorHandlers.add(handler);
		this.join();
		return () => this.errorHandlers.delete(handler);
	}
	onBroadcast(eventOrHandler, maybeHandler) {
		const wrapped = typeof eventOrHandler === "string" ? (e) => {
			if (e.event === eventOrHandler) maybeHandler(e.payload);
		} : eventOrHandler;
		this.broadcastHandlers.add(wrapped);
		this.join();
		return () => this.broadcastHandlers.delete(wrapped);
	}
	/**
	* The last sequence number this channel has delivered.
	*
	* Zero on a channel that retains nothing. Persist it if you want catch-up
	* to survive a page reload as well as a reconnect, and pass it back via
	* {@link history}.
	*/
	get sequence() {
		return this.lastSeq;
	}
	/**
	* Fetch retained messages explicitly, instead of waiting for join or
	* reconnect to do it.
	*
	* Defaults to resuming from {@link sequence}. Messages are delivered to
	* `onBroadcast` handlers as usual — the returned value is for callers that
	* want to inspect the batch, or to learn from `retained` that the channel
	* keeps no history at all.
	*/
	async history(options = {}) {
		await this.join();
		if (options.sinceSeq !== void 0) this.lastSeq = options.sinceSeq;
		const result = new Promise((resolve) => {
			this.historyWaiters.push(resolve);
		});
		await this.requestHistory(options.limit);
		return result;
	}
	/** Leave the channel and release every listener and timer. */
	async leave() {
		this.stopHeartbeat();
		this.trackedState = null;
		this.presences = {};
		this.presenceHandlers.clear();
		this.broadcastHandlers.clear();
		this.lastSeq = 0;
		this.pendingLive = [];
		this.catchUpInFlight = false;
		if (this.catchUpTimeout) {
			clearTimeout(this.catchUpTimeout);
			this.catchUpTimeout = null;
		}
		for (const resolve of this.historyWaiters.splice(0)) resolve({
			messages: [],
			retained: false
		});
		for (const off of this.unsubscribers) off();
		this.unsubscribers = [];
		if (this.joined) {
			this.joined = false;
			await this.send("leave_channel");
		}
	}
	stopHeartbeat() {
		if (this.heartbeat) {
			clearInterval(this.heartbeat);
			this.heartbeat = null;
		}
	}
	/** Fold an incoming frame into the roster and fan it out. */
	handle(message) {
		switch (message.type) {
			case "presence_state":
				this.presences = message.presences;
				this.emitPresence();
				break;
			case "presence_diff": {
				const { joins, leaves } = message;
				for (const [id, state] of Object.entries(joins)) this.presences[id] = state;
				for (const id of Object.keys(leaves)) delete this.presences[id];
				this.emitPresence({
					joins,
					leaves
				});
				break;
			}
			case "broadcast": {
				const seq = message.seq;
				const event = {
					event: message.event,
					payload: message.payload,
					...seq !== void 0 ? { seq } : {}
				};
				if (seq === void 0) {
					this.deliver(event);
					break;
				}
				if (this.catchUpInFlight) {
					this.pendingLive.push(event);
					break;
				}
				if (seq <= this.lastSeq) break;
				this.lastSeq = seq;
				this.deliver(event);
				break;
			}
			case "channel_history": {
				this.catchUpInFlight = false;
				if (this.catchUpTimeout) {
					clearTimeout(this.catchUpTimeout);
					this.catchUpTimeout = null;
				}
				const { messages: entries, retained, latestSeq } = message;
				for (const resolve of this.historyWaiters.splice(0)) resolve({
					messages: entries,
					retained,
					latestSeq
				});
				for (const entry of entries) {
					if (entry.seq <= this.lastSeq) continue;
					this.lastSeq = entry.seq;
					this.deliver({
						event: entry.event,
						payload: entry.payload,
						seq: entry.seq,
						replayed: true
					});
				}
				this.flushPendingLive();
				break;
			}
			case "ERROR":
			case "error": {
				const raw = message.payload?.error;
				const asObject = typeof raw === "object" ? raw : void 0;
				const text = asObject?.message ?? (typeof raw === "string" ? raw : void 0) ?? message.error ?? "The server refused a channel operation.";
				const error = new RebaseApiError$1(text, { ...asObject?.code ? { code: asObject.code } : {} });
				if (this.errorHandlers.size === 0) {
					console.warn(`[Rebase] Channel "${this.name}" error${asObject?.code ? ` (${asObject.code})` : ""}: ${text}. Attach \`channel.onError(...)\` to handle it.`);
					break;
				}
				for (const handler of [...this.errorHandlers]) try {
					handler(error);
				} catch (e) {
					console.error("Error in channel error handler:", e);
				}
				break;
			}
		}
	}
	/** Deliver everything held back during a catch-up, in sequence order. */
	flushPendingLive() {
		if (this.pendingLive.length === 0) return;
		const buffered = this.pendingLive.sort((a, b) => (a.seq ?? 0) - (b.seq ?? 0));
		this.pendingLive = [];
		for (const event of buffered) {
			const seq = event.seq;
			if (seq !== void 0) {
				if (seq <= this.lastSeq) continue;
				this.lastSeq = seq;
			}
			this.deliver(event);
		}
	}
	deliver(event) {
		for (const handler of [...this.broadcastHandlers]) handler(event);
	}
	emitPresence(diff) {
		const snapshot = { ...this.presences };
		for (const handler of this.presenceHandlers) handler(snapshot, diff);
	}
};
//#endregion
//#region src/offline-codec.ts
/**
* Lossless round-tripping of rows through the offline store.
*
* Both persistence backends move values by structured clone, which keeps
* `Date` but flattens every class instance to a plain object. For
* `EntityReference`/`EntityRelation` that is harmless — they carry their own
* `__type` discriminator, so the JSON reviver can rebuild them — but
* `GeoPoint` and `Vector` do not, and would come back out of the cache as
* anonymous `{ latitude, longitude }` / `{ value }` bags. A row read from the
* cache must be indistinguishable from the same row read from the network, so
* those two are tagged on the way in and revived on the way out.
*
* Type tests here are structural rather than `instanceof`, because a structured
* clone can arrive from another realm — an iframe, a worker, or the polyfill
* the tests run against — where the constructor identity differs but the value
* is the real thing. Only *plain* objects are walked; anything else is passed
* through whole, so a class instance is never quietly reduced to `{}`.
*/
function isDate(value) {
	return Object.prototype.toString.call(value) === "[object Date]";
}
/** An object literal — not a Date, RegExp, Map, or any class instance. */
function isPlainObject(value) {
	if (value === null || typeof value !== "object" || Array.isArray(value)) return false;
	const proto = Object.getPrototypeOf(value);
	if (proto === null || proto === Object.prototype) return true;
	return proto.constructor?.name === "Object";
}
function dehydrateValue(value) {
	if (value === null || value === void 0) return value;
	if (value instanceof GeoPoint) return {
		__type: "GeoPoint",
		latitude: value.latitude,
		longitude: value.longitude
	};
	if (value instanceof Vector) return {
		__type: "Vector",
		value: [...value.value]
	};
	if (value instanceof EntityReference || value instanceof EntityRelation) return value;
	if (Array.isArray(value)) return value.map(dehydrateValue);
	if (isPlainObject(value)) {
		const out = {};
		for (const [key, inner] of Object.entries(value)) out[key] = dehydrateValue(inner);
		return out;
	}
	return value;
}
function hydrateValue(value) {
	if (value === null || value === void 0 || isDate(value)) return value;
	if (Array.isArray(value)) return value.map(hydrateValue);
	if (typeof value === "object") {
		const revived = rebaseReviver("", value);
		if (revived !== value) return revived;
		if (!isPlainObject(value)) return value;
		const out = {};
		for (const [key, inner] of Object.entries(value)) out[key] = hydrateValue(inner);
		return out;
	}
	return value;
}
/** Prepare a row for the store. */
function dehydrateRow(row) {
	return dehydrateValue(row);
}
/** Restore a row read back from the store. */
function hydrateRow(row) {
	return hydrateValue(row);
}
//#endregion
//#region src/offline-connectivity.ts
/**
* Whether the network is worth trying, and when to try again after it wasn't.
*
* `navigator.onLine` is necessary but not sufficient: it reports the state of
* the network interface, so it stays `true` behind a captive portal, on a
* connection that resolves DNS but reaches nothing, and while the API itself
* is down. This tracks what actually happened to requests as well, so the
* first failure is the only one an app pays for — everything after it inside
* the backoff window skips the doomed round trip and answers from the local
* store immediately, which is the difference between an app that freezes when
* the wifi drops and one that does not.
*/
/**
* The request never reached the server, so nothing was decided by it.
*
* One question, one answer: `status === 0` is how a transport spells "there
* was no response at all", and the transport now wraps every `fetch` rejection
* into a `RebaseApiError` carrying it. This used to also sniff for the shapes
* a raw `fetch` rejects with — a `TypeError` in a browser, an `AbortError`, a
* `TimeoutError` — which is a list that has to be kept in step with three
* runtimes and was wrong in both directions: it missed anything they add, and
* it claimed a `TypeError` thrown by application code inside a callback was a
* network failure, which quietly served a cached answer in its place.
*/
function isNetworkError(error) {
	return error instanceof RebaseApiError && error.status === 0;
}
/**
* Statuses that mean "not now" rather than "not ever": a queued write that
* gets one of these is worth replaying, while a 400 or a 403 never will be.
* 500 is deliberately absent — an unhandled server error is far more often a
* bug the same payload will hit again than a blip, and retrying it forever
* jams every write behind it.
*/
var RETRYABLE_STATUSES = /* @__PURE__ */ new Set([
	408,
	425,
	429,
	502,
	503,
	504
]);
/**
* The server holds this key for a request it has not answered yet.
*
* It is a 409 like a duplicate row is a 409, and nothing but the code separates
* them — one means "your write is already there", the other means "your write
* may not have happened at all, ask again".
*/
var IDEMPOTENCY_IN_PROGRESS = "IDEMPOTENCY_KEY_IN_PROGRESS";
/**
* Is the server still answering an earlier attempt of this same write?
*
* The only correct response is to ask again — which is exactly what the
* server's own message says, and exactly what this SDK used not to do.
*/
function isIdempotencyInProgressError(error) {
	return error instanceof RebaseApiError && error.status === 409 && error.code === IDEMPOTENCY_IN_PROGRESS;
}
/** Is this failure worth another attempt later? */
function isRetryableError(error) {
	if (isNetworkError(error)) return true;
	if (!(error instanceof RebaseApiError)) return false;
	if (isIdempotencyInProgressError(error)) return true;
	return error.status !== void 0 && RETRYABLE_STATUSES.has(error.status);
}
/**
* Did this write fail because the row is already there?
*
* Matched on the SQLSTATE the server passes through (`23505`, unique_violation)
* and on 409, never on the message — a duplicate-key message names the
* constraint and the values, so it is neither stable nor safe to parse.
*
* The queue uses this to recognise its own earlier attempt. A create whose
* response was lost is replayed, and for a row carrying an id the SDK generated
* the server can only be rejecting it because the first attempt actually landed.
*
* Which is why the status alone cannot decide it: `IDEMPOTENCY_KEY_IN_PROGRESS`
* is a 409 that means the opposite — the row may not exist at all. Read as a
* duplicate, the queue looked for a row that was never written, found nothing,
* concluded there was nothing left to do and deleted the write from the queue.
*/
function isDuplicateKeyError(error) {
	if (!(error instanceof RebaseApiError)) return false;
	if (error.code === "23505") return true;
	return error.status === 409 && !isIdempotencyInProgressError(error);
}
var ConnectivityMonitor = class {
	state = "online";
	backoffMs;
	initialBackoffMs;
	maxBackoffMs;
	retryAt = 0;
	timer;
	listeners = /* @__PURE__ */ new Set();
	respectBackoff;
	now;
	setTimer;
	clearTimer;
	/** Called when the backoff window expires, to drive an automatic retry. */
	onRetryDue;
	handleOnline = () => {
		this.retryAt = 0;
		this.backoffMs = this.initialBackoffMs;
		this.clearPendingTimer();
		this.setState("online");
		this.onRetryDue?.();
	};
	handleOffline = () => {
		this.setState("offline");
	};
	constructor(options = {}) {
		this.initialBackoffMs = options.initialBackoffMs ?? 1e3;
		this.maxBackoffMs = Math.max(this.initialBackoffMs, options.maxBackoffMs ?? 6e4);
		this.backoffMs = this.initialBackoffMs;
		this.respectBackoff = options.respectBackoff ?? true;
		this.now = options.now ?? (() => Date.now());
		this.setTimer = options.setTimer ?? ((fn, ms) => setTimeout(fn, ms));
		this.clearTimer = options.clearTimer ?? ((handle) => clearTimeout(handle));
		if (typeof window !== "undefined" && typeof window.addEventListener === "function") {
			window.addEventListener("online", this.handleOnline);
			window.addEventListener("offline", this.handleOffline);
		}
		if (typeof navigator !== "undefined" && navigator.onLine === false) this.state = "offline";
	}
	/** What the app should be told: are we connected? */
	isOnline() {
		if (typeof navigator !== "undefined" && navigator.onLine === false) return false;
		return this.state === "online";
	}
	/**
	* Should this request even be sent? False means "answer from the local
	* store instead" — the request would only burn a timeout to reach the same
	* conclusion the last one already did.
	*/
	shouldAttempt() {
		if (typeof navigator !== "undefined" && navigator.onLine === false) return false;
		if (this.state === "online" || !this.respectBackoff) return true;
		return this.now() >= this.retryAt;
	}
	/** A request reached the server. */
	markSuccess() {
		this.backoffMs = this.initialBackoffMs;
		this.retryAt = 0;
		this.clearPendingTimer();
		this.setState("online");
	}
	/** A request did not reach the server: we are offline until proven otherwise. */
	markFailure() {
		this.deferRetry();
		this.setState("offline");
	}
	/**
	* Back off and try again later without claiming the connection is gone.
	* This is what a 429 or a 503 deserves — the server answered, so the app
	* is demonstrably online; it just should not hammer.
	*/
	deferRetry() {
		const jitter = .8 + Math.random() * .4;
		this.retryAt = this.now() + this.backoffMs * jitter;
		const delay = Math.max(0, this.retryAt - this.now());
		this.backoffMs = Math.min(this.maxBackoffMs, this.backoffMs * 2);
		this.scheduleRetry(delay);
	}
	/** Milliseconds until the next attempt is allowed; 0 when one is allowed now. */
	msUntilRetry() {
		if (this.state === "online") return 0;
		return Math.max(0, this.retryAt - this.now());
	}
	onChange(listener) {
		this.listeners.add(listener);
		return () => this.listeners.delete(listener);
	}
	dispose() {
		if (typeof window !== "undefined" && typeof window.removeEventListener === "function") {
			window.removeEventListener("online", this.handleOnline);
			window.removeEventListener("offline", this.handleOffline);
		}
		this.clearPendingTimer();
		this.listeners.clear();
		this.onRetryDue = void 0;
	}
	scheduleRetry(delay) {
		this.clearPendingTimer();
		if (!this.onRetryDue) return;
		this.timer = this.setTimer(() => {
			this.timer = void 0;
			this.onRetryDue?.();
		}, delay);
		unref(this.timer);
	}
	clearPendingTimer() {
		if (this.timer !== void 0) {
			this.clearTimer(this.timer);
			this.timer = void 0;
		}
	}
	setState(next) {
		if (this.state === next) return;
		this.state = next;
		const online = this.isOnline();
		for (const listener of this.listeners) listener(online);
	}
};
//#endregion
//#region src/offline-store.ts
/**
* Monotonic within a tab, unique across tabs, and sortable as a plain string:
* `<ms base36, padded>-<counter>-<random>`. The padding is what keeps
* lexicographic order equal to chronological order, and the random suffix is
* what stops two tabs from writing the same queue key in the same millisecond
* — which would silently drop one of the two writes.
*/
var mutationCounter = 0;
function createMutationId(now = Date.now()) {
	return `${now.toString(36).padStart(10, "0")}-${(mutationCounter = (mutationCounter + 1) % 1679616).toString(36).padStart(4, "0")}-${Math.random().toString(36).slice(2, 10).padStart(8, "0")}`;
}
/**
* In-memory store: the default outside the browser and the workhorse of the
* test suite. Values are deep-copied on the way in and out so a caller
* mutating a returned row cannot silently edit the "persisted" copy — the
* IndexedDB implementation gets the same guarantee for free from structured
* cloning, and the two must not differ in aliasing behaviour.
*/
var MemoryOfflineStore = class {
	cache = /* @__PURE__ */ new Map();
	queue = /* @__PURE__ */ new Map();
	async getCache(key) {
		const entry = this.cache.get(key);
		return entry ? structuredClone(entry) : void 0;
	}
	async setCache(key, entry) {
		this.cache.set(key, structuredClone(entry));
	}
	async setCacheMany(entries) {
		for (const { key, entry } of entries) this.cache.set(key, structuredClone(entry));
	}
	async deleteCache(keys) {
		for (const key of keys) this.cache.delete(key);
	}
	async listCache(prefix) {
		const out = [];
		for (const [key, entry] of this.cache) if (key.startsWith(prefix)) out.push({
			key,
			cachedAt: entry.cachedAt
		});
		return out;
	}
	async listCacheEntries(prefix) {
		const out = [];
		for (const [key, entry] of this.cache) if (key.startsWith(prefix)) out.push({
			key,
			...structuredClone(entry)
		});
		out.sort((a, b) => a.key < b.key ? -1 : a.key > b.key ? 1 : 0);
		return out;
	}
	async enqueue(key, mutation) {
		this.queue.set(key, structuredClone(mutation));
	}
	async dequeue(key) {
		this.queue.delete(key);
	}
	async listQueue(prefix) {
		return [...this.queue.entries()].filter(([key]) => key.startsWith(prefix)).sort(([a], [b]) => a < b ? -1 : a > b ? 1 : 0).map(([, mutation]) => structuredClone(mutation));
	}
	async clear(prefix) {
		for (const key of [...this.cache.keys()]) if (key.startsWith(prefix)) this.cache.delete(key);
		for (const key of [...this.queue.keys()]) if (key.startsWith(prefix)) this.queue.delete(key);
	}
};
var IDB_NAME = "rebase-offline";
/**
* v2 introduced the normalized row cache and string mutation ids. A v1
* database holds whole-response blobs under keys this version cannot read and
* queue entries ordered by a numeric `seq` this version no longer writes, so
* the upgrade drops both stores rather than trying to translate them. Offline
* support had not shipped in a release when v2 landed, so nothing in the wild
* loses a queued write to this.
*/
var IDB_VERSION = 2;
var CACHE_STORE = "cache";
var QUEUE_STORE = "queue";
/** The exclusive upper bound of an IDBKeyRange covering every key under `prefix`. */
function prefixRange(prefix) {
	return IDBKeyRange.bound(prefix, prefix + "￿", false, false);
}
function requestToPromise(request) {
	return new Promise((resolve, reject) => {
		request.onsuccess = () => resolve(request.result);
		request.onerror = () => reject(request.error ?? /* @__PURE__ */ new Error("IndexedDB request failed"));
	});
}
/** Resolve when the whole transaction commits, not just when the last request returns. */
function transactionDone(tx) {
	return new Promise((resolve, reject) => {
		tx.oncomplete = () => resolve();
		tx.onabort = tx.onerror = () => reject(tx.error ?? /* @__PURE__ */ new Error("IndexedDB transaction failed"));
	});
}
/**
* IndexedDB-backed store — the browser default, so cached rows and queued
* writes survive a reload or a browser restart. Everything lives in one
* database with two object stores; keys are the manager's full prefixed
* strings, so multiple users (scopes) share the database without ever
* sharing entries.
*/
var IndexedDBOfflineStore = class {
	dbPromise;
	open() {
		if (!this.dbPromise) this.dbPromise = new Promise((resolve, reject) => {
			const request = indexedDB.open(IDB_NAME, IDB_VERSION);
			request.onupgradeneeded = (event) => {
				const db = request.result;
				if (event.oldVersion > 0 && event.oldVersion < 2) {
					if (db.objectStoreNames.contains(CACHE_STORE)) db.deleteObjectStore(CACHE_STORE);
					if (db.objectStoreNames.contains(QUEUE_STORE)) db.deleteObjectStore(QUEUE_STORE);
				}
				if (!db.objectStoreNames.contains(CACHE_STORE)) db.createObjectStore(CACHE_STORE);
				if (!db.objectStoreNames.contains(QUEUE_STORE)) db.createObjectStore(QUEUE_STORE);
			};
			request.onsuccess = () => {
				const db = request.result;
				db.onversionchange = () => {
					db.close();
					this.dbPromise = void 0;
				};
				resolve(db);
			};
			request.onerror = () => {
				this.dbPromise = void 0;
				reject(request.error ?? /* @__PURE__ */ new Error("Failed to open IndexedDB"));
			};
			request.onblocked = () => {
				this.dbPromise = void 0;
				reject(/* @__PURE__ */ new Error("IndexedDB upgrade blocked by another tab"));
			};
		});
		return this.dbPromise;
	}
	async store(name, mode) {
		return (await this.open()).transaction(name, mode).objectStore(name);
	}
	async getCache(key) {
		return await requestToPromise((await this.store(CACHE_STORE, "readonly")).get(key));
	}
	async setCache(key, entry) {
		await requestToPromise((await this.store(CACHE_STORE, "readwrite")).put(entry, key));
	}
	async setCacheMany(entries) {
		if (entries.length === 0) return;
		const store = await this.store(CACHE_STORE, "readwrite");
		for (const { key, entry } of entries) store.put(entry, key);
		await transactionDone(store.transaction);
	}
	async deleteCache(keys) {
		if (keys.length === 0) return;
		const store = await this.store(CACHE_STORE, "readwrite");
		for (const key of keys) store.delete(key);
		await transactionDone(store.transaction);
	}
	async listCache(prefix) {
		const store = await this.store(CACHE_STORE, "readonly");
		const [keys, entries] = await Promise.all([requestToPromise(store.getAllKeys(prefixRange(prefix))), requestToPromise(store.getAll(prefixRange(prefix)))]);
		return keys.map((key, i) => ({
			key: String(key),
			cachedAt: entries[i]?.cachedAt ?? 0
		}));
	}
	async listCacheEntries(prefix) {
		const store = await this.store(CACHE_STORE, "readonly");
		const [keys, entries] = await Promise.all([requestToPromise(store.getAllKeys(prefixRange(prefix))), requestToPromise(store.getAll(prefixRange(prefix)))]);
		return keys.map((key, i) => {
			const entry = entries[i];
			return {
				key: String(key),
				value: entry?.value,
				cachedAt: entry?.cachedAt ?? 0
			};
		});
	}
	async enqueue(key, mutation) {
		await requestToPromise((await this.store(QUEUE_STORE, "readwrite")).put(mutation, key));
	}
	async dequeue(key) {
		await requestToPromise((await this.store(QUEUE_STORE, "readwrite")).delete(key));
	}
	async listQueue(prefix) {
		return await requestToPromise((await this.store(QUEUE_STORE, "readonly")).getAll(prefixRange(prefix)));
	}
	async clear(prefix) {
		await requestToPromise((await this.store(CACHE_STORE, "readwrite")).delete(prefixRange(prefix)));
		await requestToPromise((await this.store(QUEUE_STORE, "readwrite")).delete(prefixRange(prefix)));
	}
};
//#endregion
//#region src/offline-query.ts
/**
* A local evaluator for `FindParams`, so cached rows can answer a query the
* client has never sent to the server — and so a row written offline shows up
* in every filtered list it belongs to, not just in unfiltered ones.
*
* This mirrors the Postgres driver's semantics rather than JavaScript's:
*
* - Comparing against NULL is *unknown*, not false-or-true. `status != "done"`
*   excludes rows where `status` is null, exactly as SQL does — a JS `!==`
*   would have included them.
* - `ORDER BY` puts nulls last ascending and first descending, which is the
*   Postgres default.
* - The wire format carries no types, so values arriving as strings are
*   compared numerically against numeric columns and as instants against
*   date columns. `["==", "3"]` matches the number `3`, as it does server-side.
*
* Two things it deliberately approximates, both flagged by
* {@link isExactlyEvaluable}: `searchString` becomes a case-insensitive
* substring scan over the row's string fields (the server runs real full-text
* search over the collection's configured columns), and `include` cannot be
* evaluated at all, because the related rows live in collections this query
* knows nothing about.
*/
var collator = typeof Intl !== "undefined" && typeof Intl.Collator === "function" ? new Intl.Collator(void 0, {
	numeric: false,
	sensitivity: "variant"
}) : void 0;
function isNullish(value) {
	return value === null || value === void 0;
}
/**
* Reduce a value to something comparable. Relations compare by the id they
* point at — the column holds a foreign key, so that is what the server
* compares too.
*/
function toComparable(value) {
	if (value instanceof Date) return value.getTime();
	if (value instanceof EntityRelation) return value.id;
	if (value && typeof value === "object") {
		const record = value;
		if (typeof record.__type === "string" && "id" in record) return record.id;
	}
	return value;
}
/**
* Three-way compare with SQL's type coercion but not its collation. Returns
* `undefined` when the two values are not ordered relative to each other,
* which is how NULL propagates through a comparison.
*/
function compareValues(a, b) {
	const left = toComparable(a);
	const right = toComparable(b);
	if (isNullish(left) || isNullish(right)) return void 0;
	if (typeof left === "boolean" || typeof right === "boolean") return (left === true || left === "true" || left === 1 ? 1 : 0) - (right === true || right === "true" || right === 1 ? 1 : 0);
	const leftNum = typeof left === "number" ? left : numericOrNaN(left);
	const rightNum = typeof right === "number" ? right : numericOrNaN(right);
	if (!Number.isNaN(leftNum) && !Number.isNaN(rightNum)) return leftNum < rightNum ? -1 : leftNum > rightNum ? 1 : 0;
	if (typeof left === "number" || typeof right === "number") {
		const leftTime = toTime(left);
		const rightTime = toTime(right);
		if (leftTime !== void 0 && rightTime !== void 0) return leftTime < rightTime ? -1 : leftTime > rightTime ? 1 : 0;
	}
	const leftStr = String(left);
	const rightStr = String(right);
	if (collator) return collator.compare(leftStr, rightStr);
	return leftStr < rightStr ? -1 : leftStr > rightStr ? 1 : 0;
}
function numericOrNaN(value) {
	if (typeof value === "number") return value;
	if (typeof value === "string" && value.trim() !== "") {
		const n = Number(value);
		return Number.isNaN(n) ? NaN : n;
	}
	if (typeof value === "bigint") return Number(value);
	return NaN;
}
function toTime(value) {
	if (typeof value === "number") return value;
	if (typeof value === "string") {
		const t = Date.parse(value);
		return Number.isNaN(t) ? void 0 : t;
	}
}
/** Equality with the wire's type erasure allowed for, but never across NULL. */
function looseEquals(a, b) {
	const left = toComparable(a);
	const right = toComparable(b);
	if (isNullish(left) || isNullish(right)) return isNullish(left) && isNullish(right);
	if (left === right) return true;
	return compareValues(left, right) === 0;
}
/**
* Translate a SQL `LIKE` pattern to an anchored regular expression.
* `%` matches any run of characters, `_` exactly one, and a backslash escapes
* either of them.
*/
function likeToRegExp(pattern, caseInsensitive) {
	let source = "^";
	let lastWasWildcard = false;
	for (let i = 0; i < pattern.length; i++) {
		const char = pattern[i];
		if (char === "\\" && i + 1 < pattern.length) {
			source += pattern[i + 1].replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
			i++;
			lastWasWildcard = false;
		} else if (char === "%") {
			if (!lastWasWildcard) source += "[\\s\\S]*";
			lastWasWildcard = true;
		} else if (char === "_") {
			source += "[\\s\\S]";
			lastWasWildcard = false;
		} else {
			source += char.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
			lastWasWildcard = false;
		}
	}
	return new RegExp(source + "$", caseInsensitive ? "i" : "");
}
function asArray(value) {
	if (Array.isArray(value)) return value;
	if (value === void 0) return [];
	return [value];
}
/** Evaluate one canonical operator against one row value. */
function matchesOperator(rowValue, op, filterValue) {
	switch (op) {
		case "is-null": return isNullish(rowValue);
		case "is-not-null": return !isNullish(rowValue);
		case "==": return looseEquals(rowValue, filterValue);
		case "!=":
			if (isNullish(rowValue)) return false;
			return !looseEquals(rowValue, filterValue);
		case "<":
		case "<=":
		case ">":
		case ">=": {
			const cmp = compareValues(rowValue, filterValue);
			if (cmp === void 0) return false;
			if (op === "<") return cmp < 0;
			if (op === "<=") return cmp <= 0;
			if (op === ">") return cmp > 0;
			return cmp >= 0;
		}
		case "in":
			if (isNullish(rowValue)) return false;
			return asArray(filterValue).some((v) => looseEquals(rowValue, v));
		case "not-in":
			if (isNullish(rowValue)) return false;
			return !asArray(filterValue).some((v) => looseEquals(rowValue, v));
		case "array-contains":
			if (!Array.isArray(rowValue)) return false;
			return rowValue.some((v) => looseEquals(v, filterValue));
		case "array-contains-any": {
			if (!Array.isArray(rowValue)) return false;
			const wanted = asArray(filterValue);
			return rowValue.some((v) => wanted.some((w) => looseEquals(v, w)));
		}
		case "like":
		case "not-like":
		case "ilike":
		case "not-ilike": {
			if (isNullish(rowValue)) return false;
			const insensitive = op === "ilike" || op === "not-ilike";
			const negated = op === "not-like" || op === "not-ilike";
			const matched = likeToRegExp(String(filterValue), insensitive).test(String(rowValue));
			return negated ? !matched : matched;
		}
		default: return true;
	}
}
function isTuple(value) {
	return Array.isArray(value) && value.length === 2 && typeof value[0] === "string" && toCanonicalOp(value[0]) !== void 0;
}
/** Evaluate a `where` clause: every field, and every tuple on a field, AND-ed. */
function matchesWhere(row, where) {
	if (!where) return true;
	for (const [field, condition] of Object.entries(where)) {
		if (condition === void 0) continue;
		const tuples = isTuple(condition) ? [condition] : Array.isArray(condition) ? condition.filter(isTuple) : [];
		for (const [rawOp, value] of tuples) {
			const op = toCanonicalOp(rawOp) ?? rawOp;
			if (!matchesOperator(row[field], op, value)) return false;
		}
	}
	return true;
}
/** Evaluate a nested and/or tree. */
function matchesLogical(row, condition) {
	if (!condition) return true;
	if ("type" in condition) {
		const children = condition.conditions ?? [];
		if (children.length === 0) return true;
		return condition.type === "or" ? children.some((c) => matchesLogical(row, c)) : children.every((c) => matchesLogical(row, c));
	}
	const op = toCanonicalOp(condition.operator) ?? condition.operator;
	return matchesOperator(row[condition.column], op, condition.value);
}
/**
* Approximate the server's full-text search with a case-insensitive substring
* scan over the row's own string fields. Narrower than the real thing (no
* stemming, no configured search columns), and it never matches a field the
* cached row does not carry — a local list may therefore be missing rows the
* server would have returned, which is why {@link isExactlyEvaluable} refuses
* to call a search query exact.
*
* Every term has to match, but they may match different fields — the same rule
* both drivers apply, and the one that finds `sebastian melendez` on a row that
* keeps the two halves of a name in separate fields. Matching the typed string
* as a whole found neither that row nor, because the space was part of the
* needle, anything at all for `sebastian `.
*/
function matchesSearch(row, searchString) {
	if (!searchString) return true;
	const terms = splitSearchTerms(searchString).map((term) => term.toLowerCase());
	if (terms.length === 0) return true;
	const values = Object.values(row);
	return terms.every((term) => values.some((value) => {
		if (typeof value === "string") return value.toLowerCase().includes(term);
		if (typeof value === "number") return String(value).includes(term);
		return false;
	}));
}
/** Does this row belong in the result set for `params`, ignoring pagination? */
function matchesParams(row, params) {
	if (!params) return true;
	return matchesWhere(row, params.where) && matchesLogical(row, params.logical) && matchesSearch(row, params.searchString);
}
/**
* Sort in place, Postgres-style: nulls last ascending, first descending, with
* the row id as a tiebreak so paging through an unsorted-but-equal run does
* not shuffle rows between pages.
*
* The tiebreak runs *descending*, which is not a taste: every server-side sort
* ends on `id DESC` — `FetchService.buildOrderExpressions` appends it to make
* the ordering total, and the keyset cursor is built to match. This ran
* ascending, so two rows sharing a sort value came back from the local overlay
* in the opposite order to the server's, and {@link isLocallySortable} called
* that page exactly reproducible while it was not.
*/
function sortRows(rows, orderBy) {
	const keys = normalizeOrderBy(orderBy);
	if (!keys) return rows;
	return rows.sort((a, b) => {
		for (const [field, direction = "asc"] of keys) {
			const cmp = compareOnKey(a, b, field, direction);
			if (cmp !== 0) return cmp;
		}
		return tiebreak(a, b);
	});
}
/** One key's verdict: negative, positive, or 0 for "these two are equal here". */
function compareOnKey(a, b, field, direction) {
	const av = a[field];
	const bv = b[field];
	const aNull = isNullish(toComparable(av));
	const bNull = isNullish(toComparable(bv));
	if (aNull || bNull) {
		if (aNull && bNull) return 0;
		return (aNull ? 1 : -1) * (direction === "desc" ? -1 : 1);
	}
	const cmp = compareValues(av, bv);
	if (cmp === void 0 || cmp === 0) return 0;
	return cmp * (direction === "desc" ? -1 : 1);
}
/** The last word, and the server's: `id DESC`. */
function tiebreak(a, b) {
	const cmp = compareValues(a.id, b.id);
	return cmp === void 0 ? 0 : -cmp;
}
/**
* Resolve `page`/`offset`/`limit` the way the server does.
*
* It did not: this defaulted an absent limit to 20 while `/api/data` pages by
* 50, so the same `observe()` answered with 20 rows from the local database and
* 50 from the network — a list that changed length depending on which side
* answered, with `page` striding differently on each. Delegated now, so the
* sentence above is true by construction rather than by agreement.
*/
function resolvePagination(params) {
	const { limit, offset } = resolveFindWindow(params);
	return {
		limit,
		offset
	};
}
/** `<`, `<=`, `>`, `>=` — the operators whose answer depends on a collation. */
var ORDERING_OPS = /* @__PURE__ */ new Set([
	"<",
	"<=",
	">",
	">="
]);
/** Does any condition in this `where` clause order its operands? */
function whereOrders(where) {
	if (!where) return false;
	for (const condition of Object.values(where)) if ((isTuple(condition) ? [condition] : condition.filter(isTuple)).some(([op]) => ORDERING_OPS.has(op))) return true;
	return false;
}
/** The same question, through an `and(...)`/`or(...)` tree. */
function logicalOrders(condition, depth = 0) {
	if (!condition || depth > 32) return false;
	if ("type" in condition) return (condition.conditions ?? []).some((c) => logicalOrders(c, depth + 1));
	return ORDERING_OPS.has(condition.operator);
}
/**
* Can a locally evaluated answer to `params` be trusted to match the server's,
* assuming the cache holds every row of the collection?
*
* `include` pulls in rows from other collections that this evaluator never
* sees, and `searchString` is only approximated — both make the local answer a
* best effort rather than an equivalent one.
*
* **Ordering comparisons are refused, and that is the interesting one.**
* `compareValues` falls back to an `Intl.Collator` for operands it cannot read
* as numbers or instants. PostgreSQL orders text by the *database's* collation,
* which is a property of the server this process has never been told: under the
* C collation `'apple' < 'Banana'` is false, under `en_US.UTF-8` it is true,
* and the collator says true. So `["<", "Banana"]` selects a different set here
* than it does there — silently, and in whichever direction the deployment
* happens to have been created.
*
* The refusal covers *every* ordering comparison rather than only the ones with
* a string operand, because the operand type does not settle it: a numeric
* bound against a text column (`["<", 10]` on a `varchar`) also reaches the
* collator, and nothing in `params` says what the column holds. Conservative on
* purpose — the cost is that a query combining an ordering filter with
* *unsynced local writes* stops placing those writes optimistically, which is a
* degraded answer rather than a wrong one. Claiming exactness we do not have is
* the other way round.
*
* This says nothing about ordering *results*; that is a separate claim with a
* separate answer, because a sort changes which rows come first and not which
* rows match. See {@link isLocallySortable}.
*/
/**
* Whether an `include` asks for anything, in any of its spellings.
*
* `include` is a list of names, a list of dotted paths, or a tree — and only
* the first two have a `length`. A `.length > 0` test read the tree form as
* "no relations", which is the answer that makes the local cache claim it can
* evaluate a relational query exactly.
*/
function hasAnyInclude(include) {
	if (!include) return false;
	if (Array.isArray(include)) return include.length > 0;
	return Object.keys(include).length > 0;
}
function isExactlyEvaluable(params) {
	if (!params) return true;
	if (hasAnyInclude(params.include)) return false;
	if (params.searchString) return false;
	if (params.vectorSearch) return false;
	if (whereOrders(params.where)) return false;
	if (logicalOrders(params.logical)) return false;
	if (whereReachesThroughRelation(params.where)) return false;
	if (logicalReachesThroughRelation(params.logical)) return false;
	return true;
}
/** Does any filter key reach outside this row — `applications.status`? */
function whereReachesThroughRelation(where) {
	return where ? Object.keys(where).some((field) => field.includes(".")) : false;
}
/** The same question, through an `and(...)`/`or(...)` tree. */
function logicalReachesThroughRelation(condition, depth = 0) {
	if (!condition || depth > 32) return false;
	if ("type" in condition) return (condition.conditions ?? []).some((c) => logicalReachesThroughRelation(c, depth + 1));
	return typeof condition.column === "string" && condition.column.includes(".");
}
/**
* Would sorting `rows` locally reproduce the order the server would have sent?
*
* Asked of the rows rather than of the query, because unlike a filter this one
* *is* decidable from the data in hand: {@link compareValues} reaches the
* collator only when it cannot read both operands as numbers, and `toComparable`
* has already turned dates and relations into numbers and ids by then. If every
* value on the sort column normalises to a number, the collator is unreachable
* and the local order is the server's order.
*
* A text column is therefore refused — see {@link isExactlyEvaluable} for why
* the two cannot be made to agree — and so is a column this page happens to see
* only as strings, which is the same thing from here.
*
* Nulls are fine either way: they are ordered by an explicit rule (last
* ascending, first descending) that matches Postgres and never reaches the
* comparator.
*/
function isLocallySortable(rows, orderBy) {
	const keys = normalizeOrderBy(orderBy);
	if (!keys) return true;
	if (keys.some(([field]) => parseRelationAggregateSort(field))) return false;
	return keys.every(([field]) => rows.every((row) => {
		const value = toComparable(row[field]);
		if (isNullish(value)) return true;
		if (typeof value === "number" || typeof value === "boolean") return true;
		if (typeof value === "bigint") return true;
		if (typeof value === "string" && value.trim() !== "" && !Number.isNaN(Number(value))) return true;
		return false;
	}));
}
/** Run a full query — filter, sort, paginate — over a set of rows. */
function runLocalQuery(rows, params) {
	const matched = rows.filter((row) => matchesParams(row, params));
	sortRows(matched, params?.orderBy);
	const { limit, offset } = resolvePagination(params);
	const page = matched.slice(offset, offset + limit);
	return {
		data: page,
		meta: {
			total: matched.length,
			limit,
			offset,
			hasMore: offset + page.length < matched.length
		}
	};
}
//#endregion
//#region src/offline.ts
/**
* True when a read failed because there was neither network nor local data.
*
* The code is `OFFLINE`. It was `"offline"`, the one lowercase code in a
* surface whose every other code is `SCREAMING_SNAKE_CASE` — including the
* `NETWORK_ERROR` it sits next to — so `e.code === "OFFLINE"`, which is what
* anyone writes, never matched.
*/
function isOfflineError(error) {
	return error instanceof RebaseApiError && error.code === "OFFLINE";
}
function offlineError(message) {
	return new RebaseApiError(message, {
		status: 0,
		code: "OFFLINE"
	});
}
function generateOfflineStringId() {
	if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") return crypto.randomUUID();
	return `off-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}
/**
* The last temporary numeric id minted, so the next one is smaller.
*
* Module-scoped and strictly decreasing: two temporary ids in the same session
* must not collide, and neither must one minted a millisecond after another.
*/
var lastOfflineNumericId = 0;
/**
* A temporary id for a collection whose ids are numbers.
*
* **Negative**, which is the convention every local-first engine settles on for
* the same reason: a real key is a positive integer or an identity sequence, so
* the sign alone says "this one is not the server's yet" — and it stays a
* number, which is what matters. A UUID string was minted for every collection,
* so a `create` while offline handed back `{ id: "526e2faf-…" }` on a row the
* generated types declare `id: number`. Anything that put that in a URL, a
* foreign key or an `id.toFixed()` was wrong or threw until the replay
* reconciled it — and the SDK's own types said it could not happen.
*/
function generateOfflineNumericId() {
	const candidate = -Date.now();
	lastOfflineNumericId = lastOfflineNumericId === 0 ? candidate : Math.min(candidate, lastOfflineNumericId - 1);
	return lastOfflineNumericId;
}
/**
* The row an optimistic write puts in the cache before the server has seen it.
*
* It is NOT an `M`, and saying so is the point of collecting this in one place.
* `M` is the row as the database has it — every server default, every generated
* column, every `afterRead` transform. What an optimistic write has is the
* fields the caller supplied (plus, on an update, whatever was already cached)
* and an id, which may itself be a locally-minted placeholder.
*
* The two are reconciled when the queued write drains and the server's row
* replaces this one. Until then the SDK hands the caller this, under `M`,
* because that is what the offline API's return type promises — and there is
* nothing in this file that can make that promise true.
*
* So: one deliberate assertion, named, rather than five spread across the
* create / createMany / update / upsert paths, each free to assemble the fields
* differently. Fixing it properly means the offline surface saying what it
* actually returns, which is a change to its public types.
*/
function optimisticRow(fields, id) {
	return {
		...fields,
		id
	};
}
var MISSING = "\0missing";
/**
* Replays to spend on a mutation whose idempotency key the server is still
* holding, when the app has not asked for more.
*
* Retries double from a second and cap at the sync interval, so the default
* budget of five covers about half a minute — less than the lease a server
* gives a claim nobody came back for. This many outlast it with room for a slow
* batch, and the count is what stops a server that never releases the key from
* blocking the queue behind it indefinitely.
*/
var IN_PROGRESS_MIN_RETRIES = 12;
var OfflineManager = class {
	store;
	maxCachedQueries;
	maxCachedRows;
	maxRetries;
	onSyncError;
	createInner;
	inners = /* @__PURE__ */ new Map();
	connectivity;
	scope = "anon";
	/** The local database: normalized rows and query snapshots per collection. */
	collections = /* @__PURE__ */ new Map();
	/** In-memory mirror of the current scope's queue, in replay order. */
	queue = [];
	/**
	* The mutation currently on the wire, if any.
	*
	* `flush` awaits `replay(op)` with `op` still at the head of `queue`, so for
	* the whole duration of that request the in-flight op is also the queue's
	* *tail* whenever it is the only entry. Both shortcuts in `enqueue` reach
	* for the tail, and neither may touch an op the server is already reading:
	*
	* - Coalescing an update into it mutates a payload that has already been
	*   serialized and sent, and `drop` then removes the whole entry on ACK —
	*   so the second edit is neither sent nor kept. A silently lost write.
	* - Cancelling it out against a delete assumes the server never saw the
	*   create. It is seeing it right now, so the row would be created and the
	*   delete never queued — an orphan row nothing will ever remove.
	*
	* Guarding on the id rather than on a boolean keeps this correct if the
	* flush loop ever sends more than one op at a time.
	*/
	inFlightId = null;
	queueLoad;
	/** Serializes enqueues so concurrent writes keep the order the app made them. */
	enqueueChain = Promise.resolve();
	flushPromise;
	queueListeners = /* @__PURE__ */ new Set();
	statusListeners = /* @__PURE__ */ new Set();
	observers = /* @__PURE__ */ new Map();
	refreshPending = /* @__PURE__ */ new Set();
	revCounter = 0;
	disposed = false;
	currentStatus = {
		online: true,
		syncing: false,
		pending: 0
	};
	channel;
	tabId = createMutationId();
	api;
	constructor(config, createInner) {
		this.store = config.store ?? (typeof indexedDB !== "undefined" ? new IndexedDBOfflineStore() : new MemoryOfflineStore());
		this.maxCachedQueries = config.maxCachedQueriesPerCollection ?? 50;
		this.maxCachedRows = config.maxCachedRowsPerCollection ?? 5e3;
		this.maxRetries = config.maxRetries ?? 5;
		this.onSyncError = config.onSyncError;
		this.createInner = createInner;
		const maxBackoffMs = config.syncIntervalMs ?? 6e4;
		this.connectivity = new ConnectivityMonitor({
			maxBackoffMs: Math.max(1e3, maxBackoffMs),
			respectBackoff: maxBackoffMs > 0
		});
		if (maxBackoffMs > 0) this.connectivity.onRetryDue = () => {
			this.sync().catch(() => void 0);
		};
		this.connectivity.onChange((online) => {
			this.patchStatus({ online });
			if (online) this.revalidateAll();
		});
		this.currentStatus.online = this.connectivity.isOnline();
		if ((config.crossTab ?? this.store instanceof IndexedDBOfflineStore) && typeof BroadcastChannel !== "undefined") try {
			this.channel = new BroadcastChannel("rebase-offline");
			this.channel.onmessage = (event) => this.onBroadcast(event.data);
			unref(this.channel);
		} catch {}
		this.api = {
			sync: () => this.sync(),
			pending: async () => {
				await this.ensureQueueLoaded();
				return this.queue.map((m) => structuredClone(m));
			},
			status: () => ({ ...this.currentStatus }),
			onStatusChange: (listener) => {
				this.statusListeners.add(listener);
				return () => this.statusListeners.delete(listener);
			},
			clear: async () => {
				await this.store.clear(`${this.scope}|`);
				this.queue = [];
				this.resetCollections();
				this.patchStatus({
					pending: 0,
					lastError: void 0
				});
				this.notifyQueue();
				for (const slug of this.observers.keys()) this.notifyCollection(slug, false);
			},
			onQueueChange: (listener) => {
				this.queueListeners.add(listener);
				return () => this.queueListeners.delete(listener);
			}
		};
	}
	/**
	* Cache and queue are partitioned per signed-in user: cached rows are
	* RLS-filtered for the user who fetched them, and queued writes must
	* replay under the credentials that made them — so neither may ever leak
	* across a sign-out/sign-in on a shared browser.
	*/
	setScope(uid) {
		const next = uid || "anon";
		if (next === this.scope) return;
		this.scope = next;
		this.queueLoad = void 0;
		this.queue = [];
		this.resetCollections();
		this.patchStatus({
			pending: 0,
			lastError: void 0
		});
		this.notifyQueue();
		for (const slug of this.observers.keys()) this.notifyCollection(slug, false);
		this.revalidateAll();
		this.sync().catch(() => void 0);
	}
	/**
	* Throw away every local row, for a scope change or an explicit clear.
	*
	* The state objects are replaced rather than emptied, so a load still in
	* flight for the previous user fails its identity check and discards what
	* it read instead of grafting it onto the new one. The replacements are
	* marked ready: nothing needs loading until something asks, and observers
	* have to be told *now* that the rows they are showing are gone.
	*/
	resetCollections() {
		const slugs = [...this.collections.keys()];
		this.collections = /* @__PURE__ */ new Map();
		for (const slug of slugs) this.collections.set(slug, {
			rows: /* @__PURE__ */ new Map(),
			snapshots: /* @__PURE__ */ new Map(),
			fresh: /* @__PURE__ */ new Set(),
			freshRows: /* @__PURE__ */ new Set(),
			absent: /* @__PURE__ */ new Set(),
			ready: true
		});
	}
	/** Release listeners, timers and the cross-tab channel (client.close()). */
	dispose() {
		this.disposed = true;
		this.connectivity.dispose();
		try {
			this.channel?.close();
		} catch {}
		this.observers.clear();
		this.queueListeners.clear();
		this.statusListeners.clear();
	}
	wrap(slug, inner) {
		this.inners.set(slug, inner);
		const wrapped = {
			find: async (params) => {
				const state = await this.ensureCollection(slug);
				if (this.connectivity.shouldAttempt()) try {
					const res = await inner.find(params);
					this.connectivity.markSuccess();
					await this.ingest(slug, res.data ?? []);
					const snapshot = this.recordSnapshot(slug, params, res);
					const answer = this.answer(slug, params, snapshot);
					this.notifyCollection(slug, false);
					return {
						data: answer.data,
						meta: answer.meta
					};
				} catch (error) {
					if (!isNetworkError(error)) {
						if (isRetryableError(error) && this.hasLocalAnswer(state, slug, params)) {
							const answer = this.answer(slug, params, this.snapshotFor(slug, params));
							return {
								data: answer.data,
								meta: answer.meta
							};
						}
						throw error;
					}
					this.connectivity.markFailure();
				}
				const answer = this.localFind(slug, params);
				this.notifyCollection(slug, false);
				return {
					data: answer.data,
					meta: answer.meta
				};
			},
			iterate: (params) => paginateFind((p) => wrapped.find(p), params, slug),
			findAll: (params) => collectAllPages((p) => wrapped.find(p), params, slug),
			findById: async (id) => {
				await this.ensureCollection(slug);
				if (this.connectivity.shouldAttempt()) try {
					const row = await inner.findById(id);
					this.connectivity.markSuccess();
					if (row !== void 0) await this.ingest(slug, [row]);
					else if (!this.hasPending(slug, id)) this.removeLocalRow(slug, id, true);
					this.notifyCollection(slug, false);
					return this.localRow(slug, id);
				} catch (error) {
					if (!isNetworkError(error)) throw error;
					this.connectivity.markFailure();
				}
				const local = this.localRow(slug, id);
				if (local !== void 0 || this.hasPending(slug, id)) return local;
				if (this.collections.get(slug)?.absent.has(String(id))) return void 0;
				throw offlineError(`Offline: "${slug}" row ${String(id)} is not in the local database.`);
			},
			get: async (id) => {
				const row = await wrapped.findById(id);
				if (row === void 0) throw new RebaseApiError(`No record with id ${JSON.stringify(String(id))} in "${slug}".`, {
					status: 404,
					code: "NOT_FOUND"
				});
				return row;
			},
			create: async (data, id) => {
				await this.ensureCollection(slug);
				if (this.connectivity.shouldAttempt()) try {
					const row = await inner.create(data, id);
					this.connectivity.markSuccess();
					await this.ingest(slug, [row]);
					this.notifyCollection(slug);
					this.scheduleRefresh(slug);
					return row;
				} catch (error) {
					if (!isNetworkError(error)) throw error;
					this.connectivity.markFailure();
				}
				const providedId = id ?? data.id;
				const rowId = providedId ?? this.mintOfflineId(slug);
				const row = optimisticRow(data, rowId);
				await this.enqueue({
					collection: slug,
					type: "create",
					id: rowId,
					data: row,
					generatedId: providedId === void 0,
					rollback: { rows: { [String(rowId)]: this.rawLocalRow(slug, rowId) ?? null } }
				});
				this.setLocalRow(slug, rowId, row);
				this.notifyCollection(slug);
				return row;
			},
			createMany: async (data, options) => {
				await this.ensureCollection(slug);
				if (!Array.isArray(data)) throw new TypeError("createMany expects an array of records.");
				if (data.length === 0) return [];
				if (this.connectivity.shouldAttempt()) try {
					const rows = await inner.createMany(data, options);
					this.connectivity.markSuccess();
					await this.ingest(slug, rows);
					this.notifyCollection(slug);
					this.scheduleRefresh(slug);
					return rows;
				} catch (error) {
					if (!isNetworkError(error)) throw error;
					this.connectivity.markFailure();
				}
				const rows = data.map((r) => optimisticRow(r, r.id ?? this.mintOfflineId(slug)));
				const rollback = {};
				for (const row of rows) {
					const key = String(row.id);
					rollback[key] = this.rawLocalRow(slug, row.id) ?? null;
				}
				await this.enqueue({
					collection: slug,
					type: "createMany",
					data: rows,
					upsert: options?.upsert,
					rollback: { rows: rollback }
				});
				for (const row of rows) this.setLocalRow(slug, row.id, row);
				this.notifyCollection(slug);
				return rows;
			},
			/**
			* Upsert, with the one case that cannot be resolved without the
			* server refused rather than guessed.
			*
			* On the primary key an upsert is create-or-replace, which the
			* local store can do and the queue already carries (`createMany`
			* with `upsert`). On a natural key it is not: deciding whether a
			* row with that email exists means an index this store does not
			* keep, and inventing an id would insert a duplicate the moment the
			* queue drains — the exact outcome upsert exists to prevent.
			*/
			upsert: async (data, options) => {
				await this.ensureCollection(slug);
				if (this.connectivity.shouldAttempt()) try {
					const row = await inner.upsert(data, options);
					this.connectivity.markSuccess();
					await this.ingest(slug, [row]);
					this.notifyCollection(slug);
					this.scheduleRefresh(slug);
					return row;
				} catch (error) {
					if (!isNetworkError(error)) throw error;
					this.connectivity.markFailure();
				}
				const rowId = data.id;
				if (options?.onConflict?.length || rowId === void 0) throw new RebaseClientError(`Cannot upsert into "${slug}" while offline: the row it would replace can only be found by the server. Upserting on the primary key, with the key in the row, is queued; upserting on a natural key is not.`, { code: "OFFLINE_UPSERT_UNSUPPORTED" });
				const row = optimisticRow(data, rowId);
				await this.enqueue({
					collection: slug,
					type: "createMany",
					data: [row],
					upsert: true,
					rollback: { rows: { [String(rowId)]: this.rawLocalRow(slug, rowId) ?? null } }
				});
				this.setLocalRow(slug, rowId, row);
				this.notifyCollection(slug);
				return row;
			},
			updateMany: async (updates, options) => {
				await this.ensureCollection(slug);
				if (!Array.isArray(updates)) throw new TypeError("updateMany expects an array of { id, data } entries.");
				if (updates.length === 0) return [];
				const anyPending = updates.some((u) => this.hasPending(slug, u.id));
				if (this.connectivity.shouldAttempt() && !anyPending) try {
					const rows = await inner.updateMany(updates, options);
					this.connectivity.markSuccess();
					await this.ingest(slug, rows);
					this.notifyCollection(slug);
					return rows;
				} catch (error) {
					if (!isNetworkError(error)) throw error;
					this.connectivity.markFailure();
				}
				const rollback = {};
				const optimistic = [];
				for (const { id, data } of updates) {
					const base = this.rawLocalRow(slug, id);
					rollback[String(id)] = base ?? null;
					optimistic.push(optimisticRow({
						...base ?? {},
						...data
					}, id));
				}
				await this.enqueue({
					collection: slug,
					type: "updateMany",
					updates: updates.map((u) => ({
						id: u.id,
						data: u.data
					})),
					rollback: { rows: rollback }
				});
				for (const row of optimistic) this.setLocalRow(slug, row.id, row);
				this.notifyCollection(slug);
				return optimistic;
			},
			deleteMany: async (ids, options) => {
				await this.ensureCollection(slug);
				if (!Array.isArray(ids)) throw new TypeError("deleteMany expects an array of ids.");
				if (ids.length === 0) return;
				const anyPending = ids.some((id) => this.hasPending(slug, id));
				if (this.connectivity.shouldAttempt() && !anyPending) try {
					await inner.deleteMany(ids, options);
					this.connectivity.markSuccess();
					for (const id of ids) this.removeLocalRow(slug, id, true);
					this.notifyCollection(slug);
					this.scheduleRefresh(slug);
					return;
				} catch (error) {
					if (!isNetworkError(error)) throw error;
					this.connectivity.markFailure();
				}
				const rollback = {};
				for (const id of ids) rollback[String(id)] = this.rawLocalRow(slug, id) ?? null;
				await this.enqueue({
					collection: slug,
					type: "deleteMany",
					ids,
					rollback: { rows: rollback }
				});
				for (const id of ids) this.removeLocalRow(slug, id, false);
				this.notifyCollection(slug);
			},
			update: async (id, data, options) => {
				await this.ensureCollection(slug);
				if (this.connectivity.shouldAttempt() && !this.hasPending(slug, id)) try {
					const row = await inner.update(id, data, options);
					this.connectivity.markSuccess();
					await this.ingest(slug, [row]);
					this.notifyCollection(slug);
					return row;
				} catch (error) {
					if (!isNetworkError(error)) throw error;
					this.connectivity.markFailure();
				}
				if (hasFieldOperation(data)) throw new RebaseClientError(`Cannot apply a field operation to "${slug}" while offline: it is evaluated against the stored value, which this device does not have a current copy of. Send the value itself, or retry when the connection is back.`, { code: "OFFLINE_FIELD_OP_UNSUPPORTED" });
				const base = this.rawLocalRow(slug, id);
				await this.enqueue({
					collection: slug,
					type: "update",
					id,
					data,
					rollback: { rows: { [String(id)]: base ?? null } }
				});
				const optimistic = optimisticRow({
					...base ?? {},
					...data
				}, id);
				this.setLocalRow(slug, id, optimistic);
				this.notifyCollection(slug);
				return optimistic;
			},
			delete: async (id, options) => {
				await this.ensureCollection(slug);
				if (this.connectivity.shouldAttempt() && !this.hasPending(slug, id)) try {
					await inner.delete(id, options);
					this.connectivity.markSuccess();
					this.removeLocalRow(slug, id, true);
					this.notifyCollection(slug);
					this.scheduleRefresh(slug);
					return;
				} catch (error) {
					if (!isNetworkError(error)) throw error;
					this.connectivity.markFailure();
				}
				await this.enqueue({
					collection: slug,
					type: "delete",
					id,
					rollback: { rows: { [String(id)]: this.rawLocalRow(slug, id) ?? null } }
				});
				this.removeLocalRow(slug, id);
				this.notifyCollection(slug);
			},
			count: async (params) => {
				await this.ensureCollection(slug);
				if (this.connectivity.shouldAttempt()) try {
					const n = await inner.count(params);
					this.connectivity.markSuccess();
					this.writeCache(this.countKey(slug, params), n);
					return Math.max(0, n + this.pendingDelta(slug, params));
				} catch (error) {
					if (!isNetworkError(error)) throw error;
					this.connectivity.markFailure();
				}
				const cached = await this.readCache(this.countKey(slug, params));
				if (cached !== void 0) return Math.max(0, cached + this.pendingDelta(slug, params));
				const state = this.collections.get(slug);
				if (state && state.rows.size > 0) return runLocalQuery([...state.rows.values()].map((e) => e.row), params).meta.total;
				throw offlineError(`Offline: no cached count for "${slug}".`);
			},
			observe: (params, onResult, onError, options) => this.observe(slug, wrapped, inner, params, onResult, onError, options),
			observeById: (id, onResult, onError, options) => this.observeById(slug, wrapped, inner, id, onResult, onError, options),
			where(columnOrCondition, operator, value) {
				const builder = new SDKQueryBuilder(wrapped);
				if (typeof columnOrCondition === "object") return builder.where(columnOrCondition);
				return builder.where(columnOrCondition, operator, value);
			},
			orderBy: (column, direction, nulls) => new SDKQueryBuilder(wrapped).orderBy(column, direction, nulls),
			limit: (count) => new SDKQueryBuilder(wrapped).limit(count),
			offset: (count) => new SDKQueryBuilder(wrapped).offset(count),
			search: (searchString, options) => new SDKQueryBuilder(wrapped).search(searchString, options),
			vectorSearch: (property, vector, options) => new SDKQueryBuilder(wrapped).vectorSearch(property, vector, options),
			include: (...relations) => new SDKQueryBuilder(wrapped).include(...relations),
			fields: (...columns) => new SDKQueryBuilder(wrapped).fields(...columns),
			distinct: (enabled) => new SDKQueryBuilder(wrapped).distinct(enabled),
			after: (cursor) => new SDKQueryBuilder(wrapped).after(cursor),
			aggregate: inner.aggregate,
			listen: inner.listen,
			listenById: inner.listenById
		};
		if (!isUnsupported(inner.listen)) wrapped.listen = (params, onUpdate, onError) => inner.listen(params, (response) => {
			this.ingest(slug, response.data ?? []).then(() => this.notifyCollection(slug, false));
			onUpdate(response);
		}, onError);
		if (!isUnsupported(inner.listenById)) wrapped.listenById = (id, onUpdate, onError) => inner.listenById(id, (row) => {
			if (row) this.ingest(slug, [row]).then(() => this.notifyCollection(slug, false));
			onUpdate(row);
		}, onError);
		return wrapped;
	}
	observe(slug, wrapped, inner, params, onResult, onError, options) {
		let closed = false;
		let unlisten;
		const observer = {
			slug,
			params,
			settled: false,
			refresh: () => wrapped.find(params).catch(() => void 0),
			emit: () => {
				if (closed || !this.collections.get(slug)?.ready) return;
				const result = this.answer(slug, params, this.snapshotFor(slug, params));
				const signature = `${result.fromCache ? "c" : "s"}${result.hasPendingWrites ? "p" : "-"}` + this.signature(slug, result.data, result.meta.total);
				if (observer.settled && signature === observer.signature) return;
				observer.signature = signature;
				observer.settled = true;
				onResult(observer.error ? {
					...result,
					error: observer.error
				} : result);
			}
		};
		this.observersFor(slug).add(observer);
		(async () => {
			await this.ensureCollection(slug);
			if (closed) return;
			if (this.hasLocalAnswer(this.collections.get(slug), slug, params)) observer.emit();
			try {
				await wrapped.find(params);
				observer.error = void 0;
			} catch (error) {
				observer.error = error;
				if (closed) return;
				if (!observer.settled) {
					onError?.(error);
					return;
				}
			}
			if (!closed) observer.emit();
		})();
		if (options?.realtime !== false && !isUnsupported(inner.listen)) unlisten = inner.listen(params, (response) => {
			this.ingest(slug, response.data ?? []).then(() => {
				this.recordSnapshot(slug, params, response);
				this.notifyCollection(slug, false);
			});
		}, onError);
		return () => {
			closed = true;
			this.observersFor(slug).delete(observer);
			unlisten?.();
		};
	}
	observeById(slug, wrapped, inner, id, onResult, onError, options) {
		let closed = false;
		let unlisten;
		const observer = {
			slug,
			id,
			settled: false,
			refresh: () => wrapped.findById(id).catch(() => void 0),
			emit: () => {
				if (closed || !this.collections.get(slug)?.ready) return;
				const row = this.localRow(slug, id);
				const entry = this.collections.get(slug)?.rows.get(String(id));
				const fromCache = !this.collections.get(slug)?.freshRows.has(String(id));
				const hasPendingWrites = this.hasPending(slug, id);
				const signature = `${fromCache ? "c" : "s"}${hasPendingWrites ? "p" : "-"}|` + (row === void 0 ? MISSING : `${String(id)}:${entry?.rev ?? 0}`);
				if (observer.settled && signature === observer.signature) return;
				observer.signature = signature;
				observer.settled = true;
				onResult(row, {
					fromCache,
					hasPendingWrites
				});
			}
		};
		this.observersFor(slug).add(observer);
		(async () => {
			await this.ensureCollection(slug);
			if (closed) return;
			if (this.localRow(slug, id) !== void 0) observer.emit();
			try {
				await wrapped.findById(id);
			} catch (error) {
				if (closed) return;
				if (!observer.settled) {
					onError?.(error);
					return;
				}
			}
			if (!closed) observer.emit();
		})();
		if (options?.realtime !== false && !isUnsupported(inner.listenById)) unlisten = inner.listenById(id, (row) => {
			if (!row) {
				if (!this.hasPending(slug, id)) this.removeLocalRow(slug, id, true);
				this.notifyCollection(slug, false);
				return;
			}
			this.ingest(slug, [row]).then(() => this.notifyCollection(slug, false));
		}, onError);
		return () => {
			closed = true;
			this.observersFor(slug).delete(observer);
			unlisten?.();
		};
	}
	observersFor(slug) {
		let set = this.observers.get(slug);
		if (!set) {
			set = /* @__PURE__ */ new Set();
			this.observers.set(slug, set);
		}
		return set;
	}
	/** Cheap change detection: which rows, in what order, at which revision. */
	signature(slug, rows, total) {
		const state = this.collections.get(slug);
		return `${total}|${rows.map((row) => {
			const key = String(row.id);
			return `${key}:${state?.rows.get(key)?.rev ?? 0}`;
		}).join(",")}`;
	}
	notifyCollection(slug, broadcast = true) {
		const set = this.observers.get(slug);
		if (set) for (const observer of [...set]) observer.emit();
		if (broadcast) this.broadcast({
			type: "rows",
			slugs: [slug]
		});
	}
	/** Connectivity came back (or the user changed): re-read everything live. */
	revalidateAll() {
		for (const slug of this.observers.keys()) {
			this.notifyCollection(slug, false);
			this.scheduleRefresh(slug);
		}
	}
	collectionState(slug) {
		let state = this.collections.get(slug);
		if (!state) {
			state = {
				rows: /* @__PURE__ */ new Map(),
				snapshots: /* @__PURE__ */ new Map(),
				fresh: /* @__PURE__ */ new Set(),
				freshRows: /* @__PURE__ */ new Set(),
				absent: /* @__PURE__ */ new Set(),
				ready: false
			};
			this.collections.set(slug, state);
		}
		return state;
	}
	ensureCollection(slug) {
		const state = this.collectionState(slug);
		if (!state.loaded) {
			const scope = this.scope;
			state.loaded = (async () => {
				await this.ensureQueueLoaded();
				const [rows, snapshots, absent] = await Promise.all([
					this.store.listCacheEntries(`${scope}|row|${slug}|`).catch(() => []),
					this.store.listCacheEntries(`${scope}|q|${slug}|`).catch(() => []),
					this.store.listCache(`${scope}|abs|${slug}|`).catch(() => [])
				]);
				if (this.scope !== scope || this.collections.get(slug) !== state) return;
				for (const entry of rows) {
					const row = entry.value;
					if (!row || row.id === void 0 || row.id === null) continue;
					state.rows.set(String(row.id), {
						row: hydrateRow(row),
						cachedAt: entry.cachedAt,
						rev: ++this.revCounter
					});
				}
				for (const entry of snapshots) {
					const key = entry.key.slice(`${scope}|q|${slug}|`.length);
					if (entry.value) state.snapshots.set(key, entry.value);
				}
				for (const entry of absent) state.absent.add(entry.key.slice(`${scope}|abs|${slug}|`.length));
			})().catch(() => void 0).finally(() => {
				state.ready = true;
			});
		}
		return state.loaded.then(() => state);
	}
	snapshotFor(slug, params) {
		return this.collections.get(slug)?.snapshots.get(buildQueryString(params));
	}
	hasLocalAnswer(state, slug, params) {
		if (!state) return false;
		return state.snapshots.has(buildQueryString(params)) || state.rows.size > 0;
	}
	/**
	* Answer a query from the local database.
	*
	* With a snapshot, the server's own page — its ids, order and total — is
	* the skeleton, and the local rows fill it in: rows deleted locally drop
	* out, rows edited locally show the edit, and rows *created* locally join
	* the first page if they match. Without one, the query is evaluated
	* outright over every cached row, which is the best that can be done for a
	* query the server has never answered here.
	*/
	answer(slug, params, snapshot) {
		const state = this.collections.get(slug);
		const exact = isExactlyEvaluable(params);
		const fromCache = !state?.fresh.has(buildQueryString(params));
		if (!state) return {
			data: [],
			meta: {
				...resolvePagination(params),
				total: 0,
				hasMore: false
			},
			fromCache: true,
			hasPendingWrites: false,
			partial: true
		};
		if (!snapshot) {
			const local = runLocalQuery([...state.rows.values()].map((e) => e.row), params);
			return {
				...local,
				fromCache,
				hasPendingWrites: local.data.some((row) => this.hasPending(slug, row.id)),
				partial: true
			};
		}
		const rows = [];
		const seen = /* @__PURE__ */ new Set();
		/** Rows the server counted that we know are no longer in the result. */
		let removed = 0;
		for (const id of snapshot.ids) {
			const key = String(id);
			const entry = state.rows.get(key);
			if (!entry) {
				if (state.absent.has(key) || this.hasPending(slug, key)) removed++;
				continue;
			}
			if (exact && this.hasPending(slug, key) && !matchesParams(entry.row, params)) {
				removed++;
				continue;
			}
			rows.push(entry.row);
			seen.add(key);
		}
		let added = 0;
		const offset = snapshot.offset ?? 0;
		if (exact && offset === 0) for (const [key, entry] of state.rows) {
			if (seen.has(key) || !this.hasPending(slug, key)) continue;
			if (!this.isLocallyCreated(slug, key)) continue;
			if (!matchesParams(entry.row, params)) continue;
			rows.push(entry.row);
			added++;
		}
		const orderIsLocal = isLocallySortable(rows, params?.orderBy);
		if (params?.orderBy && orderIsLocal) sortRows(rows, params.orderBy);
		return {
			data: rows,
			meta: {
				total: Math.max(rows.length, snapshot.total - removed + added),
				limit: snapshot.limit,
				offset,
				hasMore: snapshot.hasMore
			},
			fromCache,
			hasPendingWrites: rows.some((row) => this.hasPending(slug, row.id)),
			partial: !exact || !orderIsLocal
		};
	}
	localFind(slug, params) {
		const state = this.collections.get(slug);
		const snapshot = this.snapshotFor(slug, params);
		state?.fresh.delete(buildQueryString(params));
		if (!snapshot && (!state || state.rows.size === 0)) throw offlineError(`Offline: no cached data for "${slug}".`);
		const answer = this.answer(slug, params, snapshot);
		return snapshot ? answer : {
			...answer,
			partial: true
		};
	}
	/**
	* A temporary id of the type this collection's ids actually have.
	*
	* There is no schema here to read it off — the generated `Database` types
	* are erased at compile time, and the whole point of this path is that the
	* server is unreachable. So it is read off the rows the local database
	* already holds: a collection whose cached ids are numbers gets a negative
	* number, and everything else gets a UUID.
	*
	* A collection with nothing cached falls back to a UUID, which is the same
	* guess as before — but that is a first write on a device that has never
	* read the collection, which is also the case where nothing local can tell
	* the difference. Reading one row first is enough to fix it.
	*/
	mintOfflineId(slug) {
		for (const entry of this.collections.get(slug)?.rows.values() ?? []) {
			const id = entry.row.id;
			if (typeof id === "number") return generateOfflineNumericId();
			if (typeof id === "string") break;
		}
		return generateOfflineStringId();
	}
	rawLocalRow(slug, id) {
		const entry = this.collections.get(slug)?.rows.get(String(id));
		return entry ? { ...entry.row } : void 0;
	}
	localRow(slug, id) {
		return this.collections.get(slug)?.rows.get(String(id))?.row;
	}
	setLocalRow(slug, id, row) {
		const state = this.collectionState(slug);
		const key = String(id);
		const cachedAt = Date.now();
		state.rows.set(key, {
			row: { ...row },
			cachedAt,
			rev: ++this.revCounter
		});
		state.freshRows.delete(key);
		this.forgetTombstone(slug, key);
		this.writeCache(this.rowKey(slug, key), dehydrateRow(row), cachedAt);
		this.evictRows(slug);
	}
	/**
	* Drop a row and, when the server is the one saying it is gone, remember
	* that. "I looked it up and it does not exist" is real knowledge: without
	* it, opening a deleted row while offline would report a missing local
	* database instead of a missing row.
	*/
	removeLocalRow(slug, id, known = false) {
		const state = this.collectionState(slug);
		const key = String(id);
		const existed = state.rows.delete(key);
		if (known) {
			state.absent.add(key);
			state.freshRows.add(key);
			this.writeCache(this.absentKey(slug, key), true);
		} else state.freshRows.delete(key);
		if (existed) this.deleteCache([this.rowKey(slug, key)]);
	}
	forgetTombstone(slug, key) {
		if (!this.collectionState(slug).absent.delete(key)) return;
		this.deleteCache([this.absentKey(slug, key)]);
	}
	/**
	* Merge server rows into the local database. A row with unsynced local
	* writes keeps them: the server's copy is the base the queued mutations
	* are re-applied to, not a replacement for what the user did.
	*
	* Rows that came back unchanged keep their identity and revision, so a
	* refetch that changed nothing does not re-render every live query that
	* touches them — or rewrite them all to disk.
	*/
	async ingest(slug, rows) {
		if (rows.length === 0) return;
		const state = await this.ensureCollection(slug);
		const cachedAt = Date.now();
		const writes = [];
		const deletes = [];
		for (const raw of rows) {
			if (!raw || raw.id === void 0 || raw.id === null) continue;
			const key = String(raw.id);
			const merged = this.hasPending(slug, key) ? this.applyPendingToRow(slug, key, { ...raw }) : { ...raw };
			if (merged === void 0) {
				state.rows.delete(key);
				deletes.push(this.rowKey(slug, key));
				continue;
			}
			this.forgetTombstone(slug, key);
			state.freshRows.add(key);
			const existing = state.rows.get(key);
			if (existing && JSON.stringify(existing.row) === JSON.stringify(merged)) {
				existing.cachedAt = cachedAt;
				continue;
			}
			state.rows.set(key, {
				row: merged,
				cachedAt,
				rev: ++this.revCounter
			});
			writes.push({
				key: this.rowKey(slug, key),
				entry: {
					value: dehydrateRow(merged),
					cachedAt
				}
			});
		}
		if (writes.length > 0) this.store.setCacheMany(writes).catch(() => void 0);
		if (deletes.length > 0) this.deleteCache(deletes);
		this.evictRows(slug);
	}
	/**
	* Fold the queued mutations for one row over a base, newest last.
	* `afterMutationId` skips everything up to and including that mutation,
	* which is how a just-replayed write avoids being applied on top of the
	* server's response to it.
	*/
	applyPendingToRow(slug, idKey, base, afterMutationId) {
		let row = base;
		let skipping = afterMutationId !== void 0;
		for (const op of this.queue) {
			if (skipping) {
				if (op.mutationId === afterMutationId) skipping = false;
				continue;
			}
			if (op.collection !== slug) continue;
			if (op.type === "createMany") {
				const match = op.data?.find((r) => String(r.id) === idKey);
				if (match) row = { ...match };
				continue;
			}
			if (op.id === void 0 || String(op.id) !== idKey) continue;
			if (op.type === "create") row = { ...op.data };
			else if (op.type === "update") row = {
				...row ?? {},
				...op.data,
				id: op.id
			};
			else if (op.type === "delete") row = void 0;
		}
		return row;
	}
	recordSnapshot(slug, params, result) {
		const window = resolvePagination(params);
		const meta = result.meta ?? {
			total: result.data?.length ?? 0,
			...window,
			hasMore: false
		};
		const snapshot = {
			ids: (result.data ?? []).map((row) => row.id).filter((id) => id !== void 0),
			total: meta.total ?? result.data?.length ?? 0,
			limit: meta.limit ?? window.limit,
			offset: meta.offset ?? window.offset,
			hasMore: meta.hasMore ?? false
		};
		const state = this.collectionState(slug);
		const key = buildQueryString(params);
		state.snapshots.set(key, snapshot);
		state.fresh.add(key);
		this.writeCache(`${this.scope}|q|${slug}|${key}`, snapshot);
		this.evictSnapshots(slug);
		return snapshot;
	}
	/**
	* A write changed which rows belong in a list, and only the server can say
	* how — a row it generated is in no cached page, and the totals moved.
	* Re-run every live query on the collection; queries nobody is watching
	* are corrected by their next `find`.
	*
	* Coalesced per microtask so a burst of writes costs one round trip, and
	* skipped entirely while offline, where the local database is already the
	* best answer available.
	*/
	scheduleRefresh(slug) {
		if (this.refreshPending.has(slug)) return;
		const observers = this.observers.get(slug);
		if (!observers || observers.size === 0) return;
		this.refreshPending.add(slug);
		Promise.resolve().then(() => {
			this.refreshPending.delete(slug);
			if (this.disposed || !this.connectivity.shouldAttempt()) return;
			for (const observer of [...this.observers.get(slug) ?? []]) observer.refresh();
		});
	}
	evictRows(slug) {
		const state = this.collections.get(slug);
		if (!state || state.rows.size <= this.maxCachedRows) return;
		const evictable = [...state.rows.entries()].filter(([key]) => !this.hasPending(slug, key)).sort((a, b) => a[1].cachedAt - b[1].cachedAt);
		const excess = state.rows.size - this.maxCachedRows;
		const doomed = evictable.slice(0, excess);
		for (const [key] of doomed) state.rows.delete(key);
		if (doomed.length > 0) this.deleteCache(doomed.map(([key]) => this.rowKey(slug, key)));
		if (state.absent.size > this.maxCachedRows) {
			const stale = [...state.absent].slice(0, state.absent.size - this.maxCachedRows);
			for (const key of stale) state.absent.delete(key);
			this.deleteCache(stale.map((key) => this.absentKey(slug, key)));
		}
	}
	evictSnapshots(slug) {
		const state = this.collections.get(slug);
		if (!state || state.snapshots.size <= this.maxCachedQueries) return;
		const excess = state.snapshots.size - this.maxCachedQueries;
		const doomed = [...state.snapshots.keys()].slice(0, excess);
		for (const key of doomed) state.snapshots.delete(key);
		this.deleteCache(doomed.map((key) => `${this.scope}|q|${slug}|${key}`));
	}
	ensureQueueLoaded() {
		if (!this.queueLoad) {
			const scope = this.scope;
			this.queueLoad = this.store.listQueue(`${scope}|`).then((queue) => {
				if (this.scope !== scope) return;
				this.queue = queue;
				this.patchStatus({ pending: queue.length });
				this.notifyQueue();
			}).catch(() => void 0);
		}
		return this.queueLoad;
	}
	enqueue(mutation) {
		const result = this.enqueueChain.then(async () => {
			await this.ensureQueueLoaded();
			if (mutation.type === "update") {
				const tail = this.queue[this.queue.length - 1];
				if (tail && tail.mutationId !== this.inFlightId && tail.collection === mutation.collection && (tail.type === "create" || tail.type === "update") && tail.id === mutation.id) {
					tail.data = {
						...tail.data,
						...mutation.data,
						id: tail.id
					};
					await this.store.enqueue(this.queueKey(tail), tail);
					return;
				}
			}
			if (mutation.type === "delete") {
				if (this.queue.some((m) => m.collection === mutation.collection && m.type === "create" && m.id === mutation.id && m.generatedId === true && m.mutationId !== this.inFlightId)) {
					const doomed = this.queue.filter((m) => m.collection === mutation.collection && m.id === mutation.id && (m.type === "create" || m.type === "update") && m.mutationId !== this.inFlightId);
					for (const op of doomed) await this.store.dequeue(this.queueKey(op));
					this.queue = this.queue.filter((m) => !doomed.includes(m));
					this.afterQueueChange();
					return;
				}
			}
			const full = {
				...mutation,
				mutationId: createMutationId(),
				queuedAt: Date.now()
			};
			await this.store.enqueue(this.queueKey(full), full);
			this.queue.push(full);
			this.afterQueueChange();
		});
		this.enqueueChain = result.catch(() => void 0);
		return result;
	}
	hasPending(slug, id) {
		const key = String(id);
		return this.queue.some((op) => {
			if (op.collection !== slug) return false;
			if (op.type === "createMany") return op.data?.some((r) => String(r.id) === key) ?? false;
			return op.id !== void 0 && String(op.id) === key;
		});
	}
	/** Is this row one the server has never been told about? */
	isLocallyCreated(slug, idKey) {
		return this.queue.some((op) => {
			if (op.collection !== slug) return false;
			if (op.type === "create") return op.id !== void 0 && String(op.id) === idKey;
			if (op.type === "createMany") return op.data?.some((r) => String(r.id) === idKey) ?? false;
			return false;
		});
	}
	/** How many rows the queue adds to (or removes from) a server-side count. */
	pendingDelta(slug, params) {
		if (!isExactlyEvaluable(params)) return 0;
		let delta = 0;
		for (const op of this.queue) {
			if (op.collection !== slug) continue;
			if (op.type === "create") {
				if (matchesParams(op.data, params)) delta++;
			} else if (op.type === "createMany") {
				for (const row of op.data ?? []) if (matchesParams(row, params)) delta++;
			} else if (op.type === "delete") {
				const before = op.rollback?.rows?.[String(op.id)];
				if (before && matchesParams(before, params)) delta--;
			}
		}
		return delta;
	}
	sync() {
		if (this.flushPromise) return this.flushPromise;
		this.flushPromise = this.withLock(() => this.flush()).finally(() => {
			this.flushPromise = void 0;
		});
		return this.flushPromise;
	}
	async flush() {
		await this.ensureQueueLoaded();
		await this.reloadQueue();
		if (this.queue.length === 0) return {
			flushed: 0,
			remaining: 0
		};
		this.patchStatus({ syncing: true });
		const touched = /* @__PURE__ */ new Set();
		const queuedAtStart = this.queue.length;
		let flushed = 0;
		try {
			while (this.queue.length > 0 && !this.disposed) {
				const op = this.queue[0];
				touched.add(op.collection);
				this.inFlightId = op.mutationId;
				try {
					try {
						await this.replay(op);
					} catch (error) {
						if (isNetworkError(error)) {
							this.connectivity.markFailure();
							break;
						}
						op.attempts = (op.attempts ?? 0) + 1;
						op.lastError = error?.message ?? String(error);
						const limit = isIdempotencyInProgressError(error) ? Math.max(this.maxRetries, IN_PROGRESS_MIN_RETRIES) : this.maxRetries;
						if (isRetryableError(error) && op.attempts < limit) {
							await this.store.enqueue(this.queueKey(op), op).catch(() => void 0);
							this.connectivity.deferRetry();
							this.patchStatus({ lastError: op.lastError });
							break;
						}
						await this.rejectMutation(op, error);
						continue;
					}
					this.connectivity.markSuccess();
					await this.drop(op);
					flushed++;
				} finally {
					this.inFlightId = null;
				}
			}
		} finally {
			this.patchStatus({ syncing: false });
		}
		if (this.queue.length !== queuedAtStart) {
			for (const slug of touched) {
				this.notifyCollection(slug);
				this.scheduleRefresh(slug);
			}
			this.broadcast({ type: "queue" });
		}
		if (this.queue.length === 0) this.patchStatus({ lastSyncedAt: Date.now() });
		return {
			flushed,
			remaining: this.queue.length
		};
	}
	async replay(op) {
		const inner = this.innerFor(op.collection);
		if (op.type === "create") {
			let row;
			try {
				row = await inner.create(op.data, void 0, { idempotencyKey: op.mutationId });
			} catch (error) {
				if (!(op.generatedId === true && isDuplicateKeyError(error))) throw error;
				row = await inner.findById(op.id).catch(() => void 0);
				if (!row) return;
			}
			await this.adoptServerRow(op, op.id, row);
		} else if (op.type === "createMany") {
			const queued = op.data ?? [];
			const rows = await inner.createMany(queued, {
				...op.upsert ? { upsert: true } : {},
				idempotencyKey: op.mutationId
			});
			for (let i = 0; i < rows.length; i++) await this.adoptServerRow(op, queued[i]?.id, rows[i]);
		} else if (op.type === "updateMany") {
			const queued = op.updates ?? [];
			const rows = await inner.updateMany(queued.map((u) => ({
				id: u.id,
				data: u.data
			})), { idempotencyKey: op.mutationId });
			for (let i = 0; i < rows.length; i++) await this.ingestReplaced(op, queued[i].id, rows[i]);
		} else if (op.type === "update") {
			const row = await inner.update(op.id, op.data);
			await this.ingestReplaced(op, op.id, row);
		} else if (op.type === "deleteMany") {
			const ids = op.ids ?? [];
			await inner.deleteMany(ids, { idempotencyKey: op.mutationId });
			for (const id of ids) this.removeLocalRow(op.collection, id, true);
		} else if (op.type === "delete") {
			await inner.delete(op.id);
			this.removeLocalRow(op.collection, op.id, true);
		}
	}
	/**
	* Take the server's version of a row the client created offline.
	*
	* The server may have assigned a different id — a serial column ignores
	* the id we invented — in which case every local trace of the temporary id
	* has to move with it, including queued writes that were made against it
	* before it was ever sent.
	*/
	async adoptServerRow(op, localId, row) {
		if (!row) return;
		const slug = op.collection;
		const serverId = row.id;
		if (localId !== void 0 && serverId !== void 0 && String(serverId) !== String(localId)) {
			const oldKey = String(localId);
			this.removeLocalRow(slug, localId);
			for (const queued of this.queue) {
				if (queued.collection !== slug) continue;
				let dirty = false;
				if (queued.id !== void 0 && String(queued.id) === oldKey) {
					queued.id = serverId;
					if (queued.data && !Array.isArray(queued.data)) queued.data.id = serverId;
					dirty = true;
				}
				const rollbackRows = queued.rollback?.rows;
				if (rollbackRows && oldKey in rollbackRows) {
					rollbackRows[String(serverId)] = rollbackRows[oldKey];
					delete rollbackRows[oldKey];
					dirty = true;
				}
				if (dirty) await this.store.enqueue(this.queueKey(queued), queued).catch(() => void 0);
			}
		}
		await this.ingestReplaced(op, serverId ?? localId, row);
	}
	/**
	* Write a server row over the local one, ignoring the mutation that just
	* produced it — re-applying that would put the pre-server values back on
	* top of the server's answer — but keeping every write queued *after* it.
	* Those are still unsent, and dropping them here would make the row snap
	* back to the server's version in front of the user, only to change again
	* when they replay a moment later.
	*/
	async ingestReplaced(op, id, row) {
		const slug = op.collection;
		const state = await this.ensureCollection(slug);
		const key = String(id);
		const merged = this.applyPendingToRow(slug, key, { ...row }, op.mutationId);
		if (merged === void 0) {
			this.removeLocalRow(slug, key);
			return;
		}
		const cachedAt = Date.now();
		state.rows.set(key, {
			row: merged,
			cachedAt,
			rev: ++this.revCounter
		});
		if (this.applyPendingToRow(slug, key, void 0, op.mutationId) === void 0) state.freshRows.add(key);
		this.writeCache(this.rowKey(slug, key), dehydrateRow(merged), cachedAt);
	}
	/**
	* The server refused a mutation. Put back what it changed, and discard the
	* queued writes that were built on top of it: an edit to a row whose
	* creation was rejected can only fail the same way, and applying it would
	* leave the local database claiming a row the server does not have.
	*
	* The cascade stops the moment a later write stops *depending* on the
	* rejected one. An `update` reads the row it edits, so it is doomed with
	* it; a `create` overwrites the row outright and a `delete` needs nothing
	* of it, so both stand on their own and are kept — dropping them would
	* silently lose writes the server would have accepted.
	*/
	async rejectMutation(op, error) {
		const ids = new Set(Object.keys(op.rollback?.rows ?? {}));
		if (op.id !== void 0) ids.add(String(op.id));
		const doomed = [op];
		const orphaned = new Set(ids);
		const position = this.queue.indexOf(op);
		for (const later of this.queue.slice(position + 1)) {
			if (later.collection !== op.collection) continue;
			const hit = this.idsOf(later).filter((id) => orphaned.has(id));
			if (hit.length === 0) continue;
			if (later.type === "update") doomed.push(later);
			else for (const id of hit) orphaned.delete(id);
		}
		for (const dropped of doomed) await this.drop(dropped);
		for (const [idKey, previous] of Object.entries(op.rollback?.rows ?? {})) {
			const restored = this.applyPendingToRow(op.collection, idKey, previous ?? void 0);
			if (restored === void 0) this.removeLocalRow(op.collection, idKey);
			else this.setLocalRow(op.collection, idKey, restored);
		}
		this.patchStatus({ lastError: error.message });
		this.notifyCollection(op.collection);
		this.scheduleRefresh(op.collection);
		for (const dropped of doomed) this.onSyncError?.(error, dropped);
	}
	/** Every row id a mutation writes to. */
	idsOf(op) {
		if (op.type === "createMany") return (op.data ?? []).map((r) => String(r.id));
		return op.id === void 0 ? [] : [String(op.id)];
	}
	async drop(op) {
		await this.store.dequeue(this.queueKey(op)).catch(() => void 0);
		this.queue = this.queue.filter((m) => m.mutationId !== op.mutationId);
		this.afterQueueChange(false);
	}
	/** Replay uses unwrapped clients: a failure must never re-enqueue itself. */
	innerFor(slug) {
		let inner = this.inners.get(slug);
		if (!inner) {
			inner = this.createInner(slug);
			this.inners.set(slug, inner);
		}
		return inner;
	}
	async withLock(fn) {
		const locks = globalThis.navigator?.locks;
		if (!locks?.request) return fn();
		try {
			return await locks.request(`rebase-offline-sync:${this.scope}`, fn);
		} catch {
			return fn();
		}
	}
	broadcast(message) {
		if (!this.channel) return;
		try {
			this.channel.postMessage({
				...message,
				scope: this.scope,
				sender: this.tabId
			});
		} catch {}
	}
	onBroadcast(message) {
		if (this.disposed || !message || typeof message !== "object") return;
		const msg = message;
		if (msg.sender === this.tabId || msg.scope !== this.scope) return;
		if (msg.type === "rows") for (const slug of msg.slugs ?? []) this.reloadCollection(slug);
		else if (msg.type === "queue") this.reloadQueue();
	}
	/** Re-read one collection from the store, replacing what is in memory. */
	async reloadCollection(slug) {
		const state = this.collections.get(slug);
		if (!state?.loaded) return;
		await this.reloadQueue();
		const scope = this.scope;
		const [rows, snapshots, absent] = await Promise.all([
			this.store.listCacheEntries(`${scope}|row|${slug}|`).catch(() => []),
			this.store.listCacheEntries(`${scope}|q|${slug}|`).catch(() => []),
			this.store.listCache(`${scope}|abs|${slug}|`).catch(() => [])
		]);
		if (this.scope !== scope || this.collections.get(slug) !== state) return;
		const next = /* @__PURE__ */ new Map();
		for (const entry of rows) {
			const row = entry.value;
			if (!row || row.id === void 0 || row.id === null) continue;
			const key = String(row.id);
			const existing = state.rows.get(key);
			const hydrated = hydrateRow(row);
			const unchanged = existing && JSON.stringify(existing.row) === JSON.stringify(hydrated);
			next.set(key, {
				row: hydrated,
				cachedAt: entry.cachedAt,
				rev: unchanged ? existing.rev : ++this.revCounter
			});
		}
		state.rows = next;
		state.snapshots = /* @__PURE__ */ new Map();
		for (const entry of snapshots) {
			const key = entry.key.slice(`${scope}|q|${slug}|`.length);
			if (entry.value) state.snapshots.set(key, entry.value);
		}
		state.absent = new Set(absent.map((entry) => entry.key.slice(`${scope}|abs|${slug}|`.length)));
		this.notifyCollection(slug, false);
	}
	async reloadQueue() {
		const scope = this.scope;
		const queue = await this.store.listQueue(`${scope}|`).catch(() => void 0);
		if (!queue || this.scope !== scope) return;
		this.queue = queue;
		this.afterQueueChange(false);
	}
	afterQueueChange(broadcast = true) {
		this.patchStatus({ pending: this.queue.length });
		this.notifyQueue();
		if (broadcast) this.broadcast({ type: "queue" });
	}
	notifyQueue() {
		for (const listener of this.queueListeners) listener(this.queue.length);
	}
	patchStatus(patch) {
		let changed = false;
		for (const [key, value] of Object.entries(patch)) if (this.currentStatus[key] !== value) {
			this.currentStatus[key] = value;
			changed = true;
		}
		if (!changed) return;
		const snapshot = { ...this.currentStatus };
		for (const listener of this.statusListeners) listener(snapshot);
	}
	countKey(slug, params) {
		return `${this.scope}|count|${slug}|${buildQueryString(params)}`;
	}
	rowKey(slug, id) {
		return `${this.scope}|row|${slug}|${String(id)}`;
	}
	absentKey(slug, id) {
		return `${this.scope}|abs|${slug}|${String(id)}`;
	}
	queueKey(mutation) {
		return `${this.scope}|${mutation.mutationId}`;
	}
	async readCache(key) {
		try {
			return (await this.store.getCache(key))?.value;
		} catch {
			return;
		}
	}
	async writeCache(key, value, cachedAt = Date.now()) {
		try {
			await this.store.setCache(key, {
				value,
				cachedAt
			});
		} catch {}
	}
	async deleteCache(keys) {
		try {
			await this.store.deleteCache(keys);
		} catch {}
	}
};
//#endregion
//#region src/index.ts
/**
* Derive a WebSocket URL from an HTTP base URL.
* `http://` → `ws://`, `https://` → `wss://`.
*
* A backend mounted under a path is the reason `baseUrl` accepts one, so the
* path is kept. It used to be kept for an absolute `baseUrl` and dropped for a
* relative one — resolved through `.origin` — so one deployment dialled two
* different sockets depending on whether its config said `"/backend"` or
* `"https://app.example.com/backend"`.
*
* Returns `""` when there is nothing to resolve against: a relative `baseUrl`
* outside a browser has no origin, and inventing one would dial somewhere
* arbitrary. The caller warns rather than leaving that silent.
*/
function deriveWebSocketUrl(baseUrl) {
	const toWsProtocol = (url) => {
		const secure = /^(https|wss):/i.test(url);
		return url.replace(/^https?:\/\//i, secure ? "wss://" : "ws://").replace(/^wss?:\/\//i, secure ? "wss://" : "ws://").replace(/\/$/, "");
	};
	if (typeof window !== "undefined") {
		let absoluteUrl;
		if (!baseUrl) absoluteUrl = window.location.origin;
		else if (/^https?:\/\//i.test(baseUrl) || /^wss?:\/\//i.test(baseUrl)) absoluteUrl = baseUrl;
		else try {
			const resolved = new URL(baseUrl, window.location.href);
			absoluteUrl = resolved.origin + resolved.pathname;
		} catch {
			absoluteUrl = window.location.origin;
		}
		return toWsProtocol(absoluteUrl);
	}
	if (!baseUrl) return "";
	if (!/^https?:\/\//i.test(baseUrl) && !/^wss?:\/\//i.test(baseUrl)) return "";
	return toWsProtocol(baseUrl);
}
function createRebaseClient(options) {
	const transport = createTransport(options, { credentialOutOfBand: options.auth?.authFlowMode === "cookie" });
	const auth = createAuth(transport, options.auth);
	const admin = createAdmin(transport, options.admin);
	const cron = createCron(transport, options.cron);
	const backups = createBackups(transport);
	const apiKeys = createApiKeys(transport, options.apiKeys);
	const storage = createStorage(transport);
	const functions = createFunctionsClient(transport);
	const createStorageSource = (storageId) => storageId === DEFAULT_STORAGE_SOURCE_KEY ? storage : createStorage(transport, storageId);
	const storageRegistry = new ClientStorageSourceRegistry();
	storageRegistry.register(DEFAULT_STORAGE_SOURCE_KEY, storage);
	for (const def of options.storageSources ?? []) if (def.transport === "server" && def.key !== DEFAULT_STORAGE_SOURCE_KEY) storageRegistry.register(def.key, createStorageSource(def.key));
	let storageSourcesPromise;
	const fetchStorageSources = () => {
		if (storageSourcesPromise) return storageSourcesPromise;
		storageSourcesPromise = transport.request("/storage/sources").then((res) => {
			const defs = res.data ?? [];
			for (const def of defs) if (def.transport === "server" && def.key !== DEFAULT_STORAGE_SOURCE_KEY && !storageRegistry.has(def.key)) storageRegistry.register(def.key, createStorageSource(def.key));
			return defs;
		}).catch((e) => {
			storageSourcesPromise = void 0;
			throw e;
		});
		return storageSourcesPromise;
	};
	const realtimeEnabled = options.realtime !== false;
	const resolvedWsUrl = realtimeEnabled ? options.websocketUrl ?? deriveWebSocketUrl(options.baseUrl) : void 0;
	const realtimeUnreachable = realtimeEnabled && !resolvedWsUrl;
	const unreachableReason = `no WebSocket URL could be derived from baseUrl ${JSON.stringify(options.baseUrl ?? null)} — outside a browser there is no page origin to resolve a relative URL against. Pass an absolute \`baseUrl\`, set \`websocketUrl\` explicitly, or pass \`realtime: false\` to say this was intended.`;
	if (realtimeUnreachable) console.warn(`[Rebase] Realtime is enabled but ${unreachableReason} Live queries will fall back to a single fetch and channels will throw.`);
	let ws;
	/** One channel object per name — see `realtime.channel`. */
	const realtimeChannels = /* @__PURE__ */ new Map();
	if (resolvedWsUrl) {
		ws = new RebaseWebSocketClient({
			websocketUrl: resolvedWsUrl,
			getAuthToken: async () => {
				let session = auth.getSession();
				if (session && session.expiresAt <= Date.now() + 1e4) try {
					session = await auth.refreshSession();
				} catch (e) {}
				return session?.accessToken || options.token || "";
			},
			onUnauthorized: options.onUnauthorized || (() => auth.handleUnauthorized())
		});
		auth.onAuthStateChange((event, session) => {
			if (!ws) return;
			if (event === "SIGNED_OUT") ws.disconnect();
			else if (event === "SIGNED_IN" || event === "TOKEN_REFRESHED") {
				if (session?.accessToken && ws.hasSocket) ws.authenticate(session.accessToken).catch(console.warn);
			}
		});
	}
	if (!options.onUnauthorized) transport.setOnUnauthorized(() => auth.handleUnauthorized());
	/**
	* Suggest the closest known collection key for a mistyped accessor.
	* Uses edit-distance-1 and prefix matching — no external dependency.
	*/
	function suggestCollection(prop, knownKeys) {
		const prefixMatch = knownKeys.find((k) => k.startsWith(prop) || prop.startsWith(k));
		if (prefixMatch) return prefixMatch;
		for (const key of knownKeys) {
			if (Math.abs(key.length - prop.length) > 1) continue;
			let diffs = 0;
			const longer = key.length >= prop.length ? key : prop;
			const shorter = key.length >= prop.length ? prop : key;
			if (longer.length === shorter.length) for (let i = 0; i < longer.length; i++) {
				if (longer[i] !== shorter[i]) {
					if (i + 1 < longer.length && longer[i] === shorter[i + 1] && longer[i + 1] === shorter[i]) {
						diffs++;
						i++;
						if (diffs > 1) break;
						continue;
					}
					diffs++;
				}
				if (diffs > 1) break;
			}
			else {
				let li = 0;
				let si = 0;
				while (li < longer.length) {
					if (si < shorter.length && longer[li] === shorter[si]) si++;
					else diffs++;
					li++;
					if (diffs > 1) break;
				}
			}
			if (diffs <= 1) return key;
		}
	}
	const offlineManager = options.offline ? new OfflineManager(typeof options.offline === "object" ? options.offline : {}, (slug) => createCollectionClient(transport, slug)) : void 0;
	if (offlineManager) {
		offlineManager.setScope(auth.getSession()?.user?.uid);
		auth.onAuthStateChange((event, session) => {
			offlineManager.setScope(event === "SIGNED_OUT" ? void 0 : session?.user?.uid);
		});
	}
	const collectionClients = /* @__PURE__ */ new Map();
	let untypedWarned = false;
	function collection(slug) {
		if (!collectionClients.has(slug)) {
			const inner = createCollectionClient(transport, slug, ws);
			collectionClients.set(slug, offlineManager ? offlineManager.wrap(slug, inner) : inner);
		}
		return collectionClients.get(slug);
	}
	const dataProxy = new Proxy({ collection }, { get(_target, prop) {
		if (prop === "collection") return collection;
		if (typeof prop === "symbol") return void 0;
		if (typeof prop === "string" && prop !== "then" && prop !== "toJSON" && prop !== "$$typeof") {
			if (options.collections) {
				if (prop in options.collections) return collection(options.collections[prop]);
				const knownKeys = Object.keys(options.collections);
				const suggestion = suggestCollection(prop, knownKeys);
				let msg = `Unknown collection accessor "${prop}". Known collections: ${knownKeys.join(", ")}.`;
				if (suggestion) msg += ` Did you mean "${suggestion}"?`;
				msg += ` Use data.collection("<slug>") for dynamic slugs.`;
				throw new RebaseClientError(msg, {
					code: "UNKNOWN_COLLECTION",
					details: {
						accessor: prop,
						known: knownKeys
					}
				});
			}
			if (!untypedWarned) {
				untypedWarned = true;
				console.warn(`[Rebase] Untyped data access detected (client.data.${prop}). Collection names are resolved via snake_case conversion, which may cause silent 404s at request time. Pass a \`collections\` dictionary to createRebaseClient() or use the generated SDK for type-safe access.`);
			}
			return collection(toSnakeCase(prop));
		}
	} });
	return {
		auth,
		admin,
		cron,
		backups,
		apiKeys,
		functions,
		storage,
		storageRegistry,
		createStorageSource,
		fetchStorageSources,
		ws,
		realtime: { 
		/**
		* Join a broadcast/presence channel.
		*
		* Repeated calls with the same name return the same channel, so
		* separate components can attach handlers without each opening its
		* own membership — and `leave()` from one would otherwise silently
		* cut off the others.
		*/
channel: (name, options) => {
			if (!ws) throw new RebaseClientError(realtimeUnreachable ? `Realtime is enabled but ${unreachableReason}` : "Realtime is disabled on this client (realtime: false), so channels are unavailable.", { code: "REALTIME_DISABLED" });
			let existing = realtimeChannels.get(name);
			if (!existing) {
				existing = new RebaseRealtimeChannel(name, ws, options);
				realtimeChannels.set(name, existing);
			} else if (options?.history) existing.enableHistory();
			return existing;
		} },
		/**
		* Release every handle that can keep a process alive — see the
		* `close` docblock on the client interface.
		*
		* Safe to call when realtime was never started, safe when signed out,
		* and safe to call twice.
		*/
		close: () => {
			for (const channel of realtimeChannels.values()) channel.leave();
			realtimeChannels.clear();
			ws?.disconnect(true);
			offlineManager?.dispose();
			auth.stopAutoRefresh();
		},
		setToken: transport.setToken,
		setAuthTokenGetter: transport.setAuthTokenGetter,
		setOnUnauthorized: transport.setOnUnauthorized,
		resolveToken: transport.resolveToken,
		baseUrl: transport.baseUrl,
		apiPath: transport.apiPath,
		collection,
		call: async (endpoint, payload) => {
			const prefix = endpoint.startsWith("/") ? "" : "/";
			return transport.request(`${prefix}${endpoint}`, {
				method: "POST",
				body: payload ? JSON.stringify(payload) : void 0
			});
		},
		batch: async (operations, options) => {
			if (!Array.isArray(operations)) throw new TypeError("batch expects an array of operations.");
			if (operations.length === 0) return {
				data: [],
				meta: { operations: 0 }
			};
			const headers = {};
			if (options?.idempotencyKey) headers["Idempotency-Key"] = options.idempotencyKey;
			if (options?.returning === false) headers.Prefer = "return=minimal";
			return transport.request("/data/_batch", {
				method: "POST",
				body: JSON.stringify({ operations }),
				...Object.keys(headers).length > 0 ? { headers } : {}
			});
		},
		data: dataProxy,
		...offlineManager ? { offline: offlineManager.api } : {}
	};
}
//#endregion
export { MemoryOfflineStore, QueryBuilder, RebaseApiError, RebaseClientError, RebasePaginationError, RebaseRealtimeChannel, RebaseWebSocketClient, and, cond, createBackups, createCookieStorage, createMemoryStorage, createRebaseClient, etagOf, isOfflineError, not, or };

//# sourceMappingURL=index.es.js.map